import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    Box, Typography, TextField, Button,
    Paper, Grid, FormControl, InputLabel,
    Chip, CircularProgress, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TablePagination,
    Checkbox, FormControlLabel, Snackbar, LinearProgress, Select, MenuItem, Tooltip,
    Link, List, ListItem, ListItemIcon, ListItemText
} from '@material-ui/core';

// 首先在文件顶部添加必要的导入
import UpdateIcon from '@material-ui/icons/Update';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Divider from '@material-ui/core/Divider';
import AssessmentIcon from '@material-ui/icons/Assessment';
// 在文件顶部的导入块中添加这两行
import RefreshIcon from '@material-ui/icons/Refresh';
import SettingsIcon from '@material-ui/icons/Settings';
import { PieChart, Cell, Pie, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
// 首先在文件顶部添加SVG图标导入
import TwitchIcon from '../../../images/svg/twitch.svg';
import YouTubeIcon from '../../../images/svg/youtube.svg';
import TikTokIcon from '../../../images/svg/tiktok.svg';
import TwitterIcon from '../../../images/svg/x.svg';

import {
    Search as SearchIcon,
    Sync as SyncIcon,
    FilterList as FilterListIcon,
    Info as InfoIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    Person as PersonIcon,
    Gamepad as GamepadIcon,
    Category as CategoryIcon,
    Clear as ClearIcon,
    // 添加缺失的图标导入
    Star as StarIcon,
    CheckBox as CheckBoxIcon,
    Send as SendIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    // 添加排序相关图标
    Sort as SortIcon,
    TrendingUp as TrendingUpIcon,
    People as PeopleIcon,
    AttachMoney as AttachMoneyIcon,
} from '@material-ui/icons';
import axios from 'axios';
import AddIcon from '@material-ui/icons/Add';
import Menu from '@material-ui/core/Menu';
import CheckIcon from '@material-ui/icons/Check';
// 首先在文件顶部导入函数
import { getLocalTimeString } from '../../../utils/functions';

// 使用现有的语言文件
import {
    getMultilingualText,
    detectBrowserLanguage
} from '../../../utils/languages';

// 自己实现的简单防抖函数（如果不想引入 lodash）
const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
};

const useStyles = makeStyles((theme) => ({
    moreButton: {
        height: '40px', // 与其他控件保持一致
        minWidth: '80px',
        backgroundColor: 'transparent',
        color: '#64b5f6',
        borderColor: '#64b5f6',
        fontWeight: 500,
        flexShrink: 0,
        [theme.breakpoints.down('sm')]: {
            minWidth: '60px',
            fontSize: '0.8rem',
        },
    },

    expandButton: {
        height: '40px', // 与其他控件保持一致
        minWidth: '100px',
        backgroundColor: 'transparent',
        borderRadius: '16px !important',
        borderStyle: 'solid !important',
        borderWidth: '1px !important',
        textTransform: 'none !important',
        fontSize: '0.875rem !important',
        fontWeight: 500,
        flexShrink: 0,
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05) !important',
            transform: 'scale(1.02)',
        },
        [theme.breakpoints.down('sm')]: {
            minWidth: '80px',
            fontSize: '0.8rem !important',
        },
    },

    // 优化Action Bar中主播类型的样式
    actionBarTypeChips: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.2), // 与其他元素保持一致
        '& .MuiChip-root': {
            height: '32px', // 统一芯片高度
            fontSize: '0.875rem',
            fontWeight: 500,
            margin: 0, // 移除默认边距
        },
    },

    // 添加筛选器分组样式
    filterGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.2), // 标题和控件之间的统一间距
        flexWrap: 'nowrap',
        // 手机端每个元素独占一行
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            alignItems: 'stretch',
            width: '100%',
            gap: theme.spacing(0.8),
            '& > *': {
                width: '100%',
            },
        },
    },

    // 优化工具栏样式
    filterToolbar: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.2), // 与其他元素保持一致的间距
        marginLeft: 'auto',
        flexShrink: 0,
        // 平板及以下
        [theme.breakpoints.down('md')]: {
            marginLeft: 0,
            marginTop: theme.spacing(1),
        },
        // 手机端
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            justifyContent: 'space-between',
        },
    },
    // 修改选择操作区域的样式
    selectedUserActions: {
        display: 'flex',
        gap: theme.spacing(1.5), // 使用与一键选择和取消选中相同的间距
        alignItems: 'center',
    },
    // 添加按钮样式
    addButton: {
        borderRadius: '16px !important',
        borderStyle: 'dashed !important',
        borderWidth: '2px !important',
        textTransform: 'none !important',
        fontSize: '0.875rem !important',
        height: '32px !important',
        minWidth: 'auto !important',
        padding: '6px 12px !important',
        transition: 'all 0.2s ease !important',
        '&:hover': {
            backgroundColor: 'rgba(77, 171, 245, 0.1) !important',
            transform: 'scale(1.05)',
        },
    },
    container: {
        backgroundColor: '#1a2035', // 深蓝色背景
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        boxShadow: '0 3px 10px rgba(0, 0, 0, 0.3)',
        transition: 'transform 0.2s ease-in-out',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(3),
    },
    title: {
        color: '#e0e0e0', // 浅色标题
        fontWeight: 500,
    },
    filterContainer: {
        backgroundColor: '#232b42', // 与表格容器相同的背景色
        padding: theme.spacing(2), // 增加内边距
        borderRadius: theme.shape.borderRadius,
        marginBottom: theme.spacing(2),
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)', // 与表格相同的阴影
        border: '1px solid rgba(255, 255, 255, 0.05)', // 添加细微边框
    },
    // 优化筛选器标题样式 - 响应式
    filterTitle: {
        color: '#e0e0e0',
        fontWeight: 600,
        fontSize: '0.95rem',
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0, // 防止标题被压缩
        whiteSpace: 'nowrap', // 防止文字换行
        '& .MuiSvgIcon-root': {
            marginRight: theme.spacing(1),
            fontSize: '1.2rem',
            color: '#64b5f6',
        },
        // 平板及以下设备
        [theme.breakpoints.down('md')]: {
            fontSize: '0.9rem',
            '& .MuiSvgIcon-root': {
                fontSize: '1.1rem',
            },
        },
        // 手机端
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            justifyContent: 'flex-start',
            marginBottom: theme.spacing(0.5),
        },
    },
    // 优化筛选器行样式 - 添加响应式支持
    filterRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2.5),
        padding: theme.spacing(1, 0),
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        '&:last-child': {
            borderBottom: 'none',
            marginBottom: 0,
        },
        // 平板及以下设备的响应式处理
        [theme.breakpoints.down('lg')]: {
            flexWrap: 'wrap',
            gap: theme.spacing(1.5),
        },
        // 手机端的响应式处理
        [theme.breakpoints.down('md')]: {
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: theme.spacing(1.5),
            marginBottom: theme.spacing(2),
        },
    },

    // 添加响应式筛选器行容器
    filterRowResponsive: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2), // 统一间距为 2.5
        marginBottom: theme.spacing(2),
        padding: theme.spacing(1, 0), // 统一垂直内边距
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        flexWrap: 'wrap',
        '&:last-child': {
            borderBottom: 'none',
            marginBottom: 0,
        },
        // 大屏幕保持原样
        [theme.breakpoints.up('xl')]: {
            flexWrap: 'nowrap',
            gap: theme.spacing(2.5), // 大屏幕稍大的间距
        },
        // 笔记本屏幕适配
        [theme.breakpoints.down('lg')]: {
            gap: theme.spacing(1.5),
            '& > *': {
                marginBottom: theme.spacing(0.8),
            },
        },
        // 平板屏幕适配
        [theme.breakpoints.down('md')]: {
            gap: theme.spacing(1.2),
            '& > *': {
                marginBottom: theme.spacing(0.6),
            },
        },
        // 手机屏幕适配
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: theme.spacing(1),
            '& > *': {
                width: '100%',
                marginBottom: theme.spacing(0.4),
            },
        },
    },
    // 优化芯片容器样式 - 统一间距
    chipContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1), // 统一芯片间距
        flex: 1,
        alignItems: 'center',
        minHeight: '32px', // 确保容器有最小高度
    },
    // 优化芯片样式 - 统一尺寸
    chip: {
        margin: 0, // 移除边距，使用容器的gap控制
        transition: 'all 0.2s ease-in-out',
        backgroundColor: '#2a3758',
        color: '#e0e0e0',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        fontSize: '0.875rem',
        fontWeight: 500,
        height: '32px', // 统一高度
        '&:hover': {
            transform: 'scale(1.05)',
            backgroundColor: '#3a4b78',
            borderColor: 'rgba(255, 255, 255, 0.2)',
        },
        '&.selected': {
            backgroundColor: '#1565c0',
            color: '#fff',
            borderColor: '#1565c0',
            boxShadow: '0 2px 4px rgba(21, 101, 192, 0.3)',
        },
    },
    // 优化搜索框样式
    searchField: {
        width: 350, // 从280增加到350
        flexShrink: 1,
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            color: '#e0e0e0',
            height: '40px',
            '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.15)',
            },
            '&:hover fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.25)',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#64b5f6',
            },
        },
        '& .MuiInputLabel-root': {
            color: '#b0b0b0',
        },
        '& .Mui-focused': {
            color: '#64b5f6',
        },
        '& input::placeholder': {
            color: '#8c8c8c',
            opacity: 1,
        },
        // 笔记本屏幕
        [theme.breakpoints.down('lg')]: {
            width: 320, // 从240增加到320
        },
        // 平板屏幕
        [theme.breakpoints.down('md')]: {
            width: 280, // 从200增加到280
        },
        // 手机屏幕
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },

    // 正确的方式是直接定义完整的样式
    select: {
        width: 210, // 固定宽度而不是最小宽度
        height: '40px',
        flexShrink: 0, // 防止被压缩
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            color: '#e0e0e0',
            '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.15)',
            },
            '&:hover fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.25)',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#64b5f6',
            },
        },
        '& .MuiSelect-icon': {
            color: '#b0b0b0',
        },
        '& .MuiInputLabel-root': {
            color: '#b0b0b0',
        },
        '& .Mui-focused': {
            color: '#64b5f6',
        },
        [theme.breakpoints.down('md')]: {
            width: 120, // 平板屏幕稍小
        },
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },

    // 统一按钮高度
    searchButton: {
        height: '40px', // 与其他控件保持一致
        minWidth: '80px',
        backgroundColor: '#1565c0',
        color: 'white',
        fontWeight: 500,
        flexShrink: 0,
        '&:hover': {
            backgroundColor: '#0d47a1',
        },
        '&:disabled': {
            backgroundColor: '#555',
            color: '#999',
        },
        [theme.breakpoints.down('sm')]: {
            minWidth: '60px',
            fontSize: '0.8rem',
        },
    },
    // 优化筛选状态显示样式
    filterStatus: {
        backgroundColor: '#1a2035',
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(2),
        border: '1px solid rgba(100, 181, 246, 0.2)',
        marginTop: theme.spacing(1),
        '& .filter-title': {
            color: '#64b5f6',
            fontWeight: 600,
            marginBottom: theme.spacing(1),
        },
        '& .filter-chips': {
            display: 'flex',
            flexWrap: 'wrap',
            gap: theme.spacing(1),
            alignItems: 'center',
        },
        '& .clear-button': {
            color: '#f48fb1',
            fontWeight: 500,
            '&:hover': {
                backgroundColor: 'rgba(244, 143, 177, 0.1)',
            },
        },
    },
    // 添加响应式断点
    [theme.breakpoints.down('md')]: {
        filterRow: {
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: theme.spacing(1.5),
        },
        filterTitle: {
            minWidth: 'auto',
            marginBottom: theme.spacing(0.5),
        },
        searchField: {
            width: '100%',
        },
        chipContainer: {
            width: '100%',
        },
    },
    tableContainer: {
        backgroundColor: '#232b42', // 表格背景色
        borderRadius: theme.shape.borderRadius,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    },
    tableHeaderCell: {
        backgroundColor: '#1a2035', // 表头背景色
        fontWeight: 600,
        color: '#b0b0b0', // 表头文字色
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    },
    tableRow: {
        '&:nth-of-type(odd)': {
            backgroundColor: '#29314a', // 奇数行
        },
        '&:nth-of-type(even)': {
            backgroundColor: '#232b42', // 偶数行
        },
        '&:hover': {
            backgroundColor: '#323e5c', // 悬停效果
        },
        '& .MuiTableCell-body': {
            color: '#e0e0e0', // 表格文字颜色
            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        },
    },
    actionBar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(2),
    },
    tag: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '3px 8px',
        borderRadius: '4px',
        fontSize: '0.75rem',
        margin: '2px',
        backgroundColor: '#2a3758',
        color: '#e0e0e0',
        '&.genre': {
            backgroundColor: '#1a3f5f', // 深蓝色背景
            color: '#64b5f6', // 亮蓝色文字
        },
        '&.theme': {
            backgroundColor: '#3c2a4f', // 深紫色背景
            color: '#ce93d8', // 亮紫色文字
        },
    },
    notificationPaper: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(1, 2),
        borderRadius: '4px',
        '& .MuiSvgIcon-root': {
            marginRight: theme.spacing(1),
        },
        '&.success': {
            backgroundColor: '#1b3e29',
            color: '#81c784',
        },
        '&.error': {
            backgroundColor: '#441a23',
            color: '#e57373',
        },
        '&.warning': {
            backgroundColor: '#443a18',
            color: '#ffb74d',
        },
        '&.info': {
            backgroundColor: '#1a3650',
            color: '#64b5f6',
        },
    },
    paginationToolbar: {
        color: '#e0e0e0',
        '& .MuiIconButton-root': {
            color: '#b0b0b0',
            '&.Mui-disabled': {
                color: '#555',
            },
        },
        '& .MuiSelect-icon': {
            color: '#b0b0b0',
        },
    },
    subtitle: {
        color: '#b0b0b0', // 副标题颜色
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
    },
    noTags: {
        color: '#8c8c8c',
        fontStyle: 'italic',
    },
    statValue: {
        fontFamily: 'monospace',
        fontWeight: 500,
        color: '#e0e0e0',
    },
    // 调整统计按钮位置
    statsButton: {
        minWidth: 'auto',
        padding: theme.spacing(0.6),
        marginLeft: theme.spacing(1), // 从0.8减少到1，给匹配度信息更多空间
        backgroundColor: 'rgba(58, 75, 120, 0.9)',
        color: '#e0e0e0',
        borderRadius: 6,
        border: '1px solid rgba(255, 255, 255, 0.1)',
        flexShrink: 0, // 防止按钮被压缩
        '&:hover': {
            backgroundColor: 'rgba(76, 95, 153, 1)',
            transform: 'scale(1.1)',
            borderColor: 'rgba(255, 255, 255, 0.2)',
        },
        transition: 'all 0.2s ease',
        '& .MuiSvgIcon-root': {
            fontSize: '16px',
        },
    },
    dialogTitle: {
        backgroundColor: '#1a2035',
        color: '#e0e0e0',
        '& .MuiIconButton-root': {
            color: '#b0b0b0',
        },
    },
    dialogContent: {
        backgroundColor: '#232b42',
        color: '#e0e0e0',
        padding: theme.spacing(3),
    },
    dialogActions: {
        backgroundColor: '#1a2035',
        padding: theme.spacing(1, 3),
    },
    tabRoot: {
        backgroundColor: '#1a2035',
        color: '#b0b0b0',
        '& .MuiTab-root': {
            color: '#b0b0b0',
        },
        '& .Mui-selected': {
            color: '#64b5f6',
        },
        '& .MuiTabs-indicator': {
            backgroundColor: '#64b5f6',
        },
    },
    statCard: {
        backgroundColor: '#29314a',
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(2),
        height: '100%',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    },
    statTitle: {
        color: '#b0b0b0',
        fontSize: '0.85rem',
        marginBottom: theme.spacing(1),
    },
    chartContainer: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3),
        height: 300,
    },
    infoGrid: {
        marginTop: theme.spacing(2),
    },
    configTableContainer: {
        maxHeight: '500px',
        overflowY: 'auto',
        backgroundColor: '#192231',
        '& .MuiTableCell-root': {
            borderBottomColor: 'rgba(255, 255, 255, 0.1)',
        }
    },
    // 添加主播评价标签的样式
    ratingTag: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '3px 8px',
        borderRadius: '4px',
        fontSize: '0.75rem',
        margin: '0', // 移除外边距，使用容器的gap控制间距
        fontWeight: 500,
        '&.activity': {
            backgroundColor: '#1a472a', // 深绿色背景
            color: '#81c784', // 亮绿色文字
            border: '1px solid #2e7d32',
        },
        '&.completion': {
            backgroundColor: '#1a365d', // 深蓝色背景
            color: '#64b5f6', // 亮蓝色文字
            border: '1px solid #1976d2',
        },
        '&.cost': {
            backgroundColor: '#4a1a4a', // 深紫色背景
            color: '#ce93d8', // 亮紫色文字
            border: '1px solid #7b1fa2',
        },
        '&.no-rating': {
            backgroundColor: '#2a2a2a', // 深灰色背景
            color: '#888', // 灰色文字
            fontStyle: 'italic',
        },
    },
    ratingContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        minWidth: '120px',
    },
    // 游戏选择框 - 统一样式
    gameSelect: {
        width: 220, // 固定宽度，比其他select稍大
        height: '40px',
        flexShrink: 0,
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            color: '#e0e0e0',
            '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.15)',
            },
            '&:hover fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.25)',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#64b5f6',
            },
        },
        '& .MuiSelect-icon': {
            color: '#b0b0b0',
        },
        '& .MuiInputLabel-root': {
            color: '#b0b0b0',
        },
        '& .Mui-focused': {
            color: '#64b5f6',
        },
        [theme.breakpoints.down('lg')]: {
            width: 190,
        },
        [theme.breakpoints.down('md')]: {
            width: 160,
        },
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },

    // 优化按钮组样式 - 统一间距
    buttonGroup: {
        display: 'flex',
        gap: theme.spacing(1.2), // 统一按钮间距
        alignItems: 'center',
        flexShrink: 0,
        // 平板及以下
        [theme.breakpoints.down('md')]: {
            flexWrap: 'wrap',
            gap: theme.spacing(1),
        },
        // 手机端
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            justifyContent: 'stretch',
            '& > *': {
                flex: 1,
                minWidth: 0,
            },
        },
    },

    // 调整卡片容器以适应新的尺寸
    cardContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', // 稍微减小最小宽度
        gap: theme.spacing(1.5), // 从3减少到2
        justifyContent: 'center',
        marginBottom: theme.spacing(1.5), // 从3减少到2
        padding: theme.spacing(1), // 从2减少到1.5
        // 响应式调整
        [theme.breakpoints.down('xl')]: {
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
            gap: theme.spacing(1.3),
        },
        [theme.breakpoints.down('lg')]: {
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: theme.spacing(1.2),
        },
        [theme.breakpoints.down('md')]: {
            gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))',
            gap: theme.spacing(1),
        },
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: theme.spacing(0.8),
        },
    },

    [theme.breakpoints.down('xl')]: {
        matchInfo: {
            gap: theme.spacing(0.8),
        },
        matchValue: {
            fontSize: '15px',
        },
    },
    [theme.breakpoints.down('lg')]: {
        matchInfo: {
            gap: theme.spacing(0.6),
        },
        matchLabel: {
            fontSize: '11px',
        },
        matchValue: {
            fontSize: '14px',
        },
        statsButton: {
            padding: theme.spacing(0.5),
            marginLeft: theme.spacing(0.8),
        },
    },
    [theme.breakpoints.down('md')]: {
        matchInfo: {
            gap: theme.spacing(0.5),
        },
        matchLabel: {
            fontSize: '10px',
        },
        matchValue: {
            fontSize: '13px',
        },
        statsButton: {
            padding: theme.spacing(0.4),
            marginLeft: theme.spacing(0.6),
        },
    },
    [theme.breakpoints.down('sm')]: {
        matchInfo: {
            gap: theme.spacing(0.4),
        },
        matchLabel: {
            fontSize: '9px',
        },
        matchValue: {
            fontSize: '12px',
        },
        statsButton: {
            padding: theme.spacing(0.3),
            marginLeft: theme.spacing(0.5),
            '& .MuiSvgIcon-root': {
                fontSize: '14px',
            },
        },
    },

    // 修改卡片基础样式 - 调整为更符合卡片比例的尺寸
    userCard: {
        width: 260, // 稍微减小宽度
        minHeight: 420, // 增加最小高度，确保有足够空间显示所有内容
        height: 'auto', // 改为自动高度，允许内容撑开
        borderRadius: 12,
        overflow: 'visible',
        position: 'relative',
        transition: 'all 0.3s ease',
        background: 'rgba(35, 43, 66, 0.9)', // 增加不透明度
        backdropFilter: 'blur(10px)',
        border: '2px solid rgba(255, 255, 255, 0.15)',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)', // 增加阴影
        '&:hover': {
            transform: 'translateY(-8px) scale(1.02)', // 增加轻微缩放
            boxShadow: '0 12px 24px rgba(0, 0, 0, 0.6)',
        },
    },

    // 匹配度等级样式
    premiumCard: {
        borderColor: '#6A0DAD', // 改为紫色
        background: 'linear-gradient(135deg, rgba(106, 13, 173, 0.1), rgba(90, 11, 143, 0.15))', // 紫色渐变背景
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: 'linear-gradient(90deg, #6A0DAD, #8A2BE2, #6A0DAD)', // 紫色渐变顶边
            zIndex: 3,
            boxShadow: '0 2px 4px rgba(106, 13, 173, 0.5)',
        },
        '&:hover': {
            background: 'linear-gradient(135deg, rgba(106, 13, 173, 0.15), rgba(90, 11, 143, 0.2))',
            borderColor: '#8A2BE2', // 悬停时稍亮的紫色
        },
    },

    // 修改潜力主播卡片样式
    potentialCard: {
        borderColor: '#FFD700', // 改为金色
        background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.1), rgba(230, 184, 0, 0.15))', // 金色渐变背景
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: 'linear-gradient(90deg, #FFD700, #FFA500, #FFD700)', // 金色渐变顶边
            zIndex: 3,
            boxShadow: '0 2px 4px rgba(255, 215, 0, 0.5)',
        },
        '&:hover': {
            background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.15), rgba(230, 184, 0, 0.2))',
            borderColor: '#FFA500', // 悬停时稍深的金色
        },
    },

    // 修改普通主播卡片样式
    normalCard: {
        borderColor: '#1565c0', // 改为深蓝色
        background: 'linear-gradient(135deg, rgba(21, 101, 192, 0.1), rgba(13, 71, 161, 0.15))', // 深蓝色渐变背景
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: 'linear-gradient(90deg, #1565c0, #1976d2, #1565c0)', // 深蓝色渐变顶边
            zIndex: 3,
            boxShadow: '0 2px 4px rgba(21, 101, 192, 0.5)',
        },
        '&:hover': {
            background: 'linear-gradient(135deg, rgba(21, 101, 192, 0.15), rgba(13, 71, 161, 0.2))',
            borderColor: '#1976d2', // 悬停时稍亮的蓝色
        },
    },

    // 调整卡片内容布局以适应新的高度
    cardContent: {
        padding: theme.spacing(2), // 增加内边距
        position: 'relative',
        zIndex: 2,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100%', // 占满卡片高度
        minHeight: 0, // 确保flex子元素可以收缩
    },

    // 调整头像容器
    avatarContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: theme.spacing(1.2),
    },

    // 添加链接样式
    twitchLink: {
        color: '#47BCD3',
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'underline',
        },
    },

    externalLinkIcon: {
        margin: '3px 0px 0px 5px',
        color: '#47BCD3',
        fontSize: '14px',
    },

    // 修改头像样式，添加点击提示
    cardAvatar: {
        width: 60,
        height: 60,
        borderRadius: '50%',
        border: '2px solid rgba(255, 255, 255, 0.15)',
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
        position: 'relative',
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '24px',
        fontWeight: 'bold',
        color: 'white',
        cursor: 'pointer', // 添加指针样式
        transition: 'all 0.2s ease',
        '&:hover': {
            transform: 'scale(1.05)',
            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.6)',
        },
    },

    // 修改用户名样式，添加点击提示
    cardUsername: {
        fontSize: '16px',
        fontWeight: 700,
        marginBottom: theme.spacing(1.2),
        color: '#ffffff',
        textAlign: 'center',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        lineHeight: 1.2,
        letterSpacing: '0.5px',
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
        cursor: 'pointer', // 添加指针样式
        transition: 'all 0.2s ease',
        '&:hover': {
            color: '#47BCD3',
            textShadow: '0 1px 2px rgba(71, 188, 211, 0.5)',
        },
    },

    // 调整统计信息样式 - 更紧凑
    cardStats: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(1.5), // 从3减少到2
        fontSize: '12px', // 从14px减少到12px
        color: '#b0b0b0', // 稍微亮一点的颜色
        padding: theme.spacing(0.6, 1), // 减少内边距
        backgroundColor: 'rgba(0, 0, 0, 0.35)', // 增加不透明度
        borderRadius: 6, // 从8减少到6
        border: '1px solid rgba(255, 255, 255, 0.1)', // 添加边框
        fontWeight: 500, // 增加字重
    },

    // 修改平台绑定信息样式
    platformBinding: {
        marginBottom: theme.spacing(1.5), // 从2减少到1.5
        padding: theme.spacing(0.8), // 从1减少到0.8
        backgroundColor: 'rgba(0, 0, 0, 0.25)',
        borderRadius: 6,
        border: '1px solid rgba(255, 255, 255, 0.1)',
    },

    // 调整星级评分区域 - 更紧凑
    starsGroup: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(1), // 从1.2增加到1.5
        marginBottom: theme.spacing(1.5), // 从2减少到2
        flex: 1,
        justifyContent: 'center',
        padding: theme.spacing(1, 0.8), // 增加左右内边距，从0.8,0改为1.2,1
        backgroundColor: 'rgba(0, 0, 0, 0.15)', // 添加背景
        borderRadius: 8,
        border: '1px solid rgba(255, 255, 255, 0.05)',
    },

    starsContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        minWidth: 160, // 减少最小宽度
        fontSize: '12px', // 从14px减少到12px
        width: '100%',
        justifyContent: 'space-between',
        padding: theme.spacing(0.3, 0.5), // 添加容器内边距
    },

    starsLabel: {
        fontSize: '12px', // 从14px减少到12px
        color: '#b0b0b0',
        minWidth: 50, // 减少最小宽度
        textAlign: 'left',
        fontWeight: 600, // 增加字重
        letterSpacing: '0.3px',
        paddingRight: theme.spacing(0.5), // 添加右内边距
    },

    starsDisplay: {
        display: 'flex',
        gap: 4, // 从3增加到4，星星之间的间距
        flex: 1,
        justifyContent: 'flex-end',
        paddingLeft: theme.spacing(0.5), // 添加左内边距
    },

    starIcon: {
        fontSize: '16px', // 从18px减少到16px
        margin: '0 1px', // 添加星星的左右边距
        '&.active': {
            color: '#ffd700',
            filter: 'drop-shadow(0 0 2px #ffd700)', // 减少发光效果
        },
        '&.inactive': {
            color: '#4a4a4a',
        },
    },

    // 修改匹配度区域 - 横向布局
    cardMatch: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingTop: theme.spacing(1.5),
        borderTop: '2px solid rgba(255, 255, 255, 0.15)',
        fontSize: '14px',
        marginTop: 'auto',
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
        marginLeft: theme.spacing(-2),
        marginRight: theme.spacing(-2),
        marginBottom: theme.spacing(-2),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        paddingBottom: theme.spacing(1.5),
        minHeight: 60, // 确保有足够的最小高度
    },

    // 匹配度信息容器 - 新增
    matchInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1), // 标签和值之间的间距
        flex: 1, // 占用剩余空间
    },

    matchLabel: {
        color: '#b0b0b0',
        fontWeight: 600,
        fontSize: '12px',
        letterSpacing: '0.3px',
        whiteSpace: 'nowrap', // 防止换行
    },

    matchValue: {
        fontWeight: 800,
        color: '#ffd700',
        fontSize: '16px',
        textShadow: '0 1px 3px rgba(255, 215, 0, 0.5)',
        letterSpacing: '0.5px',
        whiteSpace: 'nowrap', // 防止换行
    },

    // 调整主播类型标签 - 更像扑克牌角标
    streamerTypeBadge: {
        position: 'absolute',
        top: 8, // 从12减少到8
        right: 8, // 从12减少到8
        padding: '4px 8px', // 减少内边距
        borderRadius: 12, // 减少圆角
        fontSize: '10px', // 从12px减少到10px
        fontWeight: 600,
        zIndex: 3,
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
        border: '1px solid rgba(255, 255, 255, 0.2)', // 添加边框
        letterSpacing: '0.3px',
        textTransform: 'uppercase', // 大写字母，更有卡牌感
    },

    // 修改样式中的选中状态
    selectedCard: {
        transform: 'translateY(-6px)',
        animation: '$selectedGlow 1.2s ease-in-out infinite',
        '&.premium-selected': {
            borderColor: '#6A0DAD !important', // 改为紫色
            background: 'rgba(106, 13, 173, 0.08)',
            boxShadow: '0 0 0 2px rgba(106, 13, 173, 0.5)',
        },
        '&.potential-selected': {
            borderColor: '#FFD700 !important',
            background: 'rgba(184, 134, 11, 0.15) !important', // 改为更深的金色，降低透明度
            boxShadow: '0 0 0 2px rgba(255, 215, 0, 0.5)',
        },
        '&.normal-selected': {
            borderColor: '#1565c0 !important', // 保持深蓝色
            background: 'rgba(21, 101, 192, 0.15) !important',
            boxShadow: '0 0 0 2px rgba(21, 101, 192, 0.6) !important',
        },
    },

    '@keyframes selectedGlow': {
        '0%': {
            boxShadow: '0 0 0 2px var(--glow-color-50), 0 0 12px 4px var(--glow-color-30)'
        },
        '50%': {
            boxShadow: '0 0 0 2px var(--glow-color-60), 0 0 16px 6px var(--glow-color-40)'
        },
        '100%': {
            boxShadow: '0 0 0 2px var(--glow-color-50), 0 0 12px 4px var(--glow-color-30)'
        },
    },

    // 修改批量操作按钮容器样式
    bulkActions: {
        display: 'flex',
        gap: theme.spacing(1.5), // 使用与一键选择和取消选中相同的间距
        alignItems: 'center',
        marginLeft: theme.spacing(1.5), // 添加左边距，与标题保持一致的间距
    },

    // 修改分组管理按钮样式（清除选择也会用到这个）
    groupActionBtn: {
        backgroundColor: '#4a148c', // 改为更深的紫色
        color: 'white',
        border: 'none',
        '&:hover': {
            backgroundColor: '#6a1b9a', // 悬停时稍亮
        },
        '&:disabled': {
            backgroundColor: '#424242', // 禁用时使用灰色
            color: '#9e9e9e', // 禁用时文字为浅灰色
            border: 'none',
        },
    },

    // 修改卡片选择框样式，允许点击
    // 调整选择框大小和位置
    cardSelectBox: {
        position: 'absolute',
        top: 8, // 从12减少到8
        left: 8, // 从12减少到8
        width: 22, // 从24减少到22
        height: 22,
        border: '2px solid rgba(255, 255, 255, 0.4)',
        borderRadius: 4,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s ease',
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        zIndex: 4,
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
        '&:hover': {
            borderColor: 'rgba(255, 255, 255, 0.7)',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            transform: 'scale(1.1)',
        },
        '&.checked': {
            '& .MuiSvgIcon-root': {
                color: 'white',
                fontSize: '14px', // 从16px减少到14px
            },
        },
    },

    // 修改选中卡片的发光效果，使用动态颜色
    selectedCardGlow: {
        animation: '$cardGlow 2s ease-in-out infinite',
        // 移除固定的boxShadow，使用CSS变量动态设置
    },

    '@keyframes cardGlow': {
        '0%': {
            boxShadow: '0 0 0 4px var(--type-color-60), 0 0 20px var(--type-color-40)',
            transform: 'translateY(-8px)',
        },
        '50%': {
            boxShadow: '0 0 0 6px var(--type-color-80), 0 0 30px var(--type-color-60)',
            transform: 'translateY(-10px)',
        },
        '100%': {
            boxShadow: '0 0 0 4px var(--type-color-60), 0 0 20px var(--type-color-40)',
            transform: 'translateY(-8px)',
        },
    },

    // 分组管理弹窗样式
    groupDialog: {
        '& .MuiDialog-paper': {
            backgroundColor: '#232b42',
            color: '#e0e0e0',
            minWidth: '500px',
        },
    },

    groupName: {
        fontSize: '16px',
        fontWeight: 600,
        color: '#e0e0e0',
        marginBottom: 4,
    },

    // 确保基础按钮样式也支持禁用状态
    bulkActionBtn: {
        padding: theme.spacing(1, 2),
        borderRadius: 6,
        fontSize: '14px',
        fontWeight: 500,
        transition: 'all 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        minHeight: 36,
        textTransform: 'none',
        '&:hover:not(:disabled)': { // 只在非禁用状态下应用悬停效果
            filter: 'brightness(1.1)',
        },
        '&:disabled': {
            cursor: 'not-allowed',
            opacity: 0.7, // 禁用时降低透明度
        },
    },

    // 修改一键选择按钮样式
    selectAllBtn: {
        backgroundColor: '#0d47a1', // 改为更深的蓝色
        color: 'white',
        border: 'none',
        '&:hover': {
            backgroundColor: '#1565c0', // 悬停时稍亮
        },
        '&:disabled': {
            backgroundColor: '#424242', // 禁用时使用灰色
            color: '#9e9e9e', // 禁用时文字为浅灰色
            border: 'none',
        },
    },

    deselectAllBtn: {
        backgroundColor: '#d84315', // 改为更深的橙红色
        color: 'white',
        border: 'none',
        '&:hover': {
            backgroundColor: '#ff5722', // 悬停时稍亮
        },
        '&:disabled': {
            backgroundColor: '#424242', // 禁用时使用灰色
            color: '#9e9e9e', // 禁用时文字为浅灰色
            border: 'none',
        },
    },
}));

export default function UserGameTagsManager({
    selectedLanguage = 'en',
    onLanguageChange
}) {
    const classes = useStyles();

    const [isLoading, setIsLoading] = useState(false);
    const [isTagsLoading, setIsTagsLoading] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);

    // 添加卡片选择和分组相关状态
    const [selectedCards, setSelectedCards] = useState(new Set()); // 用于卡片选择
    const [selectedUsersDetails, setSelectedUsersDetails] = useState([]);

    // 添加主播包相关状态
    const [packageSelectionDialogOpen, setPackageSelectionDialogOpen] = useState(false);
    const [gameGroups, setGameGroups] = useState([]);
    const [customGroups, setCustomGroups] = useState([]);
    const [selectedGameForPackage, setSelectedGameForPackage] = useState('');
    const [selectedGroupForPackage, setSelectedGroupForPackage] = useState('');
    const [selectedPackageForUser, setSelectedPackageForUser] = useState('');
    const [isLoadingPackages, setIsLoadingPackages] = useState(false);
    const [isAddingToPackage, setIsAddingToPackage] = useState(false);

    // 添加排序相关状态
    const [sortBy, setSortBy] = useState('match_score'); // 默认按匹配度排序

    // 语言相关状态 - 修复初始化
    const [interfaceLanguage, setInterfaceLanguage] = useState(() => {
        try {
            // 如果父组件传入了语言，优先使用
            if (selectedLanguage && ['en', 'zh'].includes(selectedLanguage)) {
                return selectedLanguage;
            }

            // 从本地存储获取
            const savedLanguage = localStorage.getItem('interfaceLanguage') || localStorage.getItem('preferredLanguage');
            if (savedLanguage && ['en', 'zh'].includes(savedLanguage)) {
                return savedLanguage;
            }

            // 自动检测
            return detectBrowserLanguage();
        } catch (error) {
            console.error('Error initializing interface language:', error);
            return 'en';
        }
    });

    // 获取当前语言的文本
    const getText = (key, params = {}) => {
        return getMultilingualText(key, interfaceLanguage, params);
    };

    // 修复监听父组件传入的语言变化
    useEffect(() => {
        if (selectedLanguage &&
            selectedLanguage !== interfaceLanguage &&
            ['en', 'zh'].includes(selectedLanguage)) {
            console.log('UserGameTagsManager: syncing language from parent', selectedLanguage);
            setInterfaceLanguage(selectedLanguage);
            // 同步到本地存储
            localStorage.setItem('interfaceLanguage', selectedLanguage);
        }
    }, [selectedLanguage, interfaceLanguage]);

    // 新增：打开主播包选择弹窗
    const handleAddToStreamerPackage = async () => {
        if (selectedCards.size === 0) {
            setSnackbar({
                open: true,
                message: '请先选择要添加的用户',
                severity: 'warning',
            });
            return;
        }

        setPackageSelectionDialogOpen(true);
        setIsLoadingPackages(true);

        try {
            // 调用 StreamerGroupManager 的数据加载逻辑
            await loadStreamerPackageData();
        } catch (error) {
            console.error('加载主播包数据失败:', error);
            setSnackbar({
                open: true,
                message: `加载主播包数据失败: ${error.message}`,
                severity: 'error',
            });
        } finally {
            setIsLoadingPackages(false);
        }
    };

    // 加载主播包数据（复用 StreamerGroupManager 的逻辑）
    const loadStreamerPackageData = async () => {
        try {
            const response = await fetch('/api/v1/admin/game/get-package-users?' + new URLSearchParams({
                days: 30,
                page: 1,
                limit: 1000,
                sortBy: 'match_score'
            }), {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            if (response.ok) {
                const result = await response.json();
                if (result.code === 'SUCCESS' && result.data) {
                    processPackageData(result.data);
                } else {
                    throw new Error(result.message || '获取数据失败');
                }
            } else {
                throw new Error('网络请求失败');
            }
        } catch (error) {
            throw error;
        }
    };

    // 处理主播包数据
    const processPackageData = (apiData) => {
        const { games } = apiData;
        const processedGameGroups = [];
        const processedCustomGroups = [];

        games.forEach(game => {
            if (game.groups && game.groups.length > 0) {
                game.groups.forEach(group => {
                    const groupData = {
                        id: `${game.id}_${group.id}`,
                        gameId: game.id,
                        groupId: group.id,
                        gameName: game.name,
                        gameNameInTwitch: game.nameInTwitch,
                        groupName: group.name,
                        groupType: group.type,
                        status: group.status,
                        packages: []
                    };

                    if (group.packages && group.packages.length > 0) {
                        group.packages.forEach(pkg => {
                            groupData.packages.push({
                                id: pkg.id,
                                name: pkg.name,
                                description: pkg.description,
                                type: pkg.type,
                                status: pkg.status,
                                streamerCount: pkg.streamerCount || 0,
                                maxStreamers: pkg.settings?.maxStreamers || 100,
                                settings: pkg.settings
                            });
                        });
                    }

                    if (group.type === 'default') {
                        processedGameGroups.push({
                            ...groupData,
                            type: 'game'
                        });
                    } else {
                        processedCustomGroups.push({
                            ...groupData,
                            type: 'custom'
                        });
                    }
                });
            }
        });

        setGameGroups(processedGameGroups);
        setCustomGroups(processedCustomGroups);
    };

    // 修复获取可用的主播包函数，添加更多安全检查
    const getAvailablePackages = () => {
        try {
            if (!selectedGameForPackage) return [];

            const allGroups = [...(gameGroups || []), ...(customGroups || [])];
            // 找到匹配游戏的默认分组
            const gameGroup = allGroups.find(group =>
                group &&
                group.gameName &&
                group.gameName === selectedGameForPackage &&
                group.groupType === 'default' // 只查找默认分组
            );

            return (gameGroup && gameGroup.packages) ? gameGroup.packages : [];
        } catch (error) {
            console.error('获取可用主播包时出错:', error);
            return [];
        }
    };

    // 添加用户到主播包
    const handleAddUsersToPackage = async () => {
        if (!selectedPackageForUser) {
            setSnackbar({
                open: true,
                message: '请选择主播包',
                severity: 'warning',
            });
            return;
        }

        const selectedUserIds = Array.from(selectedUsers);

        if (selectedUserIds.length === 0) {
            setSnackbar({
                open: true,
                message: '请选择要添加的用户',
                severity: 'warning',
            });
            return;
        }

        setIsAddingToPackage(true);

        try {
            const response = await fetch('/api/admin/game/update-users-to-streampackage', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    packageId: selectedPackageForUser,
                    userIds: selectedUserIds,
                    operation: 'add'
                })
            });

            const result = await response.json();

            if (response.ok && result.code === 'SUCCESS') {
                const { summary, package: packageInfo } = result.data;

                setSnackbar({
                    open: true,
                    message: `成功添加 ${summary.addedCount} 个用户到主播包"${packageInfo.name}"`,
                    severity: 'success',
                });

                // 清除选择状态
                setSelectedCards(new Set());
                setSelectedUsers(new Set());
                setSelectedUsersDetails([]);

                // 关闭弹窗
                setPackageSelectionDialogOpen(false);
                setTimeout(() => {
                    resetPackageSelection();
                }, 100);

            } else {
                throw new Error(result.message || '添加失败');
            }
        } catch (error) {
            console.error('添加用户到主播包失败:', error);
            setSnackbar({
                open: true,
                message: `添加失败: ${error.message}`,
                severity: 'error',
            });
        } finally {
            setIsAddingToPackage(false);
        }
    };

    // 重置主播包选择状态
    const resetPackageSelection = () => {
        try {
            if (typeof setSelectedGameForPackage === 'function') {
                setSelectedGameForPackage('');
            }
            if (typeof setSelectedGroupForPackage === 'function') {
                setSelectedGroupForPackage(''); // 自动选择，但重置时清空
            }
            if (typeof setSelectedPackageForUser === 'function') {
                setSelectedPackageForUser('');
            }
            if (typeof setGameSearchInput === 'function') {
                setGameSearchInput('');
            }
            if (typeof setFilteredGames === 'function') {
                setFilteredGames([]);
            }
            if (typeof setGameInputFocused === 'function') {
                setGameInputFocused(false);
            }
        } catch (error) {
            console.error('重置主播包选择状态时出错:', error);
        }
    };

    // 添加选中用户管理
    const [selectedUsers, setSelectedUsers] = useState(new Set());

    // 添加到 UserGameTagsManager 组件中
    const [statsDialogOpen, setStatsDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [statTab, setStatTab] = useState(0);
    // 添加更新主播评价相关状态

    const handleOpenStatsDialog = (user) => {
        setSelectedUser(user);
        setStatsDialogOpen(true);
    };

    const handleCloseStatsDialog = () => {
        setStatsDialogOpen(false);
        setSelectedUser(null);
        setStatTab(0);
    };

    const handleTabChange = (event, newValue) => {
        setStatTab(newValue);
    };

    const [allTags, setAllTags] = useState({
        genre: [],
        theme: [],
    });

    // 获取用户头像首字母
    const getUserInitial = (user) => {
        const name = user.username || user.displayName || user.connectedAccounts?.twitch?.username || 'U';
        return name.charAt(0).toUpperCase();
    };

    // 渲染星级评分
    const renderStars = (count) => {
        const stars = [];
        for (let i = 1; i <= 5; i++) {
            stars.push(
                <StarIcon
                    key={i}
                    className={`${classes.starIcon} ${i <= count ? 'active' : 'inactive'}`}
                />
            );
        }
        return stars;
    };

    // 获取主播评价星级
    const getStreamerRatingStars = (user) => {
        if (!user.streamerRatings) {
            return { activity: 0, execution: 0, costEffect: 0 };
        }

        const { activityLevel, completionAbility, costEffectiveness } = user.streamerRatings;

        // 根据评级代码转换为星级（简化逻辑）
        const getStarLevel = (rating) => {
            if (!rating || !rating.code) return 0;
            const code = rating.code;
            if (code.includes('110') || code.includes('210') || code.includes('310')) return 5;
            if (code.includes('120') || code.includes('220') || code.includes('320')) return 4;
            if (code.includes('130') || code.includes('230') || code.includes('330')) return 3;
            if (code.includes('140') || code.includes('240') || code.includes('340')) return 2;
            return 1;
        };

        return {
            activity: getStarLevel(activityLevel),
            execution: getStarLevel(completionAbility),
            costEffect: getStarLevel(costEffectiveness),
        };
    };

    // 在现有的帮助函数中添加获取绑定平台数量的函数
    const getConnectedPlatformsCount = (user) => {
        if (!user?.connectedAccounts) return 0;

        const platforms = ['twitch', 'youtube', 'tiktok', 'twitter'];
        let connectedCount = 0;

        platforms.forEach(platform => {
            const account = user.connectedAccounts[platform];
            if (account && account.id) {
                connectedCount++;
            }
        });

        return connectedCount;
    };

    // 获取已绑定的平台列表
    const getConnectedPlatforms = (user) => {
        if (!user?.connectedAccounts) return [];

        const platforms = [
            {
                key: 'twitch',
                name: 'Twitch',
                color: '#9146ff',
                icon: TwitchIcon,
                iconStyle: { width: 12, height: 12 }
            },
            {
                key: 'youtube',
                name: 'YouTube',
                color: '#ff0000',
                icon: YouTubeIcon,
                iconStyle: { width: 12, height: 12 }
            },
            {
                key: 'tiktok',
                name: 'TikTok',
                color: '#fe2c55',
                icon: TikTokIcon,
                iconStyle: { width: 12, height: 12 }
            },
            {
                key: 'twitter',
                name: 'Twitter',
                color: '#1da1f2',
                icon: TwitterIcon,
                iconStyle: { width: 12, height: 12 }
            }
        ];

        return platforms.filter(platform => {
            const account = user.connectedAccounts[platform.key];
            return account && account.id;
        });
    };

    // 修改格式化数字函数，确保ACCV相关数据显示为整数
    const formatNumber = (num) => {
        if (!num && num !== 0) return getText('dataNotAvailable');

        // 确保数值为整数（去除小数）
        const intNum = Math.round(num);

        if (intNum >= 1000000000) return `${Math.round(intNum / 1000000000)}${getText('billionAbbrev')}`;
        if (intNum >= 1000000) return `${Math.round(intNum / 1000000)}${getText('millionAbbrev')}`;
        if (intNum >= 1000) return `${Math.round(intNum / 1000)}${getText('thousandAbbrev')}`;
        return intNum.toLocaleString();
    };

    // 修改用户卡片渲染，确保选择状态正确显示
    const renderUserCards = () => {
        if (isLoading) {
            return (
                <Box display="flex" justifyContent="center" alignItems="center" height="300px">
                    <CircularProgress />
                </Box>
            );
        }

        if (users.length === 0) {
            return (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="300px">
                    <InfoIcon style={{ fontSize: '3rem', marginBottom: 16, color: '#64b5f6' }} />
                    <Typography variant="h6" color="textSecondary">{getText('noUsers')}</Typography>
                </Box>
            );
        }

        return (
            <div className={classes.cardContainer}>
                {users.map((user) => {
                    const userType = determineUserStreamerType(user);
                    const stars = getStreamerRatingStars(user);
                    // 使用 selectedUsers 而不是 selectedCards 来判断选择状态
                    const isSelected = selectedUsers.has(user.id);
                    const isCardSelected = selectedUsers.has(user.id); // 统一使用 selectedUsers

                    // 获取绑定平台信息
                    const connectedPlatformsCount = getConnectedPlatformsCount(user);
                    const connectedPlatforms = getConnectedPlatforms(user);

                    // 确定卡片等级样式
                    let cardLevelClass = '';
                    let selectedClass = '';
                    if (userType === 'premium') {
                        cardLevelClass = classes.premiumCard;
                        selectedClass = 'premium-selected';
                    } else if (userType === 'potential') {
                        cardLevelClass = classes.potentialCard;
                        selectedClass = 'potential-selected';
                    } else if (userType === 'normal') {
                        cardLevelClass = classes.normalCard;
                        selectedClass = 'normal-selected';
                    }

                    // 获取用户类型对应的颜色
                    const typeColor = getStreamerTypeColor(userType);
                    const typeColorRgb = getTypeColorRgb(userType);

                    // 设置CSS变量用于动画
                    const cardStyle = {
                        '--glow-color-30': `${typeColorRgb}, 0.3)`,
                        '--glow-color-40': `${typeColorRgb}, 0.4)`,
                        '--glow-color-50': `${typeColorRgb}, 0.5)`,
                        '--glow-color-60': `${typeColorRgb}, 0.6)`,
                        '--type-color-40': `${typeColorRgb}, 0.4)`,
                        '--type-color-60': `${typeColorRgb}, 0.6)`,
                        '--type-color-80': `${typeColorRgb}, 0.8)`,
                    };

                    const getAvatarUrl = (user) => {
                        if (user.connectedAccounts?.twitch?.profile_image_url) {
                            return user.connectedAccounts.twitch.profile_image_url;
                        }
                        if (user.avatar) {
                            return user.avatar;
                        }
                        return null;
                    };

                    const avatarUrl = getAvatarUrl(user);

                    const getAvatarBackgroundColor = (userType) => {
                        switch (userType) {
                            case 'premium':
                                return '#6A0DAD';
                            case 'potential':
                                return '#FFD700';
                            case 'normal':
                                return '#1565c0';
                            default:
                                return '#4a4a4a';
                        }
                    };

                    const avatarBackgroundColor = getAvatarBackgroundColor(userType);

                    // 获取 Twitch 用户名的函数
                    const getTwitchUsername = (user) => {
                        return user.connectedAccounts?.twitch?.username ||
                            user.twitchUsername ||
                            user.username ||
                            user.displayName ||
                            null;
                    };

                    const twitchUsername = getTwitchUsername(user);

                    // 处理头像点击事件
                    const handleAvatarClick = (e) => {
                        e.stopPropagation();
                        if (twitchUsername) {
                            window.open(`https://twitch.tv/${twitchUsername}`, '_blank');
                        }
                    };

                    // 处理用户名点击事件
                    const handleUsernameClick = (e) => {
                        e.stopPropagation();
                        // 阻止卡片选择事件
                    };

                    // 渲染用户名链接
                    const renderUsernameLinks = () => {
                        const displayName = user.username || user.displayName || getText('unknownUser');

                        if (!twitchUsername) {
                            return (
                                <div className={classes.cardUsername}>
                                    {displayName}
                                </div>
                            );
                        }

                        return (
                            <div
                                className={classes.cardUsername}
                                onClick={handleUsernameClick}
                                style={{ cursor: 'default' }}
                            >
                                <Box display="flex" alignItems="center" justifyContent="center" flexWrap="wrap">
                                    <Link
                                        href={`https://twitch.tv/${twitchUsername}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={classes.twitchLink}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <Tooltip title={getText('openTwitchPage')} placement="top">
                                            <span>{twitchUsername}</span>
                                        </Tooltip>
                                    </Link>
                                    <Link
                                        href={`https://sullygnome.com/channel/${twitchUsername}/30`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <Tooltip title={getText('openSullyGnomeDataPage')} placement="top">
                                            <i
                                                className="fa fa-external-link"
                                                style={{
                                                    margin: '3px 0px 0px 5px',
                                                    color: '#47BCD3',
                                                    fontSize: '14px',
                                                }}
                                            />
                                        </Tooltip>
                                    </Link>
                                </Box>
                            </div>
                        );
                    };

                    const accv30 = Math.round(user.statistics?.AVG30 || 0);
                    return (
                        <div
                            key={user.id}
                            className={`${classes.userCard} ${cardLevelClass} ${isSelected ? `${classes.selectedCard} ${selectedClass}` : ''} ${isCardSelected ? classes.selectedCardGlow : ''}`}
                            style={cardStyle}
                        >
                            {/* 选择状态指示器 */}
                            <div
                                className={`${classes.cardSelectBox} ${isCardSelected ? 'checked' : ''}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleCardSelection(user.id);
                                }}
                                style={{
                                    backgroundColor: isCardSelected ? typeColor : 'rgba(0, 0, 0, 0.3)',
                                    borderColor: isCardSelected ? typeColor : 'rgba(255, 255, 255, 0.3)',
                                }}
                            >
                                {isCardSelected && <CheckIcon />}
                            </div>

                            {/* 主播类型标签 */}
                            {userType && (
                                <div
                                    className={classes.streamerTypeBadge}
                                    style={{
                                        backgroundColor: typeColor,
                                        color: 'white'
                                    }}
                                >
                                    {streamerTypeMap[userType]}
                                </div>
                            )}

                            <div className={classes.cardContent}>
                                {/* 头像区域 */}
                                <div className={classes.avatarContainer}>
                                    <Tooltip
                                        title={twitchUsername ? getText('visitTwitchPage', { username: twitchUsername }) : getText('noTwitchInfo')}
                                        placement="top"
                                    >
                                        <div className={classes.cardAvatar}
                                            style={{
                                                backgroundColor: avatarBackgroundColor,
                                                cursor: twitchUsername ? 'pointer' : 'default',
                                            }}
                                            onClick={twitchUsername ? handleAvatarClick : undefined}
                                        >
                                            {avatarUrl ? (
                                                <img
                                                    src={avatarUrl}
                                                    alt="用户头像"
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                        borderRadius: '50%'
                                                    }}
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextSibling.style.display = 'flex';
                                                    }}
                                                />
                                            ) : null}
                                            <div
                                                style={{
                                                    display: avatarUrl ? 'none' : 'flex',
                                                    width: '100%',
                                                    height: '100%',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '24px',
                                                    fontWeight: 'bold',
                                                    color: 'white'
                                                }}
                                            >
                                                {getUserInitial(user)}
                                            </div>
                                        </div>
                                    </Tooltip>
                                </div>

                                {/* 修改用户名区域 - 添加链接 */}
                                {renderUsernameLinks()}

                                {/* 统计信息 */}
                                <div className={classes.cardStats}>
                                    <span>{getText('followersLabel')}: {(user.connectedAccounts?.twitch?.followers || 0).toLocaleString()}</span>
                                    <span>ACCV: {accv30.toLocaleString()}</span>
                                </div>

                                {/* 修改平台绑定信息显示，使用SVG图标 */}
                                <Box className={classes.platformBinding}>
                                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.8}>
                                        <Typography variant="caption" style={{ color: '#b0b0b0', fontWeight: 600, fontSize: '11px' }}>
                                            {getText('boundPlatforms')}
                                        </Typography>
                                        <Chip
                                            size="small"
                                            label={`${connectedPlatformsCount}/4`}
                                            style={{
                                                backgroundColor: connectedPlatformsCount >= 3 ? '#4caf50' :
                                                    connectedPlatformsCount >= 2 ? '#ff9800' : '#f44336',
                                                color: 'white',
                                                fontSize: '9px',
                                                height: '16px',
                                                fontWeight: 'bold'
                                            }}
                                        />
                                    </Box>

                                    {connectedPlatforms.length > 0 ? (
                                        <Box display="flex" flexWrap="wrap" gap={0.8} mt={1}> {/* 从gap={0.4}增加到gap={0.8} */}
                                            {connectedPlatforms.map(platform => (
                                                <Tooltip
                                                    key={platform.key}
                                                    title={`${platform.name} 已绑定`}
                                                    placement="top"
                                                >
                                                    <Box
                                                        style={{
                                                            backgroundColor: platform.color,
                                                            color: 'white',
                                                            borderRadius: 4, // 从3增加到4
                                                            padding: '3px 6px', // 从'2px 4px'增加到'3px 6px'
                                                            fontSize: '9px',
                                                            fontWeight: 'bold',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 4, // 从3增加到4
                                                            boxShadow: `0 1px 3px ${platform.color}60`,
                                                            minWidth: 'fit-content',
                                                            margin: '1px' // 新增：为每个平台标签添加外边距
                                                        }}
                                                    >
                                                        <img
                                                            src={platform.icon}
                                                            alt={platform.name}
                                                            style={platform.iconStyle}
                                                        />
                                                        <span>{platform.name}</span>
                                                    </Box>
                                                </Tooltip>
                                            ))}
                                        </Box>
                                    ) : (
                                        <Typography variant="caption" style={{ color: '#888', fontStyle: 'italic', fontSize: '10px' }}>
                                            未绑定任何平台
                                        </Typography>
                                    )}
                                </Box>

                                {/* 星级评分区域 */}
                                <div className={classes.starsGroup}>
                                    <div className={classes.starsContainer}>
                                        <div className={classes.starsLabel}>{getText('activity')}</div>
                                        <div className={classes.starsDisplay}>
                                            {renderStars(stars.activity)}
                                        </div>
                                    </div>

                                    <div className={classes.starsContainer}>
                                        <div className={classes.starsLabel}>{getText('execution')}</div>
                                        <div className={classes.starsDisplay}>
                                            {renderStars(stars.execution)}
                                        </div>
                                    </div>

                                    <div className={classes.starsContainer}>
                                        <div className={classes.starsLabel}>{getText('costEffect')}</div>
                                        <div className={classes.starsDisplay}>
                                            {renderStars(stars.costEffect)}
                                        </div>
                                    </div>
                                </div>

                                {/* 匹配度区域 - 确保在底部且可见 */}
                                <div className={classes.cardMatch}>
                                    <div className={classes.matchInfo}>
                                        <span className={classes.matchLabel}>{getText('matchDegree')}</span>
                                        <span className={classes.matchValue}>{Math.round(user.matchScore || 0)}%</span>
                                    </div>

                                    <IconButton
                                        className={classes.statsButton}
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenStatsDialog(user);
                                        }}
                                        title="查看详细统计"
                                    >
                                        <AssessmentIcon fontSize="small" />
                                    </IconButton>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    // 添加获取用户类型RGB颜色值的函数
    const getTypeColorRgb = (type) => {
        switch (type) {
            case 'premium':
                return 'rgba(106, 13, 173'; // 红色
            case 'potential':
                return 'rgba(255, 215, 0'; // 更暗的金色
            case 'normal':
                return 'rgba(21, 101, 192';
            default:
                return 'rgba(176, 176, 176'; // 灰色
        }
    };

    // 修改 handleCardSelection 函数，保持简单的选择逻辑
    const handleCardSelection = (userId) => {
        const user = users.find(u => u.id === userId);
        if (!user) return;

        setSelectedCards(prev => {
            const newSet = new Set(prev);
            if (newSet.has(userId)) {
                newSet.delete(userId);
            } else {
                newSet.add(userId);
            }
            return newSet;
        });

        setSelectedUsers(prev => {
            const newSet = new Set(prev);
            if (newSet.has(userId)) {
                newSet.delete(userId);
            } else {
                newSet.add(userId);
            }
            return newSet;
        });

        // 同步更新详细信息列表
        setSelectedUsersDetails(prev => {
            const exists = prev.find(u => u.id === userId);
            if (exists) {
                // 如果存在，移除
                return prev.filter(u => u.id !== userId);
            } else {
                // 如果不存在，添加用户详细信息
                return [...prev, {
                    id: user.id,
                    username: user.username || user.displayName || '未知用户',
                    type: determineUserStreamerType(user),
                    matchScore: user.matchScore || 0,
                    followers: user.connectedAccounts?.twitch?.followers || 0,
                    accv: user.statistics?.AVG30 || 0
                }];
            }
        });
    };

    // 修改一键选择功能，同时存储详细信息
    const handleSelectAll = () => {
        if (users.length === 0) {
            setSnackbar({
                open: true,
                message: '没有可选择的用户',
                severity: 'warning',
            });
            return;
        }

        const userIds = users.map(user => user.id);

        // 过滤出当前页面中尚未选择的用户
        const newUsers = users.filter(user => !selectedUsers.has(user.id));

        // 添加到选择状态
        setSelectedUsers(prev => {
            const newSet = new Set(prev);
            userIds.forEach(id => newSet.add(id));
            return newSet;
        });

        setSelectedCards(prev => {
            const newSet = new Set(prev);
            userIds.forEach(id => newSet.add(id));
            return newSet;
        });

        // 添加新用户的详细信息
        setSelectedUsersDetails(prev => {
            const newDetails = newUsers.map(user => ({
                id: user.id,
                username: user.username || user.displayName || '未知用户',
                type: determineUserStreamerType(user),
                matchScore: user.matchScore || 0,
                followers: user.connectedAccounts?.twitch?.followers || 0,
                accv: user.statistics?.AVG30 || 0
            }));

            return [...prev, ...newDetails];
        });

        setSnackbar({
            open: true,
            message: `已添加当前页面的 ${newUsers.length} 个用户到选择列表`,
            severity: 'success',
        });
    };

    // 修改取消选中功能
    const handleDeselectAll = () => {
        if (selectedUsers.size === 0 && selectedCards.size === 0) {
            setSnackbar({
                open: true,
                message: '没有已选择的用户',
                severity: 'warning',
            });
            return;
        }

        const prevCount = Math.max(selectedUsers.size, selectedCards.size);

        // 清除所有选择状态和详细信息
        setSelectedUsers(new Set());
        setSelectedCards(new Set());
        setSelectedUsersDetails([]);

        setSnackbar({
            open: true,
            message: `已取消选择 ${prevCount} 个用户`,
            severity: 'info',
        });
    };

    // 新增：从选择列表中移除单个用户的函数
    const handleRemoveSelectedUser = (userId) => {
        const userDetail = selectedUsersDetails.find(u => u.id === userId);

        setSelectedUsers(prev => {
            const newSet = new Set(prev);
            newSet.delete(userId);
            return newSet;
        });

        setSelectedCards(prev => {
            const newSet = new Set(prev);
            newSet.delete(userId);
            return newSet;
        });

        setSelectedUsersDetails(prev => prev.filter(u => u.id !== userId));

        setSnackbar({
            open: true,
            message: `已移除用户 "${userDetail?.username || '未知用户'}" 从选择列表`,
            severity: 'info',
        });
    };

    // 添加游戏搜索相关状态
    const [gameSearchInput, setGameSearchInput] = useState('');
    const [filteredGames, setFilteredGames] = useState([]);
    const [gameInputFocused, setGameInputFocused] = useState(false);

    // 游戏搜索过滤函数
    const filterGames = (searchTerm) => {
        try {
            if (!searchTerm || !searchTerm.trim()) {
                setFilteredGames([]);
                return;
            }

            const allGroups = [...(gameGroups || []), ...(customGroups || [])];
            const availableGames = [...new Set(
                allGroups
                    .filter(g => g && g.gameName)
                    .map(g => g.gameName)
            )];

            const filtered = availableGames
                .filter(gameName =>
                    gameName &&
                    gameName.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .slice(0, 10);

            setFilteredGames(filtered);
        } catch (error) {
            console.error('过滤游戏时出错:', error);
            setFilteredGames([]);
        }
    };

    // 修复处理游戏输入变化函数，添加更多安全检查
    const handleGameInputChange = (value) => {
        try {
            const safeValue = value || '';
            setGameSearchInput(safeValue);
            setSelectedGameForPackage('');
            setSelectedGroupForPackage('');
            setSelectedPackageForUser('');
            filterGames(safeValue);
        } catch (error) {
            console.error('处理游戏输入变化时出错:', error);
        }
    };

    // 修复选择游戏函数，添加更多安全检查
    const handleGameSelect = (gameName) => {
        try {
            const safeName = gameName || '';
            setGameSearchInput(safeName);
            setSelectedGameForPackage(safeName);
            setFilteredGames([]);
            setGameInputFocused(false);

            // 自动选择默认分组
            if (safeName) {
                const allGroups = [...(gameGroups || []), ...(customGroups || [])];
                const defaultGroup = allGroups.find(group =>
                    group &&
                    group.gameName &&
                    (group.gameName === safeName) &&
                    group.groupType === 'default'
                );

                if (defaultGroup) {
                    setSelectedGroupForPackage(defaultGroup.id);
                } else {
                    setSelectedGroupForPackage('');
                }
            } else {
                setSelectedGroupForPackage('');
            }

            setSelectedPackageForUser('');
        } catch (error) {
            console.error('选择游戏时出错:', error);
        }
    };

    // 修复主播包选择弹窗的关闭处理
    const handleClosePackageSelectionDialog = () => {
        try {
            setPackageSelectionDialogOpen(false);

            // 延迟重置状态，避免在渲染过程中出错
            setTimeout(() => {
                resetPackageSelection();
            }, 150); // 增加延迟时间
        } catch (error) {
            console.error('关闭主播包选择弹窗时出错:', error);
        }
    };

    // 修复主播包选择弹窗组件，添加更多安全检查
    const renderPackageSelectionDialog = () => {
        // 安全地获取数据
        const availablePackages = (() => {
            try {
                return getAvailablePackages();
            } catch (error) {
                console.error('获取可用主播包失败:', error);
                return [];
            }
        })();

        // 获取当前选择的分组信息（用于显示）
        const getCurrentGroup = () => {
            if (!selectedGameForPackage) return null;

            const allGroups = [...(gameGroups || []), ...(customGroups || [])];
            return allGroups.find(group =>
                group &&
                group.gameName &&
                group.gameName === selectedGameForPackage &&
                group.groupType === 'default'
            );
        };

        const currentGroup = getCurrentGroup();

        return (
            <Dialog
                open={packageSelectionDialogOpen}
                onClose={handleClosePackageSelectionDialog}
                className={classes.groupDialog}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle className={classes.dialogTitle}>
                    {getText('addUsersToStreamerPackage')}
                    <IconButton
                        aria-label="close"
                        onClick={handleClosePackageSelectionDialog}
                        style={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent className={classes.dialogContent} style={{ padding: '24px' }}>
                    {isLoadingPackages ? (
                        <Box display="flex" justifyContent="center" alignItems="center" py={8} px={4}>
                            <CircularProgress size={40} style={{ color: '#64b5f6', marginRight: 16 }} />
                            <Typography>{getText('loadingStreamerPackageData')}</Typography>
                        </Box>
                    ) : (
                        <>
                            {/* 选中用户信息 */}
                            <Box mb={3} p={3} style={{ backgroundColor: '#29314a', borderRadius: 8, padding: '8px' }}>
                                <Typography variant="body2" style={{ color: '#64b5f6', marginBottom: 8 }}>
                                    {getText('aboutToAdd', { count: selectedUsers.size }, `即将添加 ${selectedUsers.size} 个用户到主播包：`)}
                                </Typography>
                                <Box display="flex" flexWrap="wrap" gap={1}>
                                    {selectedUsersDetails
                                        .slice(0, 10)
                                        .map(userDetail => (
                                            <Chip
                                                key={userDetail.id}
                                                label={userDetail.username}
                                                size="small"
                                                style={{
                                                    backgroundColor: getStreamerTypeColor(userDetail.type) || '#6A0DAD',
                                                    color: 'white',
                                                    fontSize: '11px'
                                                }}
                                            />
                                        ))}
                                    {selectedUsers.size > 10 && (
                                        <Chip
                                            label={`+${selectedUsers.size - 10} ${getText('more')}`}
                                            size="small"
                                            style={{
                                                backgroundColor: '#757575',
                                                color: 'white',
                                                fontSize: '11px'
                                            }}
                                        />
                                    )}
                                </Box>
                            </Box>

                            {/* 游戏搜索选择 - 优化后的版本 */}
                            <Box mb={3} position="relative">
                                <Typography variant="subtitle1" style={{ color: '#e0e0e0', marginBottom: 8 }}>
                                    {getText('searchAndSelectGameGroup')}
                                </Typography>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    placeholder={getText('enterGameNameToSearch')}
                                    value={gameSearchInput || ''}
                                    onChange={(e) => handleGameInputChange(e.target.value || '')}
                                    onFocus={() => {
                                        setGameInputFocused(true);
                                        if (gameSearchInput) {
                                            filterGames(gameSearchInput);
                                        }
                                    }}
                                    onBlur={() => {
                                        setTimeout(() => setGameInputFocused(false), 200);
                                    }}
                                    style={{
                                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <SearchIcon style={{ color: '#b0b0b0', marginRight: 8 }} />
                                        ),
                                        endAdornment: gameSearchInput && (
                                            <IconButton
                                                size="small"
                                                onClick={() => handleGameInputChange('')}
                                                style={{ color: '#b0b0b0' }}
                                            >
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        ),
                                    }}
                                />

                                {/* 游戏搜索结果下拉列表 */}
                                {gameInputFocused && Array.isArray(filteredGames) && filteredGames.length > 0 && (
                                    <Paper
                                        style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            right: 0,
                                            zIndex: 1000,
                                            backgroundColor: '#232b42',
                                            border: '1px solid rgba(255, 255, 255, 0.15)',
                                            maxHeight: '200px',
                                            overflowY: 'auto',
                                        }}
                                    >
                                        {filteredGames.map((gameName, index) => {
                                            const allGroups = [...(gameGroups || []), ...(customGroups || [])];
                                            const gameGroupCount = allGroups.filter(
                                                group => group && group.gameName === gameName && group.groupType === 'default'
                                            ).length;

                                            return (
                                                <Box
                                                    key={index}
                                                    onClick={() => handleGameSelect(gameName)}
                                                    style={{
                                                        padding: '12px 16px',
                                                        cursor: 'pointer',
                                                        borderBottom: index < filteredGames.length - 1 ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.target.style.backgroundColor = '#29314a';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.target.style.backgroundColor = 'transparent';
                                                    }}
                                                >
                                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                                        <Box display="flex" alignItems="center">
                                                            <GamepadIcon style={{ color: '#64b5f6', marginRight: 8 }} />
                                                            <Typography variant="body2" style={{ color: '#e0e0e0' }}>
                                                                {gameName}
                                                            </Typography>
                                                        </Box>
                                                        <Chip
                                                            size="small"
                                                            label={gameGroupCount > 0 ? '默认分组' : '无分组'}
                                                            style={{
                                                                backgroundColor: gameGroupCount > 0 ? '#4caf50' : '#757575',
                                                                color: 'white',
                                                                fontSize: '10px',
                                                                height: '20px'
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Paper>
                                )}

                                {/* 已选择游戏显示 */}
                                {selectedGameForPackage && (
                                    <Box mt={1} p={1} style={{ backgroundColor: '#1a2035', borderRadius: 4 }}>
                                        <Typography variant="caption" style={{ color: '#64b5f6' }}>
                                            {getText('selectedGame')}
                                        </Typography>
                                        <Box display="flex" alignItems="center" mt={0.5}>
                                            <GamepadIcon style={{ color: '#64b5f6', marginRight: 8, fontSize: '18px' }} />
                                            <Typography variant="body2" style={{ color: '#e0e0e0', fontWeight: 500 }}>
                                                {selectedGameForPackage}
                                            </Typography>
                                            {currentGroup && (
                                                <Chip
                                                    size="small"
                                                    label={getText('defaultGroup', { groupName: currentGroup.groupName || '默认分组' })}
                                                    style={{
                                                        marginLeft: 8,
                                                        backgroundColor: '#4caf50',
                                                        color: 'white',
                                                        fontSize: '10px',
                                                        height: '20px'
                                                    }}
                                                />
                                            )}
                                        </Box>
                                        {availablePackages.length > 0 && (
                                            <Typography variant="caption" style={{ color: '#b0b0b0', display: 'block', marginTop: 4 }}>
                                                {getText('autoSelectedDefaultGroup', { count: availablePackages.length })}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </Box>

                            {/* 主播包选择 - 现在是第2步 */}
                            <Box mb={3}>
                                <Typography variant="subtitle1" style={{ color: '#e0e0e0', marginBottom: 8 }}>
                                    {getText('selectStreamerPackage')}
                                </Typography>
                                <FormControl fullWidth variant="outlined" size="small" disabled={!selectedGameForPackage || !currentGroup}>
                                    <InputLabel style={{ color: '#b0b0b0' }}>
                                        {!selectedGameForPackage ? getText('pleaseSelectGameFirst') :
                                            !currentGroup ? getText('gameHasNoDefaultGroup') :
                                                getText('selectStreamerPackageOption')}
                                    </InputLabel>
                                    <Select
                                        value={selectedPackageForUser || ''}
                                        onChange={(e) => setSelectedPackageForUser(e.target.value || '')}
                                        label={!selectedGameForPackage ? getText('pleaseSelectGameFirst') :
                                            !currentGroup ? getText('gameHasNoDefaultGroup') :
                                                getText('selectStreamerPackageOption')}
                                        style={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                            color: '#e0e0e0'
                                        }}
                                    >
                                        {Array.isArray(availablePackages) && availablePackages.length > 0 ?
                                            availablePackages.map(pkg => (
                                                <MenuItem key={pkg.id} value={pkg.id || ''}>
                                                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                                        <Box>
                                                            <Typography variant="body2" style={{ fontWeight: 500 }}>
                                                                {pkg.name || '未知主播包'}
                                                            </Typography>
                                                            {pkg.description && (
                                                                <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                                    {pkg.description}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        <Box textAlign="right">
                                                            <Typography variant="caption" style={{ color: '#64b5f6' }}>
                                                                {pkg.streamerCount || 0}/{pkg.maxStreamers || 100}
                                                            </Typography>
                                                            <Chip
                                                                size="small"
                                                                label={pkg.type === 'default' ? '默认' : '自定义'}
                                                                style={{
                                                                    marginLeft: 8,
                                                                    backgroundColor: pkg.type === 'default' ? '#4caf50' : '#ff7043',
                                                                    color: 'white',
                                                                    fontSize: '10px',
                                                                    height: '20px'
                                                                }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                </MenuItem>
                                            )) :
                                            <MenuItem disabled>
                                                <Typography variant="body2" style={{ color: '#888' }}>
                                                    {!selectedGameForPackage ? getText('pleaseSelectGameFirst') :
                                                        !currentGroup ? getText('gameHasNoDefaultGroup') :
                                                            getText('selectStreamerPackageOption')}
                                                </Typography>
                                            </MenuItem>
                                        }
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* 选择预览 */}
                            {selectedPackageForUser && (
                                <Box p={2} style={{ backgroundColor: '#1a2035', borderRadius: 8 }}>
                                    <Typography variant="body2" style={{ color: '#64b5f6', marginBottom: 8 }}>
                                        {getText('addPreview')}
                                    </Typography>
                                    {(() => {
                                        const selectedPkg = availablePackages.find(p => p && p.id === selectedPackageForUser);
                                        return selectedPkg ? (
                                            <Typography variant="body2" style={{ color: '#e0e0e0' }}>

                                                {getText('willAddUsersToPackage', { packageName: selectedPkg.name, userCount: selectedCards.size })}
                                                <br />
                                                {getText('gameGroup')}<strong style={{ color: '#4caf50' }}>{currentGroup?.groupName || '默认分组'}</strong>
                                                <br />
                                                {getText('packageCurrently', { current: selectedPkg.streamerCount || 0, total: (selectedPkg.streamerCount || 0) + selectedCards.size, max: selectedPkg.maxStreamers })}
                                                {/* 添加后包内主播数量：{(selectedPkg.streamerCount || 0) + selectedCards.size}/{selectedPkg.maxStreamers || 100}
                                                {((selectedPkg.streamerCount || 0) + selectedCards.size) > (selectedPkg.maxStreamers || 100) && (
                                                    <span style={{ color: '#f44336' }}> (超出限制)</span>
                                                )} */}
                                            </Typography>
                                        ) : null;
                                    })()}
                                </Box>
                            )}

                            {/* 添加操作说明 */}
                            <Box mt={2} p={1} style={{ backgroundColor: '#29314a', borderRadius: 4 }}>
                                <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                                    <InfoIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 8, color: '#64b5f6' }} />
                                    {getText('operationDescription')}
                                </Typography>
                            </Box>
                        </>
                    )}
                </DialogContent>

                <DialogActions className={classes.dialogActions}>
                    <Button
                        onClick={handleClosePackageSelectionDialog}
                        color="default"
                        variant="outlined"
                        disabled={isAddingToPackage}
                    >
                        {getText('cancel')}
                    </Button>
                    <Button
                        onClick={handleAddUsersToPackage}
                        color="primary"
                        variant="contained"
                        disabled={!selectedPackageForUser || isAddingToPackage || isLoadingPackages}
                        style={{
                            backgroundColor: '#6A0DAD',
                            color: 'white'
                        }}
                    >
                        {isAddingToPackage ? getText('adding') : getText('addUsers', { count: selectedCards.size })}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    const [selectedTags, setSelectedTags] = useState([]); // 统一使用数组格式

    const [tagsToUpdate, setTagsToUpdate] = useState([30]); // 默认更新30天的标签
    const [users, setUsers] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedDays, setSelectedDays] = useState(30);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(30);
    const [totalUsers, setTotalUsers] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [statistics, setStatistics] = useState({});
    const [filterApplied, setFilterApplied] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'info',
    });
    // 添加状态管理
    const [syncDialogOpen, setSyncDialogOpen] = useState(false);
    // 在组件顶部的状态定义中添加或修改

    // 游戏标签同步相关状态
    const [gameTagsDialogOpen, setGameTagsDialogOpen] = useState(false);
    const [selectedGameTagRanges, setSelectedGameTagRanges] = useState([30]); // 游戏标签范围，不能为空
    const [updateAllRanges, setUpdateAllRanges] = useState(false); // 是否更新所有范围

    // 修改游戏信息同步相关状态
    const [gameInfoDialogOpen, setGameInfoDialogOpen] = useState(false);
    const [selectedGameInfoRange, setSelectedGameInfoRange] = useState(30); // 游戏信息范围
    const [gamesList, setGamesList] = useState([]); // 游戏列表
    const [selectedGames, setSelectedGames] = useState([]); // 选中的游戏
    const [syncMode, setSyncMode] = useState('notUpdated'); // 同步模式: 'notUpdated', 'all', 'updated'
    const [isGameSyncing, setIsGameSyncing] = useState(false);
    const [gameSearchKeyword, setGameSearchKeyword] = useState(''); // 游戏搜索关键词
    // 主播类型状态
    const [selectedStreamerType, setSelectedStreamerType] = useState('');
    const [gamesPagination, setGamesPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
    });
    const [gamesStatistics, setGamesStatistics] = useState({
        totalGames: 0,
        updatedGames: 0,
        notUpdatedGames: 0,
        updateRate: 0
    });
    const [isLoadingGames, setIsLoadingGames] = useState(false);

    // 添加游戏相关状态
    const [availableGamesList, setAvailableGamesList] = useState([]);
    const [selectedGame, setSelectedGame] = useState('');
    const [isLoadingAvailableGames, setIsLoadingAvailableGames] = useState(false);

    // 新的获取可用游戏列表的方法
    const fetchAvailableGames = async () => {
        setIsLoadingAvailableGames(true);
        try {
            const response = await axios.get('/api/v1/admin/game?filterEmptyNameInTwitch=true');
            if (response.data && response.data.data) {
                const processedGames = response.data.data.map(game => ({
                    ...game,
                    displayName: game.name || game.nameInTwitch, // 优先使用 nameInTwitch
                    id: game.id || game._id
                }));
                setAvailableGamesList(processedGames);
            } else if (response.data && Array.isArray(response.data)) {
                const processedGames = response.data.map(game => ({
                    ...game,
                    displayName: game.name || game.nameInTwitch,
                    id: game.id || game._id
                }));
                setAvailableGamesList(processedGames);
            }
        } catch (error) {
            console.error('获取游戏列表失败:', error);
            setSnackbar({
                open: true,
                message: '获取游戏列表失败: ' + (error.response?.data?.message || error.message),
                severity: 'error',
            });
            setAvailableGamesList([]);
        } finally {
            setIsLoadingAvailableGames(false);
        }
    };

    // 获取所有游戏标签
    useEffect(() => {
        fetchGameTags();
        fetchAvailableGames(); // 使用新的方法名
    }, []);

    // 在现有状态中添加平台用户筛选状态
    const [isNoizUser, setIsNoizUser] = useState(false); // 默认为false，不限制平台用户

    // 处理平台用户筛选变化的函数
    const handleNoizUserChange = (checked) => {
        setIsNoizUser(checked);
        setPage(0); // 重置到第一页
        // 不清除用户选择状态
    };

    const debouncedFetchUsers = useCallback(
        debounce(() => {
            fetchUsers();
        }, 300),
        [selectedDays, page, rowsPerPage, selectedStreamerType, selectedTags, searchQuery, selectedGame, sortBy, isNoizUser]
    );

    // 确保在组件挂载和依赖变化时重新获取数据
    // 修改 useEffect 使用防抖函数
    useEffect(() => {
        debouncedFetchUsers();
    }, [selectedDays, page, rowsPerPage, selectedStreamerType, selectedTags, selectedGame, sortBy, isNoizUser]);

    // 添加编辑相关状态，分别处理类型和标签
    const [isEditModeTypes, setIsEditModeTypes] = useState(false);
    const [isEditModeTags, setIsEditModeTags] = useState(false);
    const [editedTypes, setEditedTypes] = useState([]);
    const [editedTags, setEditedTags] = useState([]);
    const [isSavingTypes, setIsSavingTypes] = useState(false);
    const [isSavingTags, setIsSavingTags] = useState(false);

    // 在现有状态中添加更多菜单相关状态
    const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);

    // 处理更多菜单的打开和关闭
    const handleMoreMenuOpen = (event) => {
        setMoreMenuAnchor(event.currentTarget);
    };

    const handleMoreMenuClose = () => {
        setMoreMenuAnchor(null);
    };

    // 处理菜单项点击，保持原有逻辑
    const handleMenuItemClick = (action) => {
        handleMoreMenuClose(); // 先关闭菜单

        switch (action) {
            case 'syncStreamerRatings':
                handleOpenStreamerRatingsDialog();
                break;
            case 'syncGameTags':
                handleOpenGameTagsDialog();
                break;
            case 'syncGameInfo':
                handleOpenGameInfoDialog();
                break;
            default:
                break;
        }
    };

    // 标签类型配置编辑相关函数
    const handleEditTypes = () => {
        setIsEditModeTypes(true);
        setEditedTypes(JSON.parse(JSON.stringify(tagConfig.types || [])));
    };

    const handleCancelEditTypes = () => {
        setIsEditModeTypes(false);
        setEditedTypes([]);
    };

    const handleSaveTypes = async () => {
        setIsSavingTypes(true);
        try {
            const response = await axios.put('/api/v1/admin/user/edit-activetype', {
                types: editedTypes
            });

            if (response.data && response.data.success) {
                const result = response.data.data;

                // 构建详细的成功消息
                let message = `标签类型配置保存成功！\n`;
                message += `总数: ${result.totalCount}, 成功: ${result.successCount}`;

                if (result.failureCount > 0) {
                    message += `, 失败: ${result.failureCount}`;
                }

                // 如果有成功的结果，显示具体信息
                if (result.successResults && result.successResults.length > 0) {
                    const successNames = result.successResults
                        .filter(item => item.success)
                        .map(item => item.name)
                        .join(', ');

                    if (successNames) {
                        message += `\n成功更新: ${successNames}`;
                    }
                }

                setSnackbar({
                    open: true,
                    message: message,
                    severity: 'success',
                });

                // 更新配置数据 - 使用最新的返回数据
                if (result.successResults) {
                    const updatedTypes = result.successResults
                        .filter(item => item.success)
                        .map(item => ({
                            id: item.id,
                            code: item.code,
                            name: item.name,
                            defaultDays: item.defaultDays || editedTypes.find(t => t.code === item.code)?.defaultDays,
                            description: item.description || editedTypes.find(t => t.code === item.code)?.description
                        }));

                    setTagConfig(prev => ({
                        ...prev,
                        types: updatedTypes
                    }));
                } else {
                    // 如果没有返回详细结果，使用编辑的数据
                    setTagConfig(prev => ({
                        ...prev,
                        types: editedTypes
                    }));
                }

                setIsEditModeTypes(false);
                setEditedTypes([]);
            } else {
                throw new Error(response.data?.message || '保存失败');
            }
        } catch (error) {
            console.error('保存标签类型配置失败:', error);

            let errorMessage = '保存标签类型配置失败: ';
            if (error.response?.data?.data) {
                const errorData = error.response.data.data;
                errorMessage += `总数: ${errorData.totalCount || 0}, 成功: ${errorData.successCount || 0}, 失败: ${errorData.failureCount || 0}`;

                // 如果有失败的具体信息
                if (errorData.failureResults && errorData.failureResults.length > 0) {
                    const failureDetails = errorData.failureResults
                        .map(item => `${item.name}: ${item.error}`)
                        .join('; ');
                    errorMessage += `\n失败详情: ${failureDetails}`;
                }
            } else {
                errorMessage += (error.response?.data?.message || error.message);
            }

            setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error',
            });
        } finally {
            setIsSavingTypes(false);
        }
    };

    const handleTypeConfigChange = (index, field, value) => {
        setEditedTypes(prev =>
            prev.map((type, i) =>
                i === index ? { ...type, [field]: value } : type
            )
        );
    };

    // 标签详细配置编辑相关函数
    const handleEditTags = () => {
        setIsEditModeTags(true);
        setEditedTags(JSON.parse(JSON.stringify(tagConfig.tags || [])));
    };

    const handleCancelEditTags = () => {
        setIsEditModeTags(false);
        setEditedTags([]);
    };

    const handleSaveTags = async () => {
        setIsSavingTags(true);
        try {
            const response = await axios.put('/api/v1/admin/tag/tags', {
                tags: editedTags
            });

            if (response.data && response.data.success) {
                const result = response.data.data;

                // 构建详细的成功消息
                let message = `标签详细配置保存成功！\n`;
                message += `总数: ${result.totalCount}, 成功: ${result.successCount}`;

                if (result.failureCount > 0) {
                    message += `, 失败: ${result.failureCount}`;
                }

                // 如果有成功的结果，显示具体信息
                if (result.successResults && result.successResults.length > 0) {
                    const successNames = result.successResults
                        .filter(item => item.success)
                        .map(item => item.backendLabel || item.uniqueCode)
                        .join(', ');

                    if (successNames) {
                        message += `\n成功更新: ${successNames}`;
                    }
                }

                setSnackbar({
                    open: true,
                    message: message,
                    severity: 'success',
                });

                // 更新配置数据
                if (result.successResults) {
                    const updatedTags = result.successResults
                        .filter(item => item.success)
                        .map(item => {
                            const originalTag = editedTags.find(t => t.uniqueCode === item.uniqueCode);
                            return {
                                ...originalTag,
                                id: item.id,
                                uniqueCode: item.uniqueCode,
                                backendLabel: item.backendLabel || originalTag?.backendLabel,
                                // 保持其他编辑的字段
                            };
                        });

                    setTagConfig(prev => ({
                        ...prev,
                        tags: updatedTags
                    }));
                } else {
                    setTagConfig(prev => ({
                        ...prev,
                        tags: editedTags
                    }));
                }

                setIsEditModeTags(false);
                setEditedTags([]);
            } else {
                throw new Error(response.data?.message || '保存失败');
            }
        } catch (error) {
            console.error('保存标签详细配置失败:', error);

            let errorMessage = '保存标签详细配置失败: ';
            if (error.response?.data?.data) {
                const errorData = error.response.data.data;
                errorMessage += `总数: ${errorData.totalCount || 0}, 成功: ${errorData.successCount || 0}, 失败: ${errorData.failureCount || 0}`;

                if (errorData.failureResults && errorData.failureResults.length > 0) {
                    const failureDetails = errorData.failureResults
                        .map(item => `${item.uniqueCode}: ${item.error}`)
                        .join('; ');
                    errorMessage += `\n失败详情: ${failureDetails}`;
                }
            } else {
                errorMessage += (error.response?.data?.message || error.message);
            }

            setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error',
            });
        } finally {
            setIsSavingTags(false);
        }
    };

    const handleTagConfigChange = (index, field, value) => {
        setEditedTags(prev =>
            prev.map((tag, i) =>
                i === index ? { ...tag, [field]: value } : tag
            )
        );
    };

    const handleTagConfigRangeChange = (index, field, minValue, maxValue) => {
        const rangeValue = formatRange(minValue, maxValue);
        setEditedTags(prev =>
            prev.map((tag, i) =>
                i === index ? { ...tag, [field]: rangeValue } : tag
            )
        );
    };

    // 添加辅助函数来解析和格式化区间值
    const parseRange = (rangeStr) => {
        if (!rangeStr || typeof rangeStr !== 'string') return { min: '', max: '' };
        const parts = rangeStr.split('-');
        return {
            min: parts[0] || '',
            max: parts[1] || ''
        };
    };

    const formatRange = (min, max) => {
        if (!min && !max) return '';
        return `${min || '0'}-${max || '0'}`;
    };

    // 修改按钮点击处理函数
    const handleOpenStreamerRatingsDialog = async () => {
        setIsUpdatingStreamerRatings(true);
        setStatTab(0); // 默认显示同步标签面板
        await fetchTagConfig(); // 自动获取配置数据
    };

    const handleCloseStreamerRatingsDialog = () => {
        setIsUpdatingStreamerRatings(false);
        setConfigDialogOpen(false);
    };

    const handleOpenGameTagsDialog = () => {
        setGameTagsDialogOpen(true);
    };

    const handleCloseGameTagsDialog = () => {
        setGameTagsDialogOpen(false);
    };

    // 修改 handleOpenGameInfoDialog 函数，确保打开弹窗时获取最新数据
    const handleOpenGameInfoDialog = async () => {
        setGameInfoDialogOpen(true);
        // 使用当前的状态值立即获取数据
        await fetchGamesList(1, true, {
            mode: syncMode,
            range: selectedGameInfoRange,
            keyword: gameSearchKeyword
        });
    };

    const handleCloseGameInfoDialog = () => {
        setGameInfoDialogOpen(false);
        setGamesList([]);
        setSelectedGames([]);
        setGameSearchKeyword('');
        setGamesPagination({
            page: 1,
            limit: 20,
            total: 0,
            totalPages: 0,
            hasNext: false,
            hasPrev: false
        });
        setGamesStatistics({
            totalGames: 0,
            updatedGames: 0,
            notUpdatedGames: 0,
            updateRate: 0
        });
    };

    // 在状态定义中添加游戏来源选择
    const [gameSourceMode, setGameSourceMode] = useState(0); // 0: 所有游戏, 1: Noiz游戏, 2: 非Noiz游戏

    // 游戏来源模式映射
    const gameSourceModeMap = {
        0: { label: '所有游戏', description: '从UserGame表获取所有游戏数据', color: '#64b5f6' },
        1: { label: 'Noiz平台游戏', description: '从Game表获取平台认证的游戏', color: '#4caf50' },
        2: { label: '非Noiz平台游戏', description: '排除平台游戏的其他游戏', color: '#ff7043' }
    };

    // 修改 fetchGamesList 函数，支持传入参数覆盖状态值
    const fetchGamesList = async (page = 1, resetSelection = false, overrides = {}) => {
        try {
            setIsLoadingGames(true);

            // 使用传入的覆盖参数或当前状态值
            const currentMode = overrides.mode !== undefined ? overrides.mode : syncMode;
            const currentRange = overrides.range !== undefined ? overrides.range : selectedGameInfoRange;
            const currentKeyword = overrides.keyword !== undefined ? overrides.keyword : gameSearchKeyword;
            const currentGameSource = overrides.gameSource !== undefined ? overrides.gameSource : gameSourceMode;

            // 构建查询参数
            const params = new URLSearchParams({
                days: currentRange.toString(),
                page: page.toString(),
                limit: gamesPagination.limit.toString(),
                type: currentMode,
                isNoiz: currentGameSource.toString() // 新增游戏来源参数
            });

            // 添加搜索关键词
            if (currentKeyword.trim()) {
                params.append('keyword', currentKeyword.trim());
            }

            console.log('fetchGamesList 请求参数:', {
                days: currentRange,
                page: page,
                limit: gamesPagination.limit,
                type: currentMode,
                isNoiz: currentGameSource,
                keyword: currentKeyword
            });

            const response = await axios.get(`/api/admin/game/get-all-game-igdb-info?${params}`);

            if (response.data && response.data.code === 'IGDB_STATISTICS_SUCCESS') {
                const data = response.data.data;

                // 处理游戏数据，确保每个游戏都有必要的字段
                const processedGames = data.games.map(game => ({
                    id: game.igdbId || (game.name || game.nameInTwitch), // 使用igdbId或nameInTwitch作为唯一标识
                    name: game.name, // 优先使用 nameInTwitch
                    nameInTwitch: game.nameInTwitch,
                    originalName: game.name, // 保留原始名称
                    igdbId: game.igdbId || null,
                    isSynced: game.status === 'updated' || !!game.igdbId,
                    lastSyncTime: game.lastSyncFromIGDB || null,
                    genres: game.genres || [],
                    themes: game.themes || [],
                    summary: game.summary || '',
                    releaseDate: game.releaseDate || null
                }));

                setGamesList(processedGames);
                setGamesPagination(data.pagination);
                setGamesStatistics(data.statistics);

                console.log('fetchGamesList 成功，返回数据:', {
                    gamesCount: processedGames.length,
                    pagination: data.pagination,
                    statistics: data.statistics,
                    gameSourceType: data.gameSourceType
                });

                // 如果需要重置选择状态
                if (resetSelection) {
                    setSelectedGames([]);
                }
            } else {
                throw new Error(response.data?.message || '获取游戏列表失败');
            }
        } catch (error) {
            console.error('获取游戏列表失败:', error);
            setSnackbar({
                open: true,
                message: '获取游戏列表失败: ' + (error.response?.data?.message || error.message),
                severity: 'error',
            });

            // 设置默认值
            setGamesList([]);
            setGamesPagination({
                page: 1,
                limit: 20,
                total: 0,
                totalPages: 0,
                hasNext: false,
                hasPrev: false
            });
            setGamesStatistics({
                totalGames: 0,
                updatedGames: 0,
                notUpdatedGames: 0,
                updateRate: 0
            });
        } finally {
            setIsLoadingGames(false);
        }
    };

    // 处理游戏来源模式变化
    const handleGameSourceModeChange = async (newMode) => {
        setGameSourceMode(newMode);
        // 模式变化时重新获取数据
        await fetchGamesList(1, true, { gameSource: newMode });
    };

    // 处理分页变化
    const handleGamesPageChange = (newPage) => {
        if (newPage >= 1 && newPage <= gamesPagination.totalPages) {
            fetchGamesList(newPage);
        }
    };

    // 修改搜索按钮的点击事件处理
    const handleSearchClick = () => {
        setPage(0); // 重置页码为0（对应第1页）
        fetchUsers();
    };

    // 修改 handleGameSearch 函数
    const handleGameSearch = async () => {
        setPage(0); // 重置页码
        await fetchGamesList(1, true, { keyword: gameSearchKeyword }); // 重置到第一页并清空选择
    };

    // 修改搜索框的回车事件处理
    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            setPage(0); // 重置页码
            fetchUsers();
        }
    };

    // 处理同步模式变化
    const handleSyncModeChange = async (newMode) => {
        setSyncMode(newMode);
        // 模式变化时重新获取数据
        await fetchGamesList(1, true, { mode: newMode });
    };

    // 处理时间范围变化
    const handleGameInfoRangeChange = async (newRange) => {
        setSelectedGameInfoRange(newRange);
        // 时间范围变化时重新获取数据
        await fetchGamesList(1, true, { range: newRange });
    };

    // 在组件状态中添加强制更新选项
    const [forceUpdateAll, setForceUpdateAll] = useState(false); // 是否强制更新所有用户

    // 修改 handleSyncStreamerRatings 函数
    const handleSyncStreamerRatings = async () => {
        if (!window.confirm('确定要同步所有用户的主播评价标签吗？这可能需要较长时间。')) {
            return;
        }

        setStreamerRatingsProgress({
            phase: '准备同步...',
            percentage: 0,
            processedCount: 0,
            totalCount: 1
        });

        try {
            const response = await fetch('/api/v1/admin/user/update-all-streamer-ratings', {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache'
                },
                body: JSON.stringify({
                    updateAll: forceUpdateAll
                }),
                cache: 'no-cache'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            if (result.success) {
                setSnackbar({
                    open: true,
                    message: result.message || '主播评价标签同步完成',
                    severity: 'success',
                });

                // 刷新用户列表
                await fetchUsers();
            } else {
                throw new Error(result.message || '同步失败');
            }

        } catch (error) {
            console.error('同步主播评价标签失败:', error);
            setSnackbar({
                open: true,
                message: '同步主播评价标签失败: ' + error.message,
                severity: 'error',
            });
        } finally {
            setStreamerRatingsProgress({
                phase: '',
                percentage: 0,
                processedCount: 0,
                totalCount: 0
            });
        }
    };

    // 修改同步游戏标签函数，去掉 syncGames 参数
    const handleSyncGameTags = async () => {
        if (!updateAllRanges && selectedGameTagRanges.length === 0) {
            setSnackbar({
                open: true,
                message: getText('pleaseSelectAtLeastOneRange'),
                severity: 'warning',
            });
            return;
        }

        setIsSyncing(true);
        try {
            const response = await axios.post('/api/v1/admin/user/sync-users-tags', {
                tagsToUpdate: selectedGameTagRanges,
                updateAll: updateAllRanges
                // 移除 syncGames 参数
            });

            if (response.data && response.data.data && response.data.data.result) {
                const result = response.data.data.result;
                setSnackbar({
                    open: true,
                    message: getText('syncSuccess', {
                        count: result.processedCount,
                        time: result.processingTime
                    }),
                    severity: 'success',
                });

                fetchUsers();
                handleCloseGameTagsDialog();
            }
        } catch (error) {
            console.error('同步游戏标签失败:', error);
            setSnackbar({
                open: true,
                message: getText('syncGameTagsFailed') + ': ' + (error.response?.data?.message || error.message),
                severity: 'error',
            });
        } finally {
            setIsSyncing(false);
        }
    };

    // 添加同步IGDB信息的状态
    const [isIGDBSyncing, setIsIGDBSyncing] = useState(false);
    const [igdbSyncProgress, setIGDBSyncProgress] = useState({
        processed: 0,
        total: 0,
        currentGame: '',
        phase: ''
    });

    // 修改同步游戏信息函数
    // 修改同步游戏信息函数，适配新的接口参数
    const handleSyncGameInfo = async () => {
        let gamesToSync = [];

        // 根据选择状态确定要同步的游戏
        if (selectedGames.length > 0) {
            // 如果有手动选择游戏，使用选择的游戏
            gamesToSync = gamesList
                .filter(game => selectedGames.includes(game.id))
                .map(game => game.name);
        } else {
            // 如果没有手动选择，根据同步模式确定游戏列表
            if (syncMode === 'notUpdated') {
                gamesToSync = gamesList
                    .filter(game => !game.isSynced)
                    .map(game => game.name);
            } else if (syncMode === 'all') {
                gamesToSync = gamesList.map(game => game.name);
            } else if (syncMode === 'updated') {
                gamesToSync = gamesList
                    .filter(game => game.isSynced)
                    .map(game => game.name);
            }
        }

        // 确认强制同步
        let forceSync = false;
        if (syncMode === 'updated' || (syncMode === 'all' && gamesList.some(game => game.isSynced))) {
            const confirmed = window.confirm(
                `即将同步 ${selectedGames.length > 0 ? selectedGames.length : '自动筛选出的'} 个游戏的IGDB信息。\n\n` +
                `检测到部分游戏已有IGDB信息，是否强制重新同步？\n` +
                `选择"确定"将删除现有信息并重新获取（强制同步）\n` +
                `选择"取消"将跳过已有信息的游戏（智能同步）`
            );
            forceSync = confirmed;
        }

        setIsIGDBSyncing(true);

        // 设置初始进度状态
        const totalCount = selectedGames.length > 0 ? selectedGames.length : gamesList.length;
        setIGDBSyncProgress({
            processed: 0,
            total: totalCount,
            currentGame: '',
            phase: '准备同步...'
        });

        try {
            // 构建请求参数 - 适配新的接口结构
            const requestParams = {
                forceSync: forceSync,
                batchSize: 50, // 后端内部批处理大小
                apiDelay: 1500, // API调用间隔
                gameType: gameSourceMode, // 使用游戏来源模式作为gameType
                days: selectedGameInfoRange, // 传递时间范围参数
            };

            // 如果有选择的游戏，传递games数组；否则让后端根据gameType和days自动筛选
            if (selectedGames.length > 0) {
                requestParams.games = gamesToSync;
            }
            // 注意：当没有手动选择游戏时，不传递games参数，让后端根据gameType和days自动筛选

            console.log('同步IGDB信息请求参数:', requestParams);

            const response = await axios.post('/api/admin/game/sync-game-igdb-info', requestParams);

            if (response.data && response.data.code === 'SUCCESS') {
                const responseData = response.data.data;
                const result = response.data.data.result;

                // 构建详细的成功消息
                let message = `游戏IGDB信息同步完成！\n`;
                message += `游戏来源: ${responseData.gameTypeText || '未知来源'}`;
                message += `，时间范围: ${responseData.days || selectedGameInfoRange} 天\n`;
                message += `处理游戏: ${result.actualGameCount || 0} 个`;
                if (result.processingTime) {
                    message += `，用时: ${result.processingTime} 秒`;
                }
                message += `\n新建: ${result.createdCount || 0} 个`;
                if (result.syncedCount > 0) {
                    message += `，已同步: ${result.syncedCount || 0} 个`;
                }
                if (result.existingCount > 0) {
                    message += `，跳过: ${result.existingCount} 个`;
                }
                if (result.failedCount > 0) {
                    message += `，失败: ${result.failedCount} 个`;
                }

                setSnackbar({
                    open: true,
                    message: message,
                    severity: 'success',
                });

                // 刷新游戏列表以显示最新同步状态
                await fetchGamesList(gamesPagination.page);

                // 清空选择
                setSelectedGames([]);

            } else {
                throw new Error(response.data?.message || '同步失败');
            }

        } catch (error) {
            console.error('同步游戏IGDB信息失败:', error);

            let errorMessage = '同步游戏IGDB信息失败: ';
            if (error.response?.data?.data?.result) {
                const errorResult = error.response.data.data.result;
                errorMessage += `处理了 ${errorResult.syncedCount || 0} 个，失败 ${errorResult.failedCount || 0} 个。`;
            } else {
                errorMessage += (error.response?.data?.message || error.message);
            }

            setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error',
            });
        } finally {
            setIsIGDBSyncing(false);
            setIGDBSyncProgress({
                processed: 0,
                total: 0,
                currentGame: '',
                phase: ''
            });
        }
    };

    // 渲染同步主播评价标签弹窗
    const renderStreamerRatingsDialog = () => {
        return (
            <Dialog
                open={isUpdatingStreamerRatings}
                onClose={handleCloseStreamerRatingsDialog}
                fullWidth
                maxWidth="xl"
            >
                <DialogTitle className={classes.dialogTitle}>
                    {getText('streamerRatingSystemManagement')}
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseStreamerRatingsDialog}
                        style={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <Tabs
                    value={statTab}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="fullWidth"
                    className={classes.tabRoot}
                >
                    <Tab label={getText('syncTags')} />
                    <Tab label={getText('systemConfig')} />
                </Tabs>

                <DialogContent className={classes.dialogContent}>
                    {/* 同步标签标签面板 */}
                    {statTab === 0 && (
                        <>
                            <Typography variant="h6" gutterBottom>
                                {getText('syncStreamerRatingTags')}
                            </Typography>
                            <Typography variant="body1" paragraph>
                                {getText('syncAllStreamerRatingDesc')}
                            </Typography>

                            {/* 显示当前配置概览 */}
                            <Paper className={classes.statCard} style={{ marginBottom: 24 }}>
                                <Typography variant="h6" gutterBottom>{getText('currentConfigOverview')}</Typography>
                                <Divider style={{ marginBottom: 16, backgroundColor: 'rgba(255,255,255,0.1)' }} />

                                {isConfigLoading ? (
                                    <Box display="flex" justifyContent="center" alignItems="center" height="100px">
                                        <CircularProgress />
                                    </Box>
                                ) : (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={4}>
                                            <Typography className={classes.statTitle}>{getText('tagTypesCount')}</Typography>
                                            <Typography className={classes.statValue}>
                                                {tagConfig.types?.length || 0} 个
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Typography className={classes.statTitle}>{getText('activeTagsCount')}</Typography>
                                            <Typography className={classes.statValue}>
                                                {tagConfig.tags?.filter(tag => tag.isActive).length || 0} 个
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Typography className={classes.statTitle}>{getText('totalTagsCount')}</Typography>
                                            <Typography className={classes.statValue}>
                                                {tagConfig.tags?.length || 0} 个
                                            </Typography>
                                        </Grid>

                                        {/* 显示标签类型概览 */}
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle1" gutterBottom style={{ marginTop: 16 }}>
                                                {getText('tagTypesOverview')}
                                            </Typography>
                                            <Box display="flex" flexWrap="wrap" gap={1.5}> {/* 从gap={1}增加到gap={1.5} */}
                                                {tagConfig.types?.map(type => (
                                                    <Chip
                                                        key={type.code}
                                                        label={`${type.name} (${type.defaultDays}天)`}
                                                        className={classes.chip}
                                                        size="small"
                                                        style={{
                                                            margin: '4px', // 新增：为每个芯片添加外边距
                                                            padding: '6px 12px', // 新增：增加内边距
                                                            height: '32px', // 新增：统一高度
                                                            fontSize: '0.875rem', // 新增：统一字体大小
                                                            fontWeight: 500 // 新增：增加字重
                                                        }}
                                                    />
                                                )) || (
                                                        <Typography variant="body2" color="textSecondary">
                                                            {getText('noConfigData')}
                                                        </Typography>
                                                    )}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                )}
                            </Paper>

                            {/* 同步选项设置 */}
                            <Box mt={3} mb={3}>
                                <Typography variant="subtitle1" gutterBottom>
                                    {getText('syncOptionSettings')}
                                </Typography>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={forceUpdateAll}
                                            onChange={(e) => setForceUpdateAll(e.target.checked)}
                                            color="primary"
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography variant="body1">{getText('forceUpdateAllUsers')}</Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                {getText('forceUpdateDesc')}
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Box>

                            {/* 同步操作区域 */}
                            <Box display="flex" gap={2} mt={2} alignItems="center" justifyContent="flex-start">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<UpdateIcon />}
                                    onClick={handleSyncStreamerRatings}
                                    disabled={streamerRatingsProgress.totalCount > 0}
                                    style={{
                                        backgroundColor: '#ff7043',
                                        color: 'white',
                                        minWidth: '160px',
                                        height: '40px',
                                        fontWeight: 600,
                                        marginRight: '16px' // 新增：在开始同步按钮和查看配置按钮之间添加间距
                                    }}
                                >
                                    {streamerRatingsProgress.totalCount > 0 ? '同步中...' : getText('startSyncTags')}
                                </Button>

                                <Button
                                    variant="outlined"
                                    startIcon={<RefreshIcon />}
                                    onClick={() => {
                                        setStatTab(1);
                                        fetchTagConfig();
                                    }}
                                    style={{
                                        color: '#64b5f6',
                                        borderColor: '#64b5f6',
                                        minWidth: '140px',
                                        height: '40px'
                                    }}
                                >
                                    {getText('viewDetailedConfig')}
                                </Button>

                                {/* 显示同步进度 */}
                                {streamerRatingsProgress.totalCount > 0 && (
                                    <Box display="flex" alignItems="center" ml={3} p={2}
                                        style={{
                                            backgroundColor: '#1a2035',
                                            borderRadius: 8,
                                            border: '1px solid rgba(255, 112, 67, 0.3)'
                                        }}>
                                        <CircularProgress
                                            size={24}
                                            style={{ marginRight: 12, color: '#ff7043' }}
                                        />
                                        <Typography variant="body2" style={{ color: '#ff7043', fontWeight: 500 }}>
                                            {streamerRatingsProgress.phase}
                                            {streamerRatingsProgress.processedCount > 0 && (
                                                <span style={{ marginLeft: 8, color: '#b0b0b0' }}>
                                                    ({streamerRatingsProgress.processedCount}/{streamerRatingsProgress.totalCount})
                                                </span>
                                            )}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>

                            {/* 同步状态提示 */}
                            <Box mt={3} mb={1} p={2} style={{
                                backgroundColor: '#1a2035',
                                borderRadius: 8,
                                border: '1px solid rgba(100, 181, 246, 0.2)'
                            }}>
                                <Typography variant="body2" style={{ color: '#64b5f6', marginBottom: 8 }}>
                                    <InfoIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 8 }} />
                                    {getText('syncProcessNote')}
                                </Typography>
                                <Typography variant="body2" style={{ color: '#e0e0e0', lineHeight: 1.6 }}>
                                    • 此功能将更新所有用户的主播评价标签（活跃度、执行力、性价比）<br />
                                    • {forceUpdateAll
                                        ? '强制更新模式：将重新计算所有用户的评价标签'
                                        : '智能更新模式：仅更新尚未评价或评价过期的用户'
                                    }<br />
                                    • 同步时间取决于用户数量，请耐心等待完成
                                </Typography>
                            </Box>

                            {/* 进度条显示 */}
                            {streamerRatingsProgress.totalCount > 0 && (
                                <Box mt={2}>
                                    <LinearProgress
                                        variant="determinate"
                                        value={streamerRatingsProgress.percentage}
                                        style={{
                                            height: 8,
                                            borderRadius: 4,
                                            backgroundColor: 'rgba(255, 112, 67, 0.2)'
                                        }}
                                        sx={{
                                            '& .MuiLinearProgress-bar': {
                                                backgroundColor: '#ff7043'
                                            }
                                        }}
                                    />
                                    <Typography variant="caption" style={{ color: '#b0b0b0', marginTop: 4, display: 'block' }}>
                                        进度: {streamerRatingsProgress.percentage.toFixed(1)}%
                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}

                    {statTab === 1 && (
                        <>
                            {isConfigLoading ? (
                                <Box display="flex" justifyContent="center" alignItems="center" height="300px">
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <>
                                    {/* 标签类型配置部分 */}
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                        <Typography variant="h6">{getText('tagTypeConfig')}</Typography>
                                        <Box display="flex" style={{ gap: '16px' }}>
                                            {isEditModeTypes ? (
                                                <>
                                                    <Button
                                                        variant="outlined"
                                                        onClick={handleCancelEditTypes}
                                                        disabled={isSavingTypes}
                                                    >
                                                        {getText('cancel')}
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={handleSaveTypes}
                                                        disabled={isSavingTypes}
                                                        startIcon={isSavingTypes ? <CircularProgress size={20} /> : <SettingsIcon />}
                                                    >
                                                        {isSavingTypes ? getText('saving') : getText('saveTypeConfig')}
                                                    </Button>
                                                </>
                                            ) : (
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    startIcon={<SettingsIcon />}
                                                    onClick={handleEditTypes}
                                                >
                                                    {getText('editTypeConfig')}
                                                </Button>
                                            )}
                                        </Box>
                                    </Box>

                                    {/* 标签类型表格 */}
                                    <TableContainer component={Paper} className={classes.configTableContainer}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell className={classes.tableHeaderCell}>{getText('typeCode')}</TableCell>
                                                    <TableCell className={classes.tableHeaderCell}>{getText('typeName')}</TableCell>
                                                    <TableCell className={classes.tableHeaderCell}>{getText('defaultStatDays')}</TableCell>
                                                    <TableCell className={classes.tableHeaderCell}>{getText('description')}</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {(isEditModeTypes ? editedTypes : tagConfig.types)?.length > 0 ? (
                                                    (isEditModeTypes ? editedTypes : tagConfig.types).map((type, index) => (
                                                        <TableRow key={type.code} className={classes.tableRow}>
                                                            <TableCell>{type.code}</TableCell>
                                                            <TableCell>{type.name}</TableCell>
                                                            <TableCell>
                                                                {isEditModeTypes ? (
                                                                    <TextField
                                                                        type="number"
                                                                        value={type.defaultDays}
                                                                        onChange={(e) => handleTypeConfigChange(index, 'defaultDays', parseInt(e.target.value))}
                                                                        size="small"
                                                                        style={{ width: '80px' }}
                                                                        inputProps={{ min: 1, max: 365 }}
                                                                    />
                                                                ) : (
                                                                    type.defaultDays
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                {isEditModeTypes ? (
                                                                    <TextField
                                                                        value={type.description}
                                                                        onChange={(e) => handleTypeConfigChange(index, 'description', e.target.value)}
                                                                        size="small"
                                                                        multiline
                                                                        maxRows={3}
                                                                        style={{ width: '200px' }}
                                                                    />
                                                                ) : (
                                                                    type.description
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={4} align="center">{getText('noTagTypeData')}</TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>

                                    {/* 标签详细配置部分 */}
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} mt={4}>
                                        <Typography variant="h6">{getText('tagDetailedConfig')}</Typography>
                                        <Box display="flex" style={{ gap: '16px' }}>
                                            {isEditModeTags ? (
                                                <>
                                                    <Button
                                                        variant="outlined"
                                                        onClick={handleCancelEditTags}
                                                        disabled={isSavingTags}
                                                    >
                                                        {getText('cancel')}
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={handleSaveTags}
                                                        disabled={isSavingTags}
                                                        startIcon={isSavingTags ? <CircularProgress size={20} /> : <SettingsIcon />}
                                                    >
                                                        {isSavingTags ? getText('saving') : getText('saveTagConfig')}
                                                    </Button>
                                                </>
                                            ) : (
                                                <>
                                                    <Button
                                                        variant="outlined"
                                                        color="primary"
                                                        startIcon={<SettingsIcon />}
                                                        onClick={handleEditTags}
                                                    >
                                                        {getText('editTagConfig')}
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        color="secondary"
                                                        onClick={initializeTagSystem}
                                                        disabled={isInitializing}
                                                        startIcon={isInitializing ? <CircularProgress size={20} /> : <RefreshIcon />}
                                                    >
                                                        {isInitializing ? getText('initializing') : getText('initializeSystem')}
                                                    </Button>
                                                </>
                                            )}
                                        </Box>
                                    </Box>

                                    {/* 标签配置表格 */}
                                    <TableContainer component={Paper} className={classes.configTableContainer}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell className={classes.tableHeaderCell}>{getText('tagCode')}</TableCell>
                                                    <TableCell className={classes.tableHeaderCell}>{getText('tagName')}</TableCell>
                                                    <TableCell className={classes.tableHeaderCell}>{getText('category')}</TableCell>
                                                    <TableCell className={classes.tableHeaderCell}>{getText('registrationTime')}</TableCell>
                                                    <TableCell className={classes.tableHeaderCell}>{getText('recentApplicationDays')}</TableCell>
                                                    <TableCell className={classes.tableHeaderCell}>{getText('applicationRatio')}</TableCell>
                                                    <TableCell className={classes.tableHeaderCell}>{getText('recentCompletionDays')}</TableCell>
                                                    <TableCell className={classes.tableHeaderCell}>{getText('completionRatio')}</TableCell>
                                                    <TableCell className={classes.tableHeaderCell}>{getText('completionRateRatio')}</TableCell>
                                                    <TableCell className={classes.tableHeaderCell}>{getText('rewardRatio')}</TableCell>
                                                    <TableCell className={classes.tableHeaderCell}>{getText('priority')}</TableCell>
                                                    <TableCell className={classes.tableHeaderCell}>{getText('status')}</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {(isEditModeTags ? editedTags : tagConfig.tags)?.length > 0 ? (
                                                    (isEditModeTags ? editedTags : tagConfig.tags).map((tag, index) => (
                                                        <TableRow key={tag.uniqueCode} className={classes.tableRow}>
                                                            <TableCell>{tag.uniqueCode}</TableCell>
                                                            <TableCell>
                                                                {isEditModeTags ? (
                                                                    <TextField
                                                                        value={tag.backendLabel}
                                                                        onChange={(e) => handleTagConfigChange(index, 'backendLabel', e.target.value)}
                                                                        size="small"
                                                                        style={{ width: '120px' }}
                                                                    />
                                                                ) : (
                                                                    tag.backendLabel
                                                                )}
                                                            </TableCell>
                                                            <TableCell>{tag.category}</TableCell>
                                                            <TableCell>
                                                                {isEditModeTags ? (
                                                                    <TextField
                                                                        value={tag.registrationTime}
                                                                        onChange={(e) => handleTagConfigChange(index, 'registrationTime', e.target.value)}
                                                                        size="small"
                                                                        style={{ width: '100px' }}
                                                                        placeholder="例: 110-150"
                                                                    />
                                                                ) : (
                                                                    tag.registrationTime
                                                                )}
                                                            </TableCell>
                                                            <TableCell>{tag.recentApplicationDays}</TableCell>
                                                            <TableCell>
                                                                {isEditModeTags && (tag.uniqueCode.includes('110') || tag.uniqueCode.includes('120') || tag.uniqueCode.includes('130') || tag.uniqueCode.includes('150')) ? (
                                                                    <Box display="flex" alignItems="center" gap={0.5}>
                                                                        <TextField
                                                                            value={parseRange(tag.applicationRatioRange).min}
                                                                            onChange={(e) => {
                                                                                const { max } = parseRange(tag.applicationRatioRange);
                                                                                handleTagConfigRangeChange(index, 'applicationRatioRange', e.target.value, max);
                                                                            }}
                                                                            size="small"
                                                                            style={{ width: '45px' }}
                                                                            placeholder="0.1"
                                                                            type="number"
                                                                            inputProps={{ step: 0.1, min: 0, max: 1 }}
                                                                        />
                                                                        <Typography variant="body2" style={{ color: '#b0b0b0' }}>-</Typography>
                                                                        <TextField
                                                                            value={parseRange(tag.applicationRatioRange).max}
                                                                            onChange={(e) => {
                                                                                const { min } = parseRange(tag.applicationRatioRange);
                                                                                handleTagConfigRangeChange(index, 'applicationRatioRange', min, e.target.value);
                                                                            }}
                                                                            size="small"
                                                                            style={{ width: '45px' }}
                                                                            placeholder="0.3"
                                                                            type="number"
                                                                            inputProps={{ step: 0.1, min: 0, max: 1 }}
                                                                        />
                                                                    </Box>
                                                                ) : (
                                                                    tag.applicationRatioRange
                                                                )}
                                                            </TableCell>
                                                            <TableCell>{tag.recentCompletionDays}</TableCell>
                                                            <TableCell>
                                                                {isEditModeTags && (tag.uniqueCode.includes('210') || tag.uniqueCode.includes('250')) ? (
                                                                    <Box display="flex" alignItems="center" gap={0.5}>
                                                                        <TextField
                                                                            value={parseRange(tag.completionRatioRange).min}
                                                                            onChange={(e) => {
                                                                                const { max } = parseRange(tag.completionRatioRange);
                                                                                handleTagConfigRangeChange(index, 'completionRatioRange', e.target.value, max);
                                                                            }}
                                                                            size="small"
                                                                            style={{ width: '45px' }}
                                                                            placeholder="0.2"
                                                                            type="number"
                                                                            inputProps={{ step: 0.1, min: 0, max: 1 }}
                                                                        />
                                                                        <Typography variant="body2" style={{ color: '#b0b0b0' }}>-</Typography>
                                                                        <TextField
                                                                            value={parseRange(tag.completionRatioRange).max}
                                                                            onChange={(e) => {
                                                                                const { min } = parseRange(tag.completionRatioRange);
                                                                                handleTagConfigRangeChange(index, 'completionRatioRange', min, e.target.value);
                                                                            }}
                                                                            size="small"
                                                                            style={{ width: '45px' }}
                                                                            placeholder="0.4"
                                                                            type="number"
                                                                            inputProps={{ step: 0.1, min: 0, max: 1 }}
                                                                        />
                                                                    </Box>
                                                                ) : (
                                                                    tag.completionRatioRange
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                {isEditModeTags && (tag.uniqueCode.includes('210') || tag.uniqueCode.includes('250')) ? (
                                                                    <Box display="flex" alignItems="center" gap={0.5}>
                                                                        <TextField
                                                                            value={parseRange(tag.completionRateRatioRange).min}
                                                                            onChange={(e) => {
                                                                                const { max } = parseRange(tag.completionRateRatioRange);
                                                                                handleTagConfigRangeChange(index, 'completionRateRatioRange', e.target.value, max);
                                                                            }}
                                                                            size="small"
                                                                            style={{ width: '45px' }}
                                                                            placeholder="0.8"
                                                                            type="number"
                                                                            inputProps={{ step: 0.1, min: 0, max: 1 }}
                                                                        />
                                                                        <Typography variant="body2" style={{ color: '#b0b0b0' }}>-</Typography>
                                                                        <TextField
                                                                            value={parseRange(tag.completionRateRatioRange).max}
                                                                            onChange={(e) => {
                                                                                const { min } = parseRange(tag.completionRateRatioRange);
                                                                                handleTagConfigRangeChange(index, 'completionRateRatioRange', min, e.target.value);
                                                                            }}
                                                                            size="small"
                                                                            style={{ width: '45px' }}
                                                                            placeholder="1.0"
                                                                            type="number"
                                                                            inputProps={{ step: 0.1, min: 0, max: 1 }}
                                                                        />
                                                                    </Box>
                                                                ) : (
                                                                    tag.completionRateRatioRange
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                {isEditModeTags && (tag.uniqueCode.includes('310') || tag.uniqueCode.includes('350')) ? (
                                                                    <Box display="flex" alignItems="center" gap={0.5}>
                                                                        <TextField
                                                                            value={parseRange(tag.rewardRatioRange).min}
                                                                            onChange={(e) => {
                                                                                const { max } = parseRange(tag.rewardRatioRange);
                                                                                handleTagConfigRangeChange(index, 'rewardRatioRange', e.target.value, max);
                                                                            }}
                                                                            size="small"
                                                                            style={{ width: '45px' }}
                                                                            placeholder="0.5"
                                                                            type="number"
                                                                            inputProps={{ step: 0.1, min: 0, max: 1 }}
                                                                        />
                                                                        <Typography variant="body2" style={{ color: '#b0b0b0' }}>-</Typography>
                                                                        <TextField
                                                                            value={parseRange(tag.rewardRatioRange).max}
                                                                            onChange={(e) => {
                                                                                const { min } = parseRange(tag.rewardRatioRange);
                                                                                handleTagConfigRangeChange(index, 'rewardRatioRange', min, e.target.value);
                                                                            }}
                                                                            size="small"
                                                                            style={{ width: '45px' }}
                                                                            placeholder="0.7"
                                                                            type="number"
                                                                            inputProps={{ step: 0.1, min: 0, max: 1 }}
                                                                        />
                                                                    </Box>
                                                                ) : (
                                                                    tag.rewardRatioRange
                                                                )}
                                                            </TableCell>
                                                            <TableCell>{tag.priority}</TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={tag.isActive ? getText('enabled') : getText('disabled')}
                                                                    size="small"
                                                                    color={tag.isActive ? "primary" : "default"}
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={12} align="center">{getText('noTagConfigData')}</TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>

                                    {/* 编辑模式下的提示信息 */}
                                    {(isEditModeTypes || isEditModeTags) && (
                                        <Box mt={2} p={2} style={{ backgroundColor: '#29314a', borderRadius: 4 }}>
                                            <Typography variant="body2" style={{ color: '#64b5f6', marginBottom: 8 }}>
                                                <InfoIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 8 }} />
                                                {getText('editInstructions')}
                                            </Typography>
                                            <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                                                {getText('editInstructionsDesc')}
                                            </Typography>
                                        </Box>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </DialogContent>

                <DialogActions className={classes.dialogActions}>
                    <Button
                        onClick={handleCloseStreamerRatingsDialog}
                        color="default"
                        variant="outlined"
                        disabled={streamerRatingsProgress.totalCount > 0}
                    >
                        {streamerRatingsProgress.totalCount > 0 ? getText('syncing') : getText('close')}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    // 修改渲染游戏标签同步弹窗，移除同步最新数据选项
    const renderGameTagsDialog = () => {
        return (
            <Dialog
                open={gameTagsDialogOpen}
                onClose={handleCloseGameTagsDialog}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle className={classes.dialogTitle}>
                    {getText('syncGameTagsTitle')}
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseGameTagsDialog}
                        style={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    <Typography variant="body1" paragraph>
                        {getText('syncGameTagsDesc')}
                    </Typography>

                    {/* 更新所有范围选项 */}
                    <Box mt={2}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={updateAllRanges}
                                    onChange={(e) => {
                                        setUpdateAllRanges(e.target.checked);
                                        // 如果勾选了更新所有范围，清空具体选择
                                        if (e.target.checked) {
                                            setSelectedGameTagRanges([]);
                                        } else {
                                            // 如果取消勾选，默认选择30天
                                            setSelectedGameTagRanges([30]);
                                        }
                                    }}
                                    color="primary"
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="body1">{getText('updateAllTimeRanges')}</Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {getText('updateAllTimeRangesDesc')}
                                    </Typography>
                                </Box>
                            }
                        />
                    </Box>

                    {/* 游戏标签范围选择 */}
                    <Box mt={3}>
                        <Typography variant="subtitle1" style={{ marginBottom: 8 }}>
                            {getText('selectGameTagRanges')}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                            {updateAllRanges ? getText('allRangesSelected') : getText('pleaseSelectAtLeastOne')}
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1.5}>
                            {[30, 60, 90, 120, 365].map(days => (
                                <Chip
                                    key={`game-tags-${days}`}
                                    label={getText(`${days}days`)}
                                    clickable
                                    disabled={updateAllRanges} // 如果选择了更新所有范围，禁用具体选择
                                    color={!updateAllRanges && selectedGameTagRanges.includes(days) ? "primary" : "default"}
                                    onClick={() => {
                                        if (!updateAllRanges) {
                                            if (selectedGameTagRanges.includes(days)) {
                                                // 如果只剩一个选项，不允许取消
                                                if (selectedGameTagRanges.length > 1) {
                                                    setSelectedGameTagRanges(prev => prev.filter(d => d !== days));
                                                } else {
                                                    setSnackbar({
                                                        open: true,
                                                        message: getText('atLeastOneRangeRequired'),
                                                        severity: 'warning',
                                                    });
                                                }
                                            } else {
                                                setSelectedGameTagRanges(prev => [...prev, days]);
                                            }
                                        }
                                    }}
                                    variant={!updateAllRanges && selectedGameTagRanges.includes(days) ? "default" : "outlined"}
                                    style={{
                                        backgroundColor: updateAllRanges ? '#555' :
                                            (selectedGameTagRanges.includes(days) ? '#4dabf5' : 'transparent'),
                                        color: updateAllRanges ? '#999' :
                                            (selectedGameTagRanges.includes(days) ? 'white' : '#b0b0b0'),
                                        opacity: updateAllRanges ? 0.6 : 1,
                                        margin: '2px', // 新增：为每个芯片添加外边距
                                        padding: '4px 8px', // 新增：增加内边距
                                        height: '32px', // 统一高度
                                        fontSize: '0.875rem' // 统一字体大小
                                    }}
                                />
                            ))}
                        </Box>

                        {/* 显示当前选择状态 */}
                        <Box mt={2} p={1} style={{ backgroundColor: '#29314a', borderRadius: 4 }}>
                            <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                                <InfoIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 8, color: '#64b5f6' }} />
                                {updateAllRanges ?
                                    getText('willUpdateAllRanges') :
                                    getText('selectedRanges', {
                                        count: selectedGameTagRanges.length,
                                        ranges: selectedGameTagRanges.sort((a, b) => a - b).join('、')
                                    })
                                }
                            </Typography>
                        </Box>
                    </Box>

                    {/* 移除"是否同步最新数据"部分 */}

                    {/* 优化操作提示 */}
                    <Box mt={3} mb={1} style={{ backgroundColor: '#1a2035', borderRadius: 4, padding: 12 }}>
                        <Typography variant="body2" style={{ color: '#64b5f6', marginBottom: 8, fontWeight: 600 }}>
                            <InfoIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 8 }} />
                            {getText('syncProcessNote')}
                        </Typography>
                        <Typography variant="body2" style={{ color: '#b0b0b0', lineHeight: 1.5 }}>
                            • {getText('syncCurrentDataDesc')}<br />
                            • {getText('syncProcessTimeDesc')}<br />
                            {updateAllRanges && `• ${getText('updateAllRangesTakeLonger')}`}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions className={classes.dialogActions}>
                    <Button onClick={handleCloseGameTagsDialog} color="default" variant="outlined">
                        {getText('cancel')}
                    </Button>
                    <Button
                        onClick={handleSyncGameTags}
                        color="primary"
                        variant="contained"
                        disabled={isSyncing || (!updateAllRanges && selectedGameTagRanges.length === 0)}
                        startIcon={isSyncing ? <CircularProgress size={20} /> : <SyncIcon />}
                        style={{
                            backgroundColor: '#1565c0',
                            color: 'white'
                        }}
                    >
                        {isSyncing ? getText('syncing') : getText('startSync')}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    // 修改渲染游戏信息同步弹窗
    const renderGameInfoDialog = () => {
        return (
            <Dialog
                open={gameInfoDialogOpen}
                onClose={handleCloseGameInfoDialog}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle className={classes.dialogTitle}>
                    {getText('gameIGDBInfoSync')}
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseGameInfoDialog}
                        style={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent className={classes.dialogContent}>
                    {/* 配置区域 - 统一在一个卡片中 */}
                    <Paper
                        style={{
                            padding: 20,
                            marginBottom: 20,
                            backgroundColor: '#29314a',
                            border: '1px solid rgba(255, 255, 255, 0.1)'
                        }}
                    >
                        <Typography variant="h6" gutterBottom style={{ color: '#e0e0e0', marginBottom: 16 }}>
                            {getText('syncConfig')}
                        </Typography>

                        <Grid container spacing={3}>
                            {/* 游戏来源选择 */}
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" style={{ color: '#b0b0b0', marginBottom: 8 }}>
                                    {getText('selectGameSource')}
                                </Typography>
                                <Box display="flex" gap={1.5}>
                                    {Object.entries(gameSourceModeMap).map(([mode, config]) => (
                                        <Chip
                                            key={mode}
                                            label={config.label}
                                            clickable
                                            onClick={() => handleGameSourceModeChange(parseInt(mode))}
                                            style={{
                                                backgroundColor: gameSourceMode === parseInt(mode) ? config.color : 'transparent',
                                                color: gameSourceMode === parseInt(mode) ? 'white' : config.color,
                                                border: `1px solid ${config.color}`,
                                                fontWeight: gameSourceMode === parseInt(mode) ? 600 : 400,
                                                fontSize: '13px',
                                                height: '32px',
                                                margin: '2px', // 新增：为每个芯片添加外边距
                                                padding: '4px 12px', // 新增：增加内边距
                                                minWidth: '120px', // 新增：统一最小宽度
                                                textAlign: 'center' // 新增：文字居中
                                            }}
                                            icon={gameSourceMode === parseInt(mode) ? <CheckIcon style={{ fontSize: 16 }} /> : null}
                                        />
                                    ))}
                                </Box>
                                <Typography variant="caption" style={{ color: '#888', display: 'block', marginTop: 4 }}>
                                    {gameSourceModeMap[gameSourceMode].description}
                                </Typography>
                            </Grid>

                            {/* 时间范围和同步模式 */}
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel style={{ color: '#b0b0b0' }}>{getText('statDays')}</InputLabel>
                                    <Select
                                        value={selectedGameInfoRange}
                                        onChange={(e) => handleGameInfoRangeChange(e.target.value)}
                                        label={getText('statDays')}
                                        style={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                            color: '#e0e0e0'
                                        }}
                                    >
                                        {[30, 60, 90, 120, 365].map(days => (
                                            <MenuItem key={days} value={days}>{getText(`${days}days`)}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel style={{ color: '#b0b0b0' }}>{getText('syncMode')}</InputLabel>
                                    <Select
                                        value={syncMode}
                                        onChange={(e) => handleSyncModeChange(e.target.value)}
                                        label={getText('syncMode')}
                                        style={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                            color: '#e0e0e0'
                                        }}
                                    >
                                        <MenuItem value="notUpdated">{getText('smartSync')}</MenuItem>
                                        <MenuItem value="updated">{getText('forceSync')}</MenuItem>
                                        <MenuItem value="all">{getText('fullSync')}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={gameSearchKeyword}
                                    onChange={(e) => setGameSearchKeyword(e.target.value)}
                                    placeholder={getText('searchGames')}
                                    variant="outlined"
                                    InputProps={{
                                        startAdornment: <SearchIcon style={{ color: '#b0b0b0', marginRight: 8 }} />,
                                        endAdornment: gameSearchKeyword && (
                                            <IconButton size="small" onClick={() => setGameSearchKeyword('')}>
                                                <ClearIcon />
                                            </IconButton>
                                        )
                                    }}
                                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleGameSearch();
                                        }
                                    }}
                                />
                            </Grid>

                            {/* 搜索按钮 */}
                            <Grid item xs={12}>
                                <Button
                                    variant="outlined"
                                    startIcon={<SearchIcon />}
                                    onClick={handleGameSearch}
                                    disabled={isLoadingGames}
                                    style={{
                                        color: '#64b5f6',
                                        borderColor: '#64b5f6'
                                    }}
                                >
                                    {getText('searchGame')}
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* 统计信息区域 */}
                    <Paper
                        style={{
                            padding: 16,
                            marginBottom: 20,
                            backgroundColor: '#1a2035',
                            border: '1px solid rgba(100, 181, 246, 0.2)'
                        }}
                    >
                        <Typography variant="h6" gutterBottom style={{ color: '#64b5f6' }}>
                            {getText('statsOverview')}
                        </Typography>

                        {isLoadingGames ? (
                            <Box display="flex" justifyContent="center" alignItems="center" py={3}>
                                <CircularProgress size={24} style={{ marginRight: 12 }} />
                                <Typography>{getText('loadingData')}</Typography>
                            </Box>
                        ) : (
                            <>
                                {/* 统计数字 */}
                                <Grid container spacing={2} style={{ marginBottom: 16 }}>
                                    <Grid item xs={3}>
                                        <Box textAlign="center">
                                            <Typography variant="h4" style={{ color: '#e0e0e0', fontWeight: 'bold' }}>
                                                {gamesStatistics.totalGames.toLocaleString()}
                                            </Typography>
                                            <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                {getText('totalGamesCount')}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Box textAlign="center">
                                            <Typography variant="h4" style={{ color: '#4caf50', fontWeight: 'bold' }}>
                                                {gamesStatistics.updatedGames.toLocaleString()}
                                            </Typography>
                                            <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                {getText('synced')}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Box textAlign="center">
                                            <Typography variant="h4" style={{ color: '#f44336', fontWeight: 'bold' }}>
                                                {gamesStatistics.notUpdatedGames.toLocaleString()}
                                            </Typography>
                                            <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                {getText('unsynced')}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Box textAlign="center">
                                            <Typography variant="h4" style={{ color: '#64b5f6', fontWeight: 'bold' }}>
                                                {gamesStatistics.updateRate}%
                                            </Typography>
                                            <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                {getText('syncRate')}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>

                                {/* 进度条 */}
                                <LinearProgress
                                    variant="determinate"
                                    value={parseFloat(gamesStatistics.updateRate)}
                                    style={{
                                        height: 8,
                                        borderRadius: 4,
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                    }}
                                />
                            </>
                        )}
                    </Paper>

                    {/* 游戏选择区域 */}
                    <Paper
                        style={{
                            padding: 16,
                            backgroundColor: '#29314a',
                            border: '1px solid rgba(255, 255, 255, 0.1)'
                        }}
                    >
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h6" style={{ color: '#e0e0e0' }}>
                                {getText('gameList')}
                            </Typography>

                            <Box display="flex" alignItems="center" gap={2}>
                                {gamesList.length > 0 && (
                                    <Button
                                        size="small"
                                        onClick={() => {
                                            if (selectedGames.length === gamesList.length) {
                                                setSelectedGames([]);
                                            } else {
                                                setSelectedGames(gamesList.map(g => g.id));
                                            }
                                        }}
                                        style={{ color: '#64b5f6' }}
                                    >
                                        {selectedGames.length === gamesList.length ? getText('deselectAll') : getText('selectAll')}
                                    </Button>
                                )}

                                <Chip
                                    size="small"

                                    style={{
                                        backgroundColor: selectedGames.length > 0 ? '#64b5f6' : 'rgba(255, 255, 255, 0.1)',
                                        color: 'white'
                                    }}
                                />
                            </Box>
                        </Box>

                        {gamesList.length > 0 ? (
                            <>
                                {/* 游戏列表 */}
                                <Box
                                    style={{
                                        maxHeight: 300,
                                        overflow: 'auto',
                                        backgroundColor: 'rgba(0, 0, 0, 0.2)',
                                        borderRadius: 4,
                                        border: '1px solid rgba(255, 255, 255, 0.1)'
                                    }}
                                >
                                    <List dense>
                                        {gamesList.map((game, index) => (
                                            <ListItem
                                                key={game.id}
                                                button
                                                onClick={() => {
                                                    const newSelected = [...selectedGames];
                                                    const gameIndex = newSelected.indexOf(game.id);
                                                    if (gameIndex === -1) {
                                                        newSelected.push(game.id);
                                                    } else {
                                                        newSelected.splice(gameIndex, 1);
                                                    }
                                                    setSelectedGames(newSelected);
                                                }}
                                                style={{
                                                    borderBottom: index < gamesList.length - 1 ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                                                    backgroundColor: selectedGames.includes(game.id) ? 'rgba(100, 181, 246, 0.1)' : 'transparent'
                                                }}
                                            >
                                                <ListItemIcon>
                                                    <Checkbox
                                                        checked={selectedGames.includes(game.id)}
                                                        style={{ color: '#64b5f6' }}
                                                    />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={
                                                        <Box display="flex" alignItems="center" justifyContent="space-between">
                                                            <Typography variant="body2" style={{ color: '#e0e0e0' }}>
                                                                {game.name}
                                                            </Typography>
                                                            <Box display="flex" gap={1}>
                                                                <Chip
                                                                    size="small"
                                                                    label={game.isSynced ? getText('synced') : getText('unsynced')}
                                                                    style={{
                                                                        backgroundColor: game.isSynced ? '#4caf50' : '#f44336',
                                                                        color: 'white',
                                                                        fontSize: '10px',
                                                                        height: '20px'
                                                                    }}
                                                                />
                                                                {game.genres?.length > 0 && (
                                                                    <Chip
                                                                        size="small"
                                                                        label={`${game.genres.length + (game.themes?.length || 0)}标签`}
                                                                        style={{
                                                                            backgroundColor: '#64b5f6',
                                                                            color: 'white',
                                                                            fontSize: '10px',
                                                                            height: '20px'
                                                                        }}
                                                                    />
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    }
                                                    secondary={
                                                        game.lastSyncTime && (
                                                            <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                                最后同步: {new Date(game.lastSyncTime).toLocaleString()}
                                                            </Typography>
                                                        )
                                                    }
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>

                                {/* 分页控制 */}
                                <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                                    <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                        第 {gamesPagination.page} 页 / 共 {gamesPagination.totalPages} 页
                                    </Typography>

                                    <Box display="flex" gap={1}>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleGamesPageChange(gamesPagination.page - 1)}
                                            disabled={!gamesPagination.hasPrev}
                                            style={{ color: '#64b5f6' }}
                                        >
                                            <ExpandLessIcon style={{ transform: 'rotate(-90deg)' }} />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleGamesPageChange(gamesPagination.page + 1)}
                                            disabled={!gamesPagination.hasNext}
                                            style={{ color: '#64b5f6' }}
                                        >
                                            <ExpandMoreIcon style={{ transform: 'rotate(-90deg)' }} />
                                        </IconButton>
                                    </Box>
                                </Box>
                            </>
                        ) : (
                            <Box
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                                py={4}
                                style={{ color: '#b0b0b0' }}
                            >
                                <GamepadIcon style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }} />
                                <Typography variant="body1">
                                    {isLoadingGames ? getText('loadingGameData') : getText('noGameData')}
                                </Typography>
                            </Box>
                        )}
                    </Paper>

                    {/* 操作说明 */}
                    <Box mt={2} p={2} style={{ backgroundColor: '#1a2035', borderRadius: 4 }}>
                        <Typography variant="body2" style={{ color: '#64b5f6', marginBottom: 8 }}>
                            <InfoIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 8 }} />
                            {getText('operationInstructions')}
                        </Typography>
                        <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                            {getText('operationInstructionsDesc')}
                        </Typography>
                    </Box>
                </DialogContent>

                <DialogActions className={classes.dialogActions}>
                    <Button
                        onClick={handleCloseGameInfoDialog}
                        color="default"
                        variant="outlined"
                        disabled={isIGDBSyncing}
                    >
                        {getText('cancel')}
                    </Button>

                    <Box style={{ marginRight: 'auto' }}>
                        <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                            {getText(gameSourceMode === 0 ? 'allGames' :
                                gameSourceMode === 1 ? 'noizPlatformGames' : 'nonNoizPlatformGames')} • {
                                getText(syncMode === 'notUpdated' ? 'smartSync' :
                                    syncMode === 'updated' ? 'forceSync' : 'fullSync')
                            }
                            {selectedGames.length > 0 && ` • ${selectedGames.length} 个游戏`}
                            {selectedGames.length === 0 && ` • 自动筛选模式`}
                        </Typography>
                    </Box>

                    <Button
                        onClick={handleSyncGameInfo}
                        color="primary"
                        variant="contained"
                        disabled={isIGDBSyncing || gamesList.length === 0}
                        startIcon={isIGDBSyncing ? <CircularProgress size={20} /> : <SyncIcon />}
                        style={{
                            minWidth: '140px',
                            backgroundColor: '#64b5f6',
                            color: 'white'
                        }}
                    >
                        {isIGDBSyncing ? getText('syncing') :
                            selectedGames.length > 0 ? `同步 ${selectedGames.length} 个` : getText('startSync')}
                    </Button>
                </DialogActions>

                {/* 同步进度显示 */}
                {isIGDBSyncing && (
                    <Box
                        style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            backgroundColor: '#1a2035',
                            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                            padding: 16
                        }}
                    >
                        <Box display="flex" alignItems="center" mb={1}>
                            <CircularProgress size={20} style={{ marginRight: 12, color: '#64b5f6' }} />
                            <Typography variant="body2" style={{ color: '#64b5f6' }}>
                                正在同步 {gameSourceModeMap[gameSourceMode].label}
                                {selectedGames.length > 0 ? ` (${selectedGames.length} 个游戏)` : ' (自动筛选)'}...
                            </Typography>
                        </Box>

                        <LinearProgress
                            variant="determinate"
                            value={(igdbSyncProgress.processed / igdbSyncProgress.total) * 100}
                            style={{
                                marginBottom: 8,
                                height: 6,
                                borderRadius: 3,
                                backgroundColor: 'rgba(255, 255, 255, 0.1)'
                            }}
                        />

                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                {igdbSyncProgress.phase || '正在处理...'}
                            </Typography>
                            <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                请稍候...
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Dialog>
        );
    };

    // 1. 添加配置相关的状态管理
    const [configDialogOpen, setConfigDialogOpen] = useState(false);
    const [tagConfig, setTagConfig] = useState({ types: [], tags: [] });
    const [isConfigLoading, setIsConfigLoading] = useState(false);
    const [isInitializing, setIsInitializing] = useState(false);

    // 3. 获取标签配置的函数
    const fetchTagConfig = async () => {
        setIsConfigLoading(true);
        try {
            const response = await axios.get('/api/v1/admin/tag/system');
            if (response.data && response.data.data) {
                setTagConfig(response.data.data);
            }
        } catch (error) {
            console.error('获取标签配置失败:', error);
            setSnackbar({
                open: true,
                message: '获取标签配置失败: ' + (error.response?.data?.message || error.message),
                severity: 'error',
            });
        } finally {
            setIsConfigLoading(false);
        }
    };

    // 4. 初始化标签系统的函数
    const initializeTagSystem = async () => {
        if (!window.confirm('确定要初始化标签系统吗？这将重置所有标签配置。')) {
            return;
        }

        setIsInitializing(true);
        try {
            const response = await axios.post('/api/v1/admin/tag/init-system');
            if (response.data && response.data.code === 'SUCCESS') {
                setSnackbar({
                    open: true,
                    message: '标签系统初始化成功',
                    severity: 'success',
                });
                // 重新获取最新配置
                await fetchTagConfig();
            }
        } catch (error) {
            console.error('初始化标签系统失败:', error);
            setSnackbar({
                open: true,
                message: '初始化标签系统失败: ' + (error.response?.data?.message || error.message),
                severity: 'error',
            });
        } finally {
            setIsInitializing(false);
        }
    };

    // 关闭同步弹窗
    const handleCloseSyncDialog = () => {
        setSyncDialogOpen(false);
    };

    // 修改弹窗中的同步函数，去掉 syncGames 参数
    const syncUsersTagsInDialog = async () => {
        // 验证逻辑
        if (!updateAllRanges && tagsToUpdate.length === 0) {
            setSnackbar({
                open: true,
                message: getText('pleaseSelectAtLeastOneRange'),
                severity: 'warning',
            });
            return;
        }

        setIsSyncing(true);
        try {
            const response = await axios.post('/api/v1/admin/user/sync-users-tags', {
                tagsToUpdate: tagsToUpdate,
                updateAll: updateAllRanges
                // 移除 syncGames 参数
            });

            if (response.data && response.data.data && response.data.data.result) {
                const result = response.data.data.result;
                setSnackbar({
                    open: true,
                    message: getText('syncSuccess', {
                        count: result.processedCount,
                        time: result.processingTime
                    }),
                    severity: 'success',
                });

                // 刷新用户列表
                fetchUsers();

                // 关闭弹窗
                handleCloseSyncDialog();
            }
        } catch (error) {
            console.error('同步用户游戏标签失败:', error);
            setSnackbar({
                open: true,
                message: getText('syncUserGameTagsFailed') + ': ' + (error.response?.data?.message || error.message),
                severity: 'error',
            });
        } finally {
            setIsSyncing(false);
        }
    };

    // 修改同步弹窗，移除同步游戏数据选项
    const renderSyncDialog = () => {
        return (
            <Dialog
                open={syncDialogOpen}
                onClose={handleCloseSyncDialog}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle className={classes.dialogTitle}>
                    {getText('syncGameTags')}
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseSyncDialog}
                        style={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    <Typography variant="body1" paragraph>
                        {getText('selectTimeRangeForTagUpdate')}
                    </Typography>

                    {/* 更新所有范围选项 */}
                    <Box mt={2}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={updateAllRanges}
                                    onChange={(e) => {
                                        setUpdateAllRanges(e.target.checked);
                                        if (e.target.checked) {
                                            setTagsToUpdate([]);
                                        } else {
                                            setTagsToUpdate([30]);
                                        }
                                    }}
                                    color="primary"
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="body1">{getText('updateAllTimeRanges')}</Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {getText('updateAllTimeRangesDesc')}
                                    </Typography>
                                </Box>
                            }
                        />
                    </Box>

                    {/* 标签更新选择 */}
                    <Box mt={3}>
                        <Typography variant="subtitle1" style={{ marginBottom: 8 }}>
                            {getText('selectUpdateRanges')}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                            {updateAllRanges ? getText('allRangesSelected') : getText('canSelectMultipleRanges')}
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1.5}>
                            {[30, 60, 90, 120, 365].map(days => (
                                <Chip
                                    key={`tags-update-${days}`}
                                    label={getText(`${days}days`)}
                                    clickable
                                    disabled={updateAllRanges}
                                    color={!updateAllRanges && tagsToUpdate.includes(days) ? "primary" : "default"}
                                    onClick={() => {
                                        if (!updateAllRanges) {
                                            if (tagsToUpdate.includes(days)) {
                                                if (tagsToUpdate.length > 1) {
                                                    setTagsToUpdate(prev => prev.filter(d => d !== days));
                                                }
                                            } else {
                                                setTagsToUpdate(prev => [...prev, days]);
                                            }
                                        }
                                    }}
                                    variant={!updateAllRanges && tagsToUpdate.includes(days) ? "default" : "outlined"}
                                    style={{
                                        backgroundColor: updateAllRanges ? '#555' :
                                            (tagsToUpdate.includes(days) ? '#4dabf5' : 'transparent'),
                                        color: updateAllRanges ? '#999' :
                                            (tagsToUpdate.includes(days) ? 'white' : '#b0b0b0'),
                                        opacity: updateAllRanges ? 0.6 : 1,
                                        margin: '4px', // 从'4px'改为统一的外边距
                                        padding: '4px 8px', // 新增：增加内边距
                                        height: '32px', // 统一高度
                                        fontSize: '0.875rem' // 统一字体大小
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>

                    {/* 移除"是否同步游戏数据选项"部分 */}

                    {/* 优化操作提示 */}
                    <Box mt={3} mb={1} style={{ backgroundColor: '#1a2035', borderRadius: 4, padding: 12 }}>
                        <Typography variant="body2" style={{ color: '#64b5f6', marginBottom: 8, fontWeight: 600 }}>
                            <InfoIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 8 }} />
                            {getText('syncProcessNote')}
                        </Typography>
                        <Typography variant="body2" style={{ color: '#b0b0b0', lineHeight: 1.5 }}>
                            • {getText('syncCurrentDataDesc')}<br />
                            • {getText('syncProcessTimeDesc')}<br />
                            {updateAllRanges && `• ${getText('updateAllRangesTakeLonger')}`}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions className={classes.dialogActions}>
                    <Button
                        onClick={handleCloseSyncDialog}
                        color="default"
                        variant="outlined"
                    >
                        {getText('cancel')}
                    </Button>
                    <Button
                        onClick={syncUsersTagsInDialog}
                        color="primary"
                        variant="contained"
                        disabled={isSyncing || (!updateAllRanges && tagsToUpdate.length === 0)}
                        startIcon={isSyncing ? <CircularProgress size={20} /> : <SyncIcon />}
                    >
                        {isSyncing ? getText('syncing') : getText('startSync')}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    // 数字格式化函数
    // const formatNumber = (num) => {
    //     if (!num && num !== 0) return getText('dataNotAvailable');
    //     if (num >= 1000000000) return `${(num / 1000000000).toFixed(1)}${getText('billionAbbrev')}`;
    //     if (num >= 1000000) return `${(num / 1000000).toFixed(1)}${getText('millionAbbrev')}`;
    //     if (num >= 1000) return `${(num / 1000).toFixed(1)}${getText('thousandAbbrev')}`;
    //     return num.toLocaleString();
    // };

    // 准备游戏标签数据用于图表
    // 修改准备游戏标签数据用于图表的函数
    const prepareTagsData = (user) => {
        if (!user?.gamePreferenceTags || !user.gamePreferenceTags[`tags${selectedDays}`]) {
            return { genre: [], theme: [] };
        }

        const tags = user.gamePreferenceTags[`tags${selectedDays}`];

        // 分别处理类型和主题标签，保留更多信息
        const genreTags = tags
            .filter(tag => tag.category === 'genre')
            .map((tag, index) => ({
                name: tag.name,
                value: tag.weight || 1,
                matchRate: tag.matchRate || 0,
                // 为饼图生成颜色
                color: getGenreColor(index)
            }))
            .sort((a, b) => b.value - a.value); // 按权重排序

        const themeTags = tags
            .filter(tag => tag.category === 'theme')
            .map((tag, index) => ({
                name: tag.name,
                value: tag.weight || 1,
                matchRate: tag.matchRate || 0,
                // 为饼图生成颜色
                color: getThemeColor(index)
            }))
            .sort((a, b) => b.value - a.value); // 按权重排序

        return { genre: genreTags, theme: themeTags };
    };

    // 重新设计颜色生成函数 - 类型标签颜色（蓝紫系，高对比度）
    const getGenreColor = (index) => {
        const genreColors = [
            '#1E88E5', // 亮蓝色
            '#3F51B5', // 靛蓝色
            '#9C27B0', // 紫色
            '#673AB7', // 深紫色
            '#00BCD4', // 青色
            '#009688', // 蓝绿色
            '#2196F3', // 蓝色
            '#5C6BC0', // 浅靛蓝
            '#7E57C2', // 中紫色
            '#26C6DA', // 浅青色
            '#42A5F5', // 浅蓝色
            '#AB47BC', // 粉紫色
        ];
        return genreColors[index % genreColors.length];
    };

    // 重新设计颜色生成函数 - 主题标签颜色（橙红暖色系，高对比度）
    const getThemeColor = (index) => {
        const themeColors = [
            '#FF5722', // 深橙红
            '#FF9800', // 橙色
            '#F44336', // 红色
            '#E91E63', // 粉红色
            '#FF6F00', // 琥珀色
            '#D32F2F', // 深红色
            '#C2185B', // 深粉红
            '#FF8F00', // 深琥珀
            '#F57C00', // 橙黄色
            '#E65100', // 深橙色
            '#AD1457', // 玫红色
            '#BF360C', // 极深橙
        ];
        return themeColors[index % themeColors.length];
    };

    // 修改格式化权重的函数
    const formatWeight = (weight) => {
        if (weight >= 1000000) {
            return (weight / 1000000).toFixed(1) + 'M';
        } else if (weight >= 1000) {
            return (weight / 1000).toFixed(1) + 'K';
        }
        return weight.toString();
    };

    // 准备ACCV历史数据
    const prepareAccvData = (user) => {
        const data = [
            { name: getText('30days'), value: user?.statistics?.AVG30 || 0 },
            { name: getText('60days'), value: user?.statistics?.AVG60 || 0 },
            { name: getText('90days'), value: user?.statistics?.AVG90 || 0 },
        ];
        return data;
    };

    // 修改 prepareFollowersData 函数，返回更有意义的数据
    const prepareFollowersData = (user) => {
        const currentFollowers = user?.connectedAccounts?.twitch?.followers || 0;
        const accv30 = Math.round(user?.statistics?.AVG30 || 0);
        const accv60 = Math.round(user?.statistics?.AVG60 || 0);
        const accv90 = Math.round(user?.statistics?.AVG90 || 0);

        // 构建观众数据趋势
        const viewerTrend = [
            { name: getText('90days'), viewers: accv90, period: '90d' },
            { name: getText('60days'), viewers: accv60, period: '60d' },
            { name: getText('30days'), viewers: accv30, period: '30d' }
        ];

        return {
            currentFollowers,
            accv30,
            conversionRate: currentFollowers > 0 ? ((accv30 / currentFollowers) * 100).toFixed(2) : 0,
            viewerTrend,
            // 计算观众数趋势
            viewerGrowthTrend: accv30 > accv60 ? 'up' : accv30 < accv60 ? 'down' : 'stable'
        };
    };

    // 在状态管理中添加新的状态
    const [ranksExpanded, setRanksExpanded] = useState(false); // 控制标签权重排名的展开

    // 添加游戏时间格式化函数
    const formatPlayTime = (minutes) => {
        if (!minutes || minutes === 0) return '0分钟';

        if (minutes < 60) {
            return `${minutes}分钟`;
        }

        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;

        if (remainingMinutes === 0) {
            return `${hours}小时`;
        }

        return `${hours}小时${remainingMinutes}分钟`;
    };

    // 优化游戏名称转URL格式的函数
    const formatGameNameForUrl = (gameName) => {
        if (!gameName) return '';

        return gameName
            .toLowerCase() // 转为小写
            .replace(/[:[\](){}]/g, '') // 修复：移除不必要的转义，在字符类中 [ 和 ] 不需要转义
            .replace(/[.,!?;'"]/g, '') // 移除标点符号
            .replace(/&/g, 'and') // 将 & 替换为 and
            .replace(/\s+/g, '-') // 将一个或多个空格替换为单个短横线
            .replace(/-+/g, '-') // 将多个连续短横线替换为单个短横线
            .replace(/^-+|-+$/g, ''); // 移除开头和结尾的短横线
    };

    // 添加处理游戏点击的函数
    const handleGameClick = (gameName) => {
        const urlName = formatGameNameForUrl(gameName);
        const igdbUrl = `https://www.igdb.com/games/${urlName}`;
        window.open(igdbUrl, '_blank', 'noopener,noreferrer');
    };

    // 修改渲染用户统计弹窗中的游戏标签部分
    const renderStatsDialog = () => {
        if (!selectedUser) return null;

        const tagsData = prepareTagsData(selectedUser);
        const accvData = prepareAccvData(selectedUser);

        const username = selectedUser.username || selectedUser.displayName || '未知用户';
        const followers = selectedUser.connectedAccounts?.twitch?.followers || 0;
        const accv = Math.round(selectedUser.statistics?.AVG30 || 0); // 确保为整数

        // 直接使用已计算好的匹配度
        const matchScore = selectedUser.gamePreferenceTags?.matchRate || 0;

        // 获取绑定平台信息
        const connectedPlatformsCount = getConnectedPlatformsCount(selectedUser);
        const connectedPlatforms = getConnectedPlatforms(selectedUser);

        return (
            <Dialog
                open={statsDialogOpen}
                onClose={handleCloseStatsDialog}
                fullWidth
                maxWidth="lg"
            >
                <DialogTitle className={classes.dialogTitle}>
                    {getText('userStatistics')} - {username}
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseStatsDialog}
                        style={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <Tabs
                    value={statTab}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="fullWidth"
                    className={classes.tabRoot}
                >
                    <Tab label={getText('overview')} />
                    <Tab
                        label={
                            <Box display="flex" alignItems="center" gap={1}>
                                <span>{getText('gameTags')}</span>
                                {tagsData.genre.length + tagsData.theme.length > 0 && (
                                    <Chip
                                        size="small"
                                        label={tagsData.genre.length + tagsData.theme.length}
                                        style={{
                                            backgroundColor: '#64b5f6',
                                            color: 'white',
                                            fontSize: '10px',
                                            height: '18px'
                                        }}
                                    />
                                )}
                            </Box>
                        }
                    />
                    <Tab label={getText('historicalTrend')} />
                </Tabs>

                <DialogContent className={classes.dialogContent}>
                    {/* 概览标签面板 */}
                    {statTab === 0 && (
                        <>
                            {/* 用户概览统计卡片 */}
                            <Box mb={3} p={3} style={{ backgroundColor: '#1a2035', borderRadius: 12, border: '1px solid rgba(100, 181, 246, 0.2)' }}>
                                <Typography variant="h6" gutterBottom style={{ color: '#64b5f6', marginBottom: 20 }}>
                                    {getText('userOverview')} - {getText('daysData', { days: selectedDays })}
                                </Typography>

                                <Grid container spacing={3}>
                                    <Grid item xs={6} md={3}>
                                        <Box textAlign="center" p={2} style={{ backgroundColor: '#29314a', borderRadius: 8 }}>
                                            <Typography variant="h4" style={{ color: '#4caf50', fontWeight: 'bold', marginBottom: 8 }}>
                                                {Math.round(matchScore)}%
                                            </Typography>
                                            <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                                                {getText('matchScore')}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Box textAlign="center" p={2} style={{ backgroundColor: '#29314a', borderRadius: 8 }}>
                                            <Typography variant="h4" style={{ color: '#e91e63', fontWeight: 'bold', marginBottom: 8 }}>
                                                {formatNumber(followers)}
                                            </Typography>
                                            <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                                                {getText('totalFollowers')}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Box textAlign="center" p={2} style={{ backgroundColor: '#29314a', borderRadius: 8 }}>
                                            <Typography variant="h4" style={{ color: '#ff9800', fontWeight: 'bold', marginBottom: 8 }}>
                                                {formatNumber(accv)}
                                            </Typography>
                                            <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                                                {getText('avgViewers30')}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Box textAlign="center" p={2} style={{ backgroundColor: '#29314a', borderRadius: 8 }}>
                                            <Typography variant="h4" style={{ color: '#9c27b0', fontWeight: 'bold', marginBottom: 8 }}>
                                                {tagsData.genre.length + tagsData.theme.length}
                                            </Typography>
                                            <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                                                {getText('totalGameTags')}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>

                            <Grid container spacing={3}>
                                {/* 用户基本信息卡片 - 移除平台类型 */}
                                <Grid item xs={12} md={4}>
                                    <Paper className={classes.statCard} style={{ height: '100%' }}>
                                        <Typography variant="h6" gutterBottom style={{ color: '#64b5f6', marginBottom: 16 }}>
                                            <PersonIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                                            {getText('userInfo')}
                                        </Typography>
                                        <Divider style={{ marginBottom: 16, backgroundColor: 'rgba(255,255,255,0.1)' }} />

                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Box p={1} style={{ backgroundColor: '#1a2035', borderRadius: 6, marginBottom: 12 }}>
                                                    <Typography variant="body2" style={{ color: '#b0b0b0', fontSize: '0.8rem' }}>{getText('username')}</Typography>
                                                    <Typography variant="body1" style={{ color: '#e0e0e0', fontWeight: 600 }}>
                                                        {username}
                                                    </Typography>
                                                </Box>
                                            </Grid>

                                            <Grid item xs={6}>
                                                <Typography className={classes.statTitle}>{getText('registrationTime')}</Typography>
                                                <Typography className={classes.statValue} style={{ fontSize: '0.9rem' }}>
                                                    {selectedUser.createdAt ?
                                                        getLocalTimeString(selectedUser.createdAt, 'YYYY-MM-DD', false) : getText('dataNotAvailable')}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography className={classes.statTitle}>{getText('userStatus')}</Typography>
                                                <Typography className={classes.statValue} style={{ fontSize: '0.9rem' }}>
                                                    <Chip
                                                        size="small"
                                                        label={selectedUser.status === 'active' ? getText('active') : getText('inactive')}
                                                        style={{
                                                            backgroundColor: selectedUser.status === 'active' ? '#4caf50' : '#f44336',
                                                            color: 'white',
                                                            fontSize: '10px',
                                                            height: '20px'
                                                        }}
                                                    />
                                                </Typography>
                                            </Grid>

                                            <Grid item xs={6}>
                                                <Typography className={classes.statTitle}>{getText('region')}</Typography>
                                                <Typography className={classes.statValue} style={{ fontSize: '0.9rem' }}>
                                                    {selectedUser.country || getText('dataNotAvailable')}
                                                </Typography>
                                            </Grid>

                                            {/* 修改用户信息卡片中的最后活跃时间显示 */}
                                            <Grid item xs={6}>
                                                <Typography className={classes.statTitle}>{getText('lastLogin')}</Typography>
                                                <Typography className={classes.statValue} style={{ fontSize: '0.8rem' }}>
                                                    {selectedUser.statistics?.AVG30LastUpdate ?
                                                        getLocalTimeString(selectedUser.statistics.AVG30LastUpdate, 'YYYY-MM-DD HH:mm', false) :
                                                        getText('noInfo')}
                                                </Typography>
                                            </Grid>

                                            {/* 如果有语言信息，显示语言偏好 */}
                                            {selectedUser.language && (
                                                <Grid item xs={12}>
                                                    <Typography className={classes.statTitle}>{getText('languagePreference')}</Typography>
                                                    <Typography className={classes.statValue} style={{ fontSize: '0.9rem' }}>
                                                        {selectedUser.language.toUpperCase()}
                                                    </Typography>
                                                </Grid>
                                            )}

                                            {/* 新增：平台绑定详情 */}
                                            <Grid item xs={12}>
                                                <Typography className={classes.statTitle}>{getText('platformBinding')}</Typography>
                                                <Box mt={1}>
                                                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                                        <Typography variant="body2" style={{ color: '#e0e0e0' }}>
                                                            {getText('bound', { count: connectedPlatformsCount })}
                                                        </Typography>
                                                        <Chip
                                                            size="small"
                                                            label={connectedPlatformsCount >= 3 ? getText('excellent') :
                                                                connectedPlatformsCount >= 2 ? getText('good') : getText('few')}
                                                            style={{
                                                                backgroundColor: connectedPlatformsCount >= 3 ? '#4caf50' :
                                                                    connectedPlatformsCount >= 2 ? '#ff9800' : '#f44336',
                                                                color: 'white',
                                                                fontSize: '10px',
                                                                height: '20px'
                                                            }}
                                                        />
                                                    </Box>

                                                    {connectedPlatforms.length > 0 ? (
                                                        <Box display="flex" flexWrap="wrap" gap={1.2} mt={1}> {/* 从gap={1}增加到gap={1.2} */}
                                                            {connectedPlatforms.map(platform => (
                                                                <Box
                                                                    key={platform.key}
                                                                    style={{
                                                                        backgroundColor: platform.color,
                                                                        color: 'white',
                                                                        borderRadius: 6, // 从4增加到6
                                                                        padding: '5px 10px', // 从'4px 8px'增加到'5px 10px'
                                                                        fontSize: '11px',
                                                                        fontWeight: 'bold',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 8, // 从6增加到8
                                                                        boxShadow: `0 2px 4px ${platform.color}60`,
                                                                        margin: '2px' // 新增：为每个平台标签添加外边距
                                                                    }}
                                                                >
                                                                    <img
                                                                        src={platform.icon}
                                                                        alt={platform.name}
                                                                        style={{ width: 14, height: 14 }}
                                                                    />
                                                                    <span>{platform.name}</span>
                                                                    {selectedUser.connectedAccounts?.[platform.key]?.username && (
                                                                        <span style={{
                                                                            fontSize: '9px',
                                                                            opacity: 0.8,
                                                                            backgroundColor: 'rgba(255,255,255,0.2)',
                                                                            padding: '2px 4px', // 从'1px 3px'增加到'2px 4px'
                                                                            borderRadius: '3px', // 从'2px'增加到'3px'
                                                                            marginLeft: 4 // 从2增加到4
                                                                        }}>
                                                                            {selectedUser.connectedAccounts[platform.key].username}
                                                                        </span>
                                                                    )}
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    ) : (
                                                        <Typography variant="body2" style={{ color: '#888', fontStyle: 'italic' }}>
                                                            {getText('noPlatformsBound')}
                                                        </Typography>
                                                    )}

                                                    {/* 显示未绑定的平台，也使用SVG图标 */}
                                                    {connectedPlatformsCount < 4 && (
                                                        <Box mt={1}>
                                                            <Typography variant="caption" style={{ color: '#888' }}>
                                                                {getText('unbound')}
                                                            </Typography>
                                                            <Box display="flex" flexWrap="wrap" gap={0.8} mt={0.8}> {/* 从gap={0.5}和mt={0.5}增加 */}
                                                                {[
                                                                    { key: 'twitch', name: 'Twitch', icon: TwitchIcon },
                                                                    { key: 'youtube', name: 'YouTube', icon: YouTubeIcon },
                                                                    { key: 'tiktok', name: 'TikTok', icon: TikTokIcon },
                                                                    { key: 'twitter', name: 'Twitter', icon: TwitterIcon }
                                                                ].filter(platform =>
                                                                    !connectedPlatforms.some(cp => cp.key === platform.key)
                                                                ).map(platform => (
                                                                    <Chip
                                                                        key={platform.key}
                                                                        label={
                                                                            <Box display="flex" alignItems="center" gap={0.8}> {/* 从gap={0.5}增加到gap={0.8} */}
                                                                                <img
                                                                                    src={platform.icon}
                                                                                    alt={platform.name}
                                                                                    style={{ width: 10, height: 10 }}
                                                                                />
                                                                                <span>{platform.name}</span>
                                                                            </Box>
                                                                        }
                                                                        size="small"
                                                                        style={{
                                                                            backgroundColor: '#424242',
                                                                            color: '#b0b0b0',
                                                                            fontSize: '9px',
                                                                            height: '18px',
                                                                            opacity: 0.6,
                                                                            margin: '1px' // 新增：为每个未绑定平台标签添加外边距
                                                                        }}
                                                                    />
                                                                ))}
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </Grid>

                                {/* Twitch数据卡片 - 移除粉丝转化率和账户创建 */}
                                <Grid item xs={12} md={4}>
                                    <Paper className={classes.statCard} style={{ height: '100%' }}>
                                        <Typography variant="h6" gutterBottom style={{ color: '#9146ff', marginBottom: 16 }}>
                                            <Box display="flex" alignItems="center">
                                                {/* 修复Twitch logo显示 */}
                                                <Box
                                                    component="span"
                                                    style={{
                                                        marginRight: 8,
                                                        fontSize: '16px',
                                                        color: '#9146ff'
                                                    }}
                                                >
                                                    📺
                                                </Box>
                                                {getText('twitchData')}
                                            </Box>
                                        </Typography>
                                        <Divider style={{ marginBottom: 16, backgroundColor: 'rgba(255,255,255,0.1)' }} />

                                        {selectedUser.connectedAccounts?.twitch ? (
                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    <Box p={1} style={{ backgroundColor: '#1a2035', borderRadius: 6, marginBottom: 12 }}>
                                                        <Typography variant="body2" style={{ color: '#b0b0b0', fontSize: '0.8rem' }}>{getText('twitchUsername')}</Typography>
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            <Typography variant="body1" style={{ color: '#9146ff', fontWeight: 600 }}>
                                                                {selectedUser.connectedAccounts.twitch.username}
                                                            </Typography>
                                                            <Link
                                                                href={`https://twitch.tv/${selectedUser.connectedAccounts.twitch.username}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ color: '#9146ff' }}
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <Box
                                                                    component="span"
                                                                    style={{
                                                                        fontSize: '12px',
                                                                        color: '#9146ff'
                                                                    }}
                                                                >
                                                                    🔗
                                                                </Box>
                                                            </Link>
                                                        </Box>
                                                    </Box>
                                                </Grid>

                                                <Grid item xs={6}>
                                                    <Typography className={classes.statTitle}>{getText('totalFollowers')}</Typography>
                                                    <Typography className={classes.statValue} style={{ color: '#e91e63', fontWeight: 600 }}>
                                                        {formatNumber(followers)}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Typography className={classes.statTitle}>{getText('accv30')}</Typography>
                                                    <Typography className={classes.statValue} style={{ color: '#ff9800', fontWeight: 600 }}>
                                                        {formatNumber(accv)}
                                                    </Typography>
                                                </Grid>

                                                {/* 添加更多数据行以填充空间 */}
                                                <Grid item xs={6}>
                                                    <Typography className={classes.statTitle}>{getText('accv60')}</Typography>
                                                    <Typography className={classes.statValue} style={{ color: '#ff9800', fontWeight: 500 }}>
                                                        {formatNumber(selectedUser.statistics?.AVG60 || 0)}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Typography className={classes.statTitle}>{getText('accv90')}</Typography>
                                                    <Typography className={classes.statValue} style={{ color: '#ff9800', fontWeight: 500 }}>
                                                        {formatNumber(selectedUser.statistics?.AVG90 || 0)}
                                                    </Typography>
                                                </Grid>

                                                {/* 添加粉丝转化率 */}
                                                <Grid item xs={12}>
                                                    <Typography className={classes.statTitle}>{getText('fanConversionRate')}</Typography>
                                                    <Typography className={classes.statValue} style={{ color: '#9c27b0', fontWeight: 600 }}>
                                                        {(() => {
                                                            const conversionRate = followers > 0 ? ((accv / followers) * 100).toFixed(2) : 0;
                                                            return `${conversionRate}%`;
                                                        })()}
                                                    </Typography>
                                                    <Typography variant="caption" style={{ color: '#888', display: 'block', marginTop: 2 }}>
                                                        {getText('conversionRateDesc')}
                                                    </Typography>
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Box mt={1} p={1} style={{ backgroundColor: '#29314a', borderRadius: 4 }}>
                                                        <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                            {getText('lastUpdated')}: {selectedUser.statistics?.AVG30LastUpdate ?
                                                                getLocalTimeString(selectedUser.statistics.AVG30LastUpdate, 'YYYY-MM-DD HH:mm', false) : getText('dataNotAvailable')}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        ) : (
                                            <Box
                                                display="flex"
                                                flexDirection="column"
                                                alignItems="center"
                                                justifyContent="center"
                                                py={4}
                                                style={{ color: '#b0b0b0' }}
                                            >
                                                <Box
                                                    style={{
                                                        fontSize: 48,
                                                        marginBottom: 16,
                                                        opacity: 0.3,
                                                        color: '#9146ff'
                                                    }}
                                                >
                                                    📺
                                                </Box>
                                                <Typography variant="body1" style={{ fontStyle: 'italic' }}>
                                                    {getText('noTwitchInfo')}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Paper>
                                </Grid>

                                {/* 游戏标签概览卡片 */}
                                <Grid item xs={12} md={4}>
                                    <Paper className={classes.statCard} style={{ height: '100%' }}>
                                        <Typography variant="h6" gutterBottom style={{ color: '#ff7043', marginBottom: 16 }}>
                                            <GamepadIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                                            {getText('gameTagsOverview')}
                                        </Typography>
                                        <Divider style={{ marginBottom: 16, backgroundColor: 'rgba(255,255,255,0.1)' }} />

                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <Box textAlign="center" p={2} style={{ backgroundColor: '#1a2035', borderRadius: 6 }}>
                                                    <Typography variant="h3" style={{ color: '#1E88E5', fontWeight: 'bold' }}>
                                                        {tagsData.genre.length}
                                                    </Typography>
                                                    <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                        {getText('gameGenres')}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Box textAlign="center" p={2} style={{ backgroundColor: '#1a2035', borderRadius: 6 }}>
                                                    <Typography variant="h3" style={{ color: '#FF5722', fontWeight: 'bold' }}>
                                                        {tagsData.theme.length}
                                                    </Typography>
                                                    <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                        {getText('gameThemes')}
                                                    </Typography>
                                                </Box>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Typography className={classes.statTitle} style={{ marginTop: 8 }}>{getText('highestWeightTag')}</Typography>
                                                {(() => {
                                                    const allTags = [...tagsData.genre, ...tagsData.theme];
                                                    const topTag = allTags.sort((a, b) => b.value - a.value)[0];
                                                    return topTag ? (
                                                        <Box display="flex" alignItems="center" gap={1.5} mt={1.2}> {/* 从gap={1}和mt={1}增加 */}
                                                            <Box
                                                                style={{
                                                                    width: 14, // 从12增加到14
                                                                    height: 14, // 从12增加到14
                                                                    backgroundColor: topTag.color,
                                                                    borderRadius: 3, // 从2增加到3
                                                                    boxShadow: `0 1px 3px ${topTag.color}50` // 新增：为颜色块添加阴影
                                                                }}
                                                            />
                                                            <Typography variant="body2" style={{
                                                                color: '#e0e0e0',
                                                                fontWeight: 600,
                                                                marginRight: 8 // 新增：在文字和标签之间添加间距
                                                            }}>
                                                                {topTag.name}
                                                            </Typography>
                                                            <Chip
                                                                size="small"
                                                                label={formatWeight(topTag.value)}
                                                                style={{
                                                                    ackgroundColor: topTag.color,
                                                                    color: 'white',
                                                                    fontSize: '10px',
                                                                    height: '20px', // 从18px增加到20px
                                                                    fontWeight: 'bold',
                                                                    boxShadow: `0 1px 3px ${topTag.color}60`, // 新增：为标签添加阴影
                                                                    padding: '0 8px' // 新增：增加内边距
                                                                }}
                                                            />
                                                        </Box>
                                                    ) : (
                                                        <Typography variant="body2" style={{ color: '#888', fontStyle: 'italic' }}>
                                                            {getText('noGameTags')}
                                                        </Typography>
                                                    );
                                                })()}
                                            </Grid>

                                            <Grid item xs={6}>
                                                <Typography className={classes.statTitle}>{getText('dataRange')}</Typography>
                                                <Typography className={classes.statValue}>
                                                    <Chip
                                                        size="small"
                                                        label={getText('daysData', { days: selectedDays })}
                                                        style={{
                                                            backgroundColor: '#64b5f6',
                                                            color: 'white',
                                                            fontSize: '10px',
                                                            height: '20px'
                                                        }}
                                                    />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography className={classes.statTitle}>{getText('avgMatchRate')}</Typography>
                                                <Typography className={classes.statValue} style={{ color: '#ffd700', fontWeight: 600 }}>
                                                    {(() => {
                                                        const allTags = [...tagsData.genre, ...tagsData.theme];
                                                        if (allTags.length === 0) return '0%';
                                                        const avgMatch = allTags.reduce((sum, tag) => sum + tag.matchRate, 0) / allTags.length;
                                                        return Math.round(avgMatch) + '%';
                                                    })()}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </Grid>
                            </Grid>

                            {/* 数据趋势图表区域 */}
                            <Grid container spacing={3} style={{ marginTop: 16 }}>
                                {/* ACCV趋势图 */}
                                <Grid item xs={12} md={6}>
                                    <Paper className={classes.statCard}>
                                        <Typography variant="h6" gutterBottom style={{ color: '#64b5f6' }}>
                                            <TrendingUpIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                                            {getText('accvTimeTrend')}
                                        </Typography>
                                        <Typography variant="body2" style={{ color: '#b0b0b0', marginBottom: 16 }}>
                                            {getText('accvTimeTrendDesc')}
                                        </Typography>

                                        <div style={{ height: 280 }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={accvData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                                                    <XAxis
                                                        dataKey="name"
                                                        stroke="#b0b0b0"
                                                        fontSize={12}
                                                    />
                                                    <YAxis
                                                        stroke="#b0b0b0"
                                                        fontSize={12}
                                                        tickFormatter={(value) => formatNumber(value)}
                                                    />
                                                    <Tooltip
                                                        contentStyle={{
                                                            backgroundColor: '#1a2035',
                                                            border: '1px solid rgba(100, 181, 246, 0.3)',
                                                            borderRadius: 8,
                                                            color: '#e0e0e0',
                                                            boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
                                                        }}
                                                        formatter={(value) => [formatNumber(value), '平均观众数']}
                                                        labelStyle={{ color: '#64b5f6' }}
                                                    />
                                                    <Bar
                                                        dataKey="value"
                                                        radius={[4, 4, 0, 0]}
                                                        fill="url(#accvGradient)"
                                                    />
                                                    <defs>
                                                        <linearGradient id="accvGradient" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="0%" stopColor="#64b5f6" stopOpacity={1} />
                                                            <stop offset="100%" stopColor="#1976d2" stopOpacity={0.8} />
                                                        </linearGradient>
                                                    </defs>
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>

                                        {/* 趋势分析 */}
                                        <Box mt={2} p={2} style={{ backgroundColor: '#1a2035', borderRadius: 6 }}>
                                            {(() => {
                                                const accv30 = selectedUser.statistics?.AVG30 || 0;
                                                const accv60 = selectedUser.statistics?.AVG60 || 0;
                                                const accv90 = selectedUser.statistics?.AVG90 || 0;

                                                let trendText = '';
                                                let trendColor = '#b0b0b0';

                                                if (accv30 > accv60 && accv60 > accv90) {
                                                    trendText = getText('risingTrend');
                                                    trendColor = '#4caf50';
                                                } else if (accv30 < accv60 && accv60 < accv90) {
                                                    trendText = getText('decliningTrend');
                                                    trendColor = '#f44336';
                                                } else if (accv30 > accv60) {
                                                    trendText = getText('recentImprovement');
                                                    trendColor = '#ff9800';
                                                } else if (accv30 < accv60) {
                                                    trendText = getText('recentDecline');
                                                    trendColor = '#ff9800';
                                                } else {
                                                    trendText = getText('stableTrend');
                                                    trendColor = '#64b5f6';
                                                }

                                                return (
                                                    <Typography variant="body2" style={{ color: trendColor, fontWeight: 500 }}>
                                                        {trendText}
                                                    </Typography>
                                                );
                                            })()}
                                        </Box>
                                    </Paper>
                                </Grid>

                                {/* 观众数据分析 - 替换原来的粉丝增长趋势 */}
                                <Grid item xs={12} md={6}>
                                    <Paper className={classes.statCard}>
                                        <Typography variant="h6" gutterBottom style={{ color: '#e91e63' }}>
                                            <PeopleIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                                            {getText('audienceDataAnalysis')}
                                        </Typography>
                                        <Typography variant="body2" style={{ color: '#b0b0b0', marginBottom: 16 }}>
                                            {getText('audienceDataAnalysisDesc')}
                                        </Typography>

                                        {(() => {
                                            const followersData = prepareFollowersData(selectedUser);

                                            return (
                                                <>
                                                    {/* 核心指标展示 */}
                                                    <Grid container spacing={2} style={{ marginBottom: 16 }}>
                                                        <Grid item xs={4}>
                                                            <Box textAlign="center" p={2} style={{ backgroundColor: '#1a2035', borderRadius: 6 }}>
                                                                <Typography variant="h4" style={{ color: '#e91e63', fontWeight: 'bold' }}>
                                                                    {formatNumber(followersData.currentFollowers)}
                                                                </Typography>
                                                                <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                                    {getText('totalFollowers')}
                                                                </Typography>
                                                            </Box>
                                                        </Grid>
                                                        <Grid item xs={4}>
                                                            <Box textAlign="center" p={2} style={{ backgroundColor: '#1a2035', borderRadius: 6 }}>
                                                                <Typography variant="h4" style={{ color: '#ff9800', fontWeight: 'bold' }}>
                                                                    {formatNumber(followersData.accv30)}
                                                                </Typography>
                                                                <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                                    {getText('30dayViewers')}
                                                                </Typography>
                                                            </Box>
                                                        </Grid>
                                                        <Grid item xs={4}>
                                                            <Box textAlign="center" p={2} style={{ backgroundColor: '#1a2035', borderRadius: 6 }}>
                                                                <Typography variant="h4" style={{ color: '#9c27b0', fontWeight: 'bold' }}>
                                                                    {followersData.conversionRate}%
                                                                </Typography>
                                                                <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                                    {getText('conversionRate')}
                                                                </Typography>
                                                            </Box>
                                                        </Grid>
                                                    </Grid>

                                                    {/* 观众趋势图 */}
                                                    <div style={{ height: 180 }}>
                                                        <ResponsiveContainer width="100%" height="100%">
                                                            <LineChart data={followersData.viewerTrend} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                                                                <XAxis
                                                                    dataKey="name"
                                                                    stroke="#b0b0b0"
                                                                    fontSize={11}
                                                                />
                                                                <YAxis
                                                                    stroke="#b0b0b0"
                                                                    fontSize={11}
                                                                    tickFormatter={(value) => formatNumber(value)}
                                                                />
                                                                <Tooltip
                                                                    contentStyle={{
                                                                        backgroundColor: '#1a2035',
                                                                        border: '1px solid rgba(233, 30, 99, 0.3)',
                                                                        borderRadius: 6,
                                                                        color: '#e0e0e0'
                                                                    }}
                                                                    formatter={(value) => [formatNumber(value), '平均观众']}
                                                                />
                                                                <Line
                                                                    type="monotone"
                                                                    dataKey="viewers"
                                                                    stroke="#e91e63"
                                                                    strokeWidth={3}
                                                                    dot={{ fill: '#e91e63', strokeWidth: 2, r: 5 }}
                                                                    activeDot={{ r: 7, fill: '#e91e63' }}
                                                                />
                                                            </LineChart>
                                                        </ResponsiveContainer>
                                                    </div>

                                                    {/* 数据解读 */}
                                                    <Box mt={2} p={2} style={{ backgroundColor: '#1a2035', borderRadius: 6 }}>
                                                        <Typography variant="body2" style={{ color: '#b0b0b0', marginBottom: 8 }}>
                                                            {getText('dataInterpretation')}:
                                                        </Typography>
                                                        <Typography variant="body2" style={{ color: '#e0e0e0', lineHeight: 1.6 }}>
                                                            {getText('conversionRateFormula')}<br />
                                                            {getText('conversionRateIndicator')}<br />
                                                            {getText('viewerTrendDesc')}
                                                        </Typography>

                                                        {/* 转化率评估 */}
                                                        <Box mt={1}>
                                                            <Typography variant="caption" style={{
                                                                color: parseFloat(followersData.conversionRate) > 5 ? '#4caf50' :
                                                                    parseFloat(followersData.conversionRate) > 2 ? '#ff9800' : '#f44336',
                                                                fontWeight: 600
                                                            }}>
                                                                {parseFloat(followersData.conversionRate) > 5 ? getText('excellentConversion') :
                                                                    parseFloat(followersData.conversionRate) > 2 ? getText('goodConversion') : getText('lowConversion')}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </>
                                            );
                                        })()}
                                    </Paper>
                                </Grid>
                            </Grid>

                            {/* 主播评价概览 */}
                            {selectedUser.streamerRatings && (
                                <Box mt={3}>
                                    <Typography variant="h6" gutterBottom style={{ color: '#64b5f6' }}>
                                        <StarIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                                        {getText('streamerRatingOverview')}
                                    </Typography>

                                    <Grid container spacing={2}>
                                        {/* 活跃度评价 */}
                                        <Grid item xs={12} md={4}>
                                            <Box p={2} style={{ backgroundColor: '#1a472a', borderRadius: 8 }}>
                                                <Typography variant="subtitle2" style={{ color: '#81c784', marginBottom: 8 }}>
                                                    {getText('activityRating')}
                                                </Typography>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <Box>
                                                        <Typography variant="body2" style={{ color: '#e0e0e0' }}>
                                                            {selectedUser.streamerRatings.activityLevel?.code || getText('dataNotAvailable')}
                                                        </Typography>
                                                        <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                            {selectedUser.streamerRatings.activityLevel?.description || ''}
                                                        </Typography>
                                                    </Box>
                                                    <Box textAlign="center">
                                                        {renderStars(getStreamerRatingStars(selectedUser).activity)}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Grid>

                                        {/* 执行力评价 */}
                                        <Grid item xs={12} md={4}>
                                            <Box p={2} style={{ backgroundColor: '#1a365d', borderRadius: 8 }}>
                                                <Typography variant="subtitle2" style={{ color: '#64b5f6', marginBottom: 8 }}>
                                                    {getText('executionRating')}
                                                </Typography>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <Box>
                                                        <Typography variant="body2" style={{ color: '#e0e0e0' }}>
                                                            {selectedUser.streamerRatings.completionAbility?.code || getText('dataNotAvailable')}
                                                        </Typography>
                                                        <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                            {selectedUser.streamerRatings.completionAbility?.description || ''}
                                                        </Typography>
                                                    </Box>
                                                    <Box textAlign="center">
                                                        {renderStars(getStreamerRatingStars(selectedUser).execution)}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Grid>

                                        {/* 性价比评价 */}
                                        <Grid item xs={12} md={4}>
                                            <Box p={2} style={{ backgroundColor: '#4a1a4a', borderRadius: 8 }}>
                                                <Typography variant="subtitle2" style={{ color: '#ce93d8', marginBottom: 8 }}>
                                                    {getText('costEffectiveRating')}
                                                </Typography>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <Box>
                                                        <Typography variant="body2" style={{ color: '#e0e0e0' }}>
                                                            {selectedUser.streamerRatings.costEffectiveness?.code || getText('dataNotAvailable')}
                                                        </Typography>
                                                        <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                            {selectedUser.streamerRatings.costEffectiveness?.description || ''}
                                                        </Typography>
                                                    </Box>
                                                    <Box textAlign="center">
                                                        {renderStars(getStreamerRatingStars(selectedUser).costEffect)}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}

                            {/* 平台数据对比 */}
                            {selectedUser.streamerRatings && platformData && (
                                <Box mt={3}>
                                    <Typography variant="h6" gutterBottom style={{ color: '#64b5f6' }}>
                                        <AssessmentIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                                        {getText('platformDataComparison')}
                                    </Typography>

                                    <Grid container spacing={2}>
                                        {/* 申请任务数量对比 */}
                                        <Grid item xs={12} md={6}>
                                            <Box p={2} style={{ backgroundColor: '#1a472a', borderRadius: 8 }}>
                                                <Typography variant="subtitle2" style={{ color: '#81c784', marginBottom: 8 }}>
                                                    {getText('recentApplicationCount', { days: platformData.activityDays })}
                                                </Typography>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <Box>
                                                        <Typography variant="h4" style={{ color: '#4caf50', fontWeight: 'bold' }}>
                                                            {selectedUser.streamerRatings.metrics?.activityApplicationCount || 0}
                                                        </Typography>
                                                        <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                            {getText('userValue')}
                                                        </Typography>
                                                    </Box>
                                                    <Box textAlign="center">
                                                        <Typography variant="body2" style={{ color: '#81c784' }}>
                                                            {getText('vs')}
                                                        </Typography>
                                                    </Box>
                                                    <Box textAlign="right">
                                                        <Typography variant="h4" style={{ color: '#81c784', fontWeight: 'bold' }}>
                                                            {platformData.activityPlatformAvg?.toFixed(1) || '0.0'}
                                                        </Typography>
                                                        <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                            {getText('platformAverage')}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                {/* 对比比例 */}
                                                <Box mt={1} p={1} style={{ backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: 4 }}>
                                                    <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                        {(() => {
                                                            const userValue = selectedUser.streamerRatings.metrics?.activityApplicationCount || 0;
                                                            const platformAvg = platformData.activityPlatformAvg || 1;
                                                            const ratio = userValue / platformAvg;
                                                            const percentage = ((ratio - 1) * 100).toFixed(1);
                                                            const isHigher = ratio > 1;

                                                            return (
                                                                <span style={{ color: isHigher ? '#4caf50' : '#f44336' }}>
                                                                    {isHigher ? '+' : ''}{percentage}% {getText(isHigher ? 'abovePlatformAvg' : 'belowPlatformAvg')}
                                                                </span>
                                                            );
                                                        })()}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>

                                        {/* 任务完成数量对比 */}
                                        <Grid item xs={12} md={6}>
                                            <Box p={2} style={{ backgroundColor: '#1a365d', borderRadius: 8 }}>
                                                <Typography variant="subtitle2" style={{ color: '#64b5f6', marginBottom: 8 }}>
                                                    {getText('recentCompletionCount', { days: platformData.completionDays })}
                                                </Typography>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <Box>
                                                        <Typography variant="h4" style={{ color: '#2196f3', fontWeight: 'bold' }}>
                                                            {selectedUser.streamerRatings.metrics?.completionCompletedTasks || 0}
                                                        </Typography>
                                                        <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                            {getText('userValue')}
                                                        </Typography>
                                                    </Box>
                                                    <Box textAlign="center">
                                                        <Typography variant="body2" style={{ color: '#64b5f6' }}>
                                                            {getText('vs')}
                                                        </Typography>
                                                    </Box>
                                                    <Box textAlign="right">
                                                        <Typography variant="h4" style={{ color: '#64b5f6', fontWeight: 'bold' }}>
                                                            {platformData.completionPlatformAvg?.toFixed(1) || '0.0'}
                                                        </Typography>
                                                        <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                            {getText('platformAverage')}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                {/* 对比比例 */}
                                                <Box mt={1} p={1} style={{ backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: 4 }}>
                                                    <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                        {(() => {
                                                            const userValue = selectedUser.streamerRatings.metrics?.completionCompletedTasks || 0;
                                                            const platformAvg = platformData.completionPlatformAvg || 1;
                                                            const ratio = userValue / platformAvg;
                                                            const percentage = ((ratio - 1) * 100).toFixed(1);
                                                            const isHigher = ratio > 1;

                                                            return (
                                                                <span style={{ color: isHigher ? '#4caf50' : '#f44336' }}>
                                                                    {isHigher ? '+' : ''}{percentage}% {getText(isHigher ? 'abovePlatformAvg' : 'belowPlatformAvg')}
                                                                </span>
                                                            );
                                                        })()}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>

                                        {/* 任务完成率 */}
                                        <Grid item xs={12} md={6}>
                                            <Box p={2} style={{ backgroundColor: '#4a1a4a', borderRadius: 8 }}>
                                                <Typography variant="subtitle2" style={{ color: '#ce93d8', marginBottom: 8 }}>
                                                    {getText('recentCompletionRate', { days: platformData.completionDays })}
                                                </Typography>
                                                <Box display="flex" alignItems="center" justifyContent="center">
                                                    <Typography variant="h3" style={{ color: '#ce93d8', fontWeight: 'bold' }}>
                                                        {((selectedUser.streamerRatings.metrics?.completionRate || 0) * 100).toFixed(1)}%
                                                    </Typography>
                                                </Box>
                                                <Typography variant="caption" style={{ color: '#b0b0b0', textAlign: 'center', display: 'block', marginTop: 8 }}>
                                                    {getText('taskCompletionRate')}
                                                </Typography>
                                            </Box>
                                        </Grid>

                                        {/* 任务平均报酬对比 */}
                                        <Grid item xs={12} md={6}>
                                            <Box p={2} style={{ backgroundColor: '#2e4a1a', borderRadius: 8 }}>
                                                <Typography variant="subtitle2" style={{ color: '#aed581', marginBottom: 8 }}>
                                                    {getText('recentAverageReward', { days: platformData.costEffectivenessDays })}
                                                </Typography>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <Box>
                                                        <Typography variant="h4" style={{ color: '#8bc34a', fontWeight: 'bold' }}>
                                                            ${(selectedUser.streamerRatings.metrics?.costEffectivenessAvgReward || 0).toFixed(2)}
                                                        </Typography>
                                                        <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                            {getText('userValue')}
                                                        </Typography>
                                                    </Box>
                                                    <Box textAlign="center">
                                                        <Typography variant="body2" style={{ color: '#aed581' }}>
                                                            {getText('vs')}
                                                        </Typography>
                                                    </Box>
                                                    <Box textAlign="right">
                                                        <Typography variant="h4" style={{ color: '#aed581', fontWeight: 'bold' }}>
                                                            ${(platformData.costEffectivenessPlatformAvg || 0).toFixed(2)}
                                                        </Typography>
                                                        <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                            {getText('platformAverage')}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                {/* 对比比例 */}
                                                <Box mt={1} p={1} style={{ backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: 4 }}>
                                                    <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                        {(() => {
                                                            const userValue = selectedUser.streamerRatings.metrics?.costEffectivenessAvgReward || 0;
                                                            const platformAvg = platformData.costEffectivenessPlatformAvg || 1;
                                                            const ratio = userValue / platformAvg;
                                                            const percentage = ((ratio - 1) * 100).toFixed(1);
                                                            const isHigher = ratio > 1;

                                                            return (
                                                                <span style={{ color: isHigher ? '#f44336' : '#4caf50' }}>
                                                                    {isHigher ? '+' : ''}{percentage}% {getText(isHigher ? 'abovePlatformAvg' : 'belowPlatformAvg')}
                                                                    {!isHigher && ' ✓'}
                                                                </span>
                                                            );
                                                        })()}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    {/* 性价比等级评估 */}
                                    <Box mt={3} p={3} style={{
                                        backgroundColor: '#1a2035',
                                        borderRadius: 12,
                                        border: '1px solid rgba(100, 181, 246, 0.2)'
                                    }}>
                                        <Typography variant="h6" gutterBottom style={{ color: '#64b5f6', marginBottom: 16 }}>
                                            {getText('costEffectivenessEvaluation')}
                                        </Typography>

                                        {(() => {
                                            const costLevel = getCostEffectivenessLevel(selectedUser, platformData);
                                            return (
                                                <Box>
                                                    {/* 当前等级显示 */}
                                                    <Box textAlign="center" mb={3}>
                                                        <Typography variant="h4" style={{
                                                            color: costLevel.color,
                                                            fontWeight: 'bold',
                                                            marginBottom: 8
                                                        }}>
                                                            {costLevel.label}
                                                        </Typography>
                                                        <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                                                            {getText('currentCostEffectivenessLevel')}
                                                        </Typography>
                                                    </Box>

                                                    {/* 性价比公式说明 */}
                                                    <Box>
                                                        <Typography variant="subtitle2" gutterBottom style={{ color: '#e0e0e0', marginBottom: 12 }}>
                                                            {getText('costEffectivenessFormula')}
                                                        </Typography>

                                                        <Grid container spacing={1}>
                                                            {/* 超高性价比 */}
                                                            <Grid item xs={12} sm={6} md={4}>
                                                                <Box p={2} style={{
                                                                    backgroundColor: costLevel.level === 'ultra_high' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255,255,255,0.05)',
                                                                    borderRadius: 6,
                                                                    border: costLevel.level === 'ultra_high' ? '2px solid #4caf50' : '1px solid rgba(255,255,255,0.1)'
                                                                }}>
                                                                    <Typography variant="body2" style={{
                                                                        color: '#4caf50',
                                                                        fontWeight: 600,
                                                                        marginBottom: 4
                                                                    }}>
                                                                        {getText('ultraHighCostEffective')}
                                                                    </Typography>
                                                                    <Typography variant="caption" style={{ color: '#b0b0b0', lineHeight: 1.4 }}>
                                                                        {getText('ultraHighCostEffectiveCondition')}
                                                                    </Typography>
                                                                </Box>
                                                            </Grid>

                                                            {/* 高性价比 */}
                                                            <Grid item xs={12} sm={6} md={4}>
                                                                <Box p={2} style={{
                                                                    backgroundColor: costLevel.level === 'high' ? 'rgba(129, 199, 132, 0.2)' : 'rgba(255,255,255,0.05)',
                                                                    borderRadius: 6,
                                                                    border: costLevel.level === 'high' ? '2px solid #81c784' : '1px solid rgba(255,255,255,0.1)'
                                                                }}>
                                                                    <Typography variant="body2" style={{
                                                                        color: '#81c784',
                                                                        fontWeight: 600,
                                                                        marginBottom: 4
                                                                    }}>
                                                                        {getText('highCostEffective')}
                                                                    </Typography>
                                                                    <Typography variant="caption" style={{ color: '#b0b0b0', lineHeight: 1.4 }}>
                                                                        {getText('highCostEffectiveCondition')}
                                                                    </Typography>
                                                                </Box>
                                                            </Grid>

                                                            {/* 中等性价比 */}
                                                            <Grid item xs={12} sm={6} md={4}>
                                                                <Box p={2} style={{
                                                                    backgroundColor: costLevel.level === 'medium' ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255,255,255,0.05)',
                                                                    borderRadius: 6,
                                                                    border: costLevel.level === 'medium' ? '2px solid #ff9800' : '1px solid rgba(255,255,255,0.1)'
                                                                }}>
                                                                    <Typography variant="body2" style={{
                                                                        color: '#ff9800',
                                                                        fontWeight: 600,
                                                                        marginBottom: 4
                                                                    }}>
                                                                        {getText('mediumCostEffective')}
                                                                    </Typography>
                                                                    <Typography variant="caption" style={{ color: '#b0b0b0', lineHeight: 1.4 }}>
                                                                        {getText('mediumCostEffectiveCondition')}
                                                                    </Typography>
                                                                </Box>
                                                            </Grid>

                                                            {/* 普通性价比 */}
                                                            <Grid item xs={12} sm={6} md={4}>
                                                                <Box p={2} style={{
                                                                    backgroundColor: costLevel.level === 'normal' ? 'rgba(255, 183, 77, 0.2)' : 'rgba(255,255,255,0.05)',
                                                                    borderRadius: 6,
                                                                    border: costLevel.level === 'normal' ? '2px solid #ffb74d' : '1px solid rgba(255,255,255,0.1)'
                                                                }}>
                                                                    <Typography variant="body2" style={{
                                                                        color: '#ffb74d',
                                                                        fontWeight: 600,
                                                                        marginBottom: 4
                                                                    }}>
                                                                        {getText('normalCostEffective')}
                                                                    </Typography>
                                                                    <Typography variant="caption" style={{ color: '#b0b0b0', lineHeight: 1.4 }}>
                                                                        {getText('normalCostEffectiveCondition')}
                                                                    </Typography>
                                                                </Box>
                                                            </Grid>

                                                            {/* 有待提升 */}
                                                            <Grid item xs={12} sm={6} md={4}>
                                                                <Box p={2} style={{
                                                                    backgroundColor: costLevel.level === 'needs_improvement' ? 'rgba(244, 67, 54, 0.2)' : 'rgba(255,255,255,0.05)',
                                                                    borderRadius: 6,
                                                                    border: costLevel.level === 'needs_improvement' ? '2px solid #f44336' : '1px solid rgba(255,255,255,0.1)'
                                                                }}>
                                                                    <Typography variant="body2" style={{
                                                                        color: '#f44336',
                                                                        fontWeight: 600,
                                                                        marginBottom: 4
                                                                    }}>
                                                                        {getText('needsImprovement')}
                                                                    </Typography>
                                                                    <Typography variant="caption" style={{ color: '#b0b0b0', lineHeight: 1.4 }}>
                                                                        {getText('needsImprovementCondition')}
                                                                    </Typography>
                                                                </Box>
                                                            </Grid>

                                                            {/* 数据不足 */}
                                                            <Grid item xs={12} sm={6} md={4}>
                                                                <Box p={2} style={{
                                                                    backgroundColor: costLevel.level === 'insufficient_data' ? 'rgba(136, 136, 136, 0.2)' : 'rgba(255,255,255,0.05)',
                                                                    borderRadius: 6,
                                                                    border: costLevel.level === 'insufficient_data' ? '2px solid #888' : '1px solid rgba(255,255,255,0.1)'
                                                                }}>
                                                                    <Typography variant="body2" style={{
                                                                        color: '#888',
                                                                        fontWeight: 600,
                                                                        marginBottom: 4
                                                                    }}>
                                                                        {getText('dataNotAvailable')}
                                                                    </Typography>
                                                                    <Typography variant="caption" style={{ color: '#b0b0b0', lineHeight: 1.4 }}>
                                                                        {getText('missingFollowersOrAccvData')}<br />
                                                                        {getText('cannotCalculateCostEffectiveness')}
                                                                    </Typography>
                                                                </Box>
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                </Box>
                                            );
                                        })()}
                                    </Box>
                                </Box>
                            )}
                        </>
                    )}

                    {/* 游戏标签标签面板 - 优化饼图颜色 */}
                    {statTab === 1 && (
                        <>
                            {/* 标签概览统计 */}
                            <Box mb={3} p={2} style={{ backgroundColor: '#1a2035', borderRadius: 8 }}>
                                <Typography variant="h6" gutterBottom style={{ color: '#64b5f6' }}>
                                    {getText('tagStatsOverview')} ({selectedDays}{getText('days')}{getText('daysData', { days: '' }).replace(/\d+/, '')})
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={3}>
                                        <Box textAlign="center">
                                            <Typography variant="h4" style={{ color: '#1E88E5', fontWeight: 'bold' }}>
                                                {tagsData.genre.length}
                                            </Typography>
                                            <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                {getText('gameGenres')}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Box textAlign="center">
                                            <Typography variant="h4" style={{ color: '#FF5722', fontWeight: 'bold' }}>
                                                {tagsData.theme.length}
                                            </Typography>
                                            <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                {getText('gameThemes')}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Box textAlign="center">
                                            <Typography variant="h4" style={{ color: '#4caf50', fontWeight: 'bold' }}>
                                                {(() => {
                                                    const allTags = [...tagsData.genre, ...tagsData.theme];
                                                    const totalWeight = allTags.reduce((sum, tag) => sum + tag.value, 0);
                                                    return formatWeight(totalWeight);
                                                })()}
                                            </Typography>
                                            <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                {getText('totalWeight')}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Box textAlign="center">
                                            <Typography variant="h4" style={{ color: '#ffd700', fontWeight: 'bold' }}>
                                                {(() => {
                                                    const allTags = [...tagsData.genre, ...tagsData.theme];
                                                    if (allTags.length === 0) return '0%';
                                                    const avgMatch = allTags.reduce((sum, tag) => sum + tag.matchRate, 0) / allTags.length;
                                                    return Math.round(avgMatch) + '%';
                                                })()}
                                            </Typography>
                                            <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                {getText('avgMatchRate')}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>

                            <Grid container spacing={3}>
                                {/* 游戏类型饼图 - 优化颜色对比度 */}
                                <Grid item xs={12} md={6}>
                                    <Paper className={classes.statCard}>
                                        <Typography variant="h6" align="center" gutterBottom style={{ color: '#1E88E5' }}>
                                            {getText('gameGenreDistribution')}
                                            <Chip
                                                size="small"
                                                label={`${tagsData.genre.length} ${getText('tags')}`}
                                                style={{
                                                    marginLeft: 8,
                                                    backgroundColor: '#1E88E5',
                                                    color: 'white',
                                                    fontSize: '10px'
                                                }}
                                            />
                                        </Typography>
                                        <div className={classes.chartContainer} style={{ height: 350 }}>
                                            {tagsData.genre.length > 0 ? (
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <PieChart>
                                                        <Pie
                                                            data={tagsData.genre}
                                                            cx="50%"
                                                            cy="45%"
                                                            labelLine={false}
                                                            label={({ name, matchRate, value }) =>
                                                                `${name}: ${matchRate}% (${formatWeight(value)})`
                                                            }
                                                            outerRadius={85}
                                                            innerRadius={25} // 添加内半径，形成甜甜圈图，增强视觉效果
                                                            dataKey="value"
                                                            stroke="#1a2035" // 添加描边，增强分段对比
                                                            strokeWidth={2}
                                                        >
                                                            {tagsData.genre.map((entry, index) => (
                                                                <Cell
                                                                    key={`genre-cell-${index}`}
                                                                    fill={entry.color}
                                                                    style={{
                                                                        filter: 'brightness(1.1)', // 稍微提亮颜色
                                                                        dropShadow: '0 2px 4px rgba(0,0,0,0.3)' // 添加阴影效果
                                                                    }}
                                                                />
                                                            ))}
                                                        </Pie>
                                                        <Tooltip
                                                            contentStyle={{
                                                                backgroundColor: '#1a2035',
                                                                border: '1px solid rgba(30, 136, 229, 0.5)',
                                                                borderRadius: 8,
                                                                color: '#e0e0e0',
                                                                boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
                                                            }}
                                                            formatter={(value, name, props) => [
                                                                `${getText('weight')}: ${formatWeight(value)}, ${getText('match')}: ${props.payload.matchRate}%`,
                                                                props.payload.name
                                                            ]}
                                                        />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            ) : (
                                                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                                                    <Typography variant="body1" color="textSecondary">{getText('noTagData')}</Typography>
                                                </Box>
                                            )}
                                        </div>

                                        {/* 游戏类型标签列表 - 使用新颜色 */}
                                        <Box mt={2}>
                                            <Typography variant="subtitle1" gutterBottom>{getText('genreTagDetails')}</Typography>
                                            <Box style={{ maxHeight: 200, overflowY: 'auto' }}>
                                                {tagsData.genre.length > 0 ? tagsData.genre.map((tag, index) => (
                                                    <Box key={index} display="flex" alignItems="center" justifyContent="space-between"
                                                        py={1.2} px={1.5} mb={1.2} // 从py={1} px={1} mb={1}增加
                                                        style={{
                                                            backgroundColor: `${tag.color}15`, // 使用标签颜色的淡化版作为背景
                                                            borderRadius: 6,
                                                            border: `1px solid ${tag.color}60`, // 使用标签颜色作为边框
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                    >
                                                        <Box display="flex" alignItems="center">
                                                            <Box
                                                                style={{
                                                                    width: 14,
                                                                    height: 14,
                                                                    backgroundColor: tag.color,
                                                                    borderRadius: 3,
                                                                    marginRight: 12,
                                                                    boxShadow: `0 2px 4px ${tag.color}40`, // 添加颜色匹配的阴影
                                                                    border: '1px solid rgba(255,255,255,0.2)'
                                                                }}
                                                            />
                                                            <Typography variant="body2" style={{
                                                                fontWeight: 600,
                                                                color: '#e0e0e0',
                                                                marginRight: 8
                                                            }}>
                                                                {tag.name}
                                                            </Typography>
                                                        </Box>
                                                        <Box display="flex" alignItems="center" gap={1.5}>
                                                            <Chip
                                                                size="small"
                                                                label={`${getText('weight')}: ${formatWeight(tag.value)}`}
                                                                style={{
                                                                    backgroundColor: tag.color,
                                                                    color: 'white',
                                                                    fontSize: '10px',
                                                                    height: '22px',
                                                                    fontWeight: 'bold',
                                                                    boxShadow: `0 2px 4px ${tag.color}60`,
                                                                    padding: '0 6px' // 新增：增加内边距
                                                                }}
                                                            />
                                                            <Chip
                                                                size="small"
                                                                label={`${getText('match')}: ${tag.matchRate}%`}
                                                                style={{
                                                                    backgroundColor: '#FFD700',
                                                                    color: '#1a2035',
                                                                    fontSize: '10px',
                                                                    height: '22px',
                                                                    fontWeight: 'bold',
                                                                    boxShadow: '0 2px 4px rgba(255, 215, 0, 0.4)',
                                                                    padding: '0 6px', // 新增：增加内边距
                                                                    marginLeft: '8px' // 新增：在标签之间添加间距
                                                                }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                )) : (
                                                    <Typography variant="body2" className={classes.noTags}>{getText('noTagData')}</Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>

                                {/* 游戏主题饼图 - 优化颜色对比度 */}
                                <Grid item xs={12} md={6}>
                                    <Paper className={classes.statCard}>
                                        <Typography variant="h6" align="center" gutterBottom style={{ color: '#FF5722' }}>
                                            {getText('gameThemeDistribution')}
                                            <Chip
                                                size="small"
                                                label={`${tagsData.theme.length} ${getText('tags')}`}
                                                style={{
                                                    marginLeft: 8,
                                                    backgroundColor: '#FF5722',
                                                    color: 'white',
                                                    fontSize: '10px'
                                                }}
                                            />
                                        </Typography>
                                        <div className={classes.chartContainer} style={{ height: 350 }}>
                                            {tagsData.theme.length > 0 ? (
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <PieChart>
                                                        <Pie
                                                            data={tagsData.theme}
                                                            cx="50%"
                                                            cy="45%"
                                                            labelLine={false}
                                                            label={({ name, matchRate, value }) =>
                                                                `${name}: ${matchRate}% (${formatWeight(value)})`
                                                            }
                                                            outerRadius={85}
                                                            innerRadius={25} // 添加内半径，形成甜甜圈图
                                                            dataKey="value"
                                                            stroke="#1a2035" // 添加描边，增强分段对比
                                                            strokeWidth={2}
                                                        >
                                                            {tagsData.theme.map((entry, index) => (
                                                                <Cell
                                                                    key={`theme-cell-${index}`}
                                                                    fill={entry.color}
                                                                    style={{
                                                                        filter: 'brightness(1.1)', // 稍微提亮颜色
                                                                        dropShadow: '0 2px 4px rgba(0,0,0,0.3)' // 添加阴影效果
                                                                    }}
                                                                />
                                                            ))}
                                                        </Pie>
                                                        <Tooltip
                                                            contentStyle={{
                                                                backgroundColor: '#1a2035',
                                                                border: '1px solid rgba(255, 87, 34, 0.5)',
                                                                borderRadius: 8,
                                                                color: '#e0e0e0',
                                                                boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
                                                            }}
                                                            formatter={(value, name, props) => [
                                                                `${getText('weight')}: ${formatWeight(value)}, ${getText('match')}: ${props.payload.matchRate}%`,
                                                                props.payload.name
                                                            ]}
                                                        />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            ) : (
                                                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                                                    <Typography variant="body1" color="textSecondary">{getText('noTagData')}</Typography>
                                                </Box>
                                            )}
                                        </div>

                                        {/* 游戏主题标签列表 - 使用新颜色 */}
                                        <Box mt={2}>
                                            <Typography variant="subtitle1" gutterBottom>{getText('themeTagDetails')}</Typography>
                                            <Box style={{ maxHeight: 200, overflowY: 'auto' }}>
                                                {tagsData.theme.length > 0 ? tagsData.theme.map((tag, index) => (
                                                    <Box key={index} display="flex" alignItems="center" justifyContent="space-between"
                                                        py={1.2} px={1.5} mb={1.2} // 从py={1} px={1} mb={1}增加
                                                        style={{
                                                            backgroundColor: `${tag.color}15`, // 使用标签颜色的淡化版作为背景
                                                            borderRadius: 6,
                                                            border: `1px solid ${tag.color}60`, // 使用标签颜色作为边框
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                    >
                                                        <Box display="flex" alignItems="center">
                                                            <Box
                                                                style={{
                                                                    width: 14,
                                                                    height: 14,
                                                                    backgroundColor: tag.color,
                                                                    borderRadius: 3,
                                                                    marginRight: 12, // 从10增加到12
                                                                    boxShadow: `0 2px 4px ${tag.color}40`, // 添加颜色匹配的阴影
                                                                    border: '1px solid rgba(255,255,255,0.2)'
                                                                }}
                                                            />
                                                            <Typography variant="body2" style={{
                                                                fontWeight: 600,
                                                                color: '#e0e0e0',
                                                                marginRight: 8
                                                            }}>
                                                                {tag.name}
                                                            </Typography>
                                                        </Box>
                                                        <Box display="flex" alignItems="center" gap={1.5}>
                                                            <Chip
                                                                size="small"
                                                                label={`${getText('weight')}: ${formatWeight(tag.value)}`}
                                                                style={{
                                                                    backgroundColor: tag.color,
                                                                    color: 'white',
                                                                    fontSize: '10px',
                                                                    height: '22px',
                                                                    fontWeight: 'bold',
                                                                    boxShadow: `0 2px 4px ${tag.color}60`,
                                                                    padding: '0 6px' // 新增：增加内边距
                                                                }}
                                                            />
                                                            <Chip
                                                                size="small"
                                                                label={`${getText('match')}: ${tag.matchRate}%`}
                                                                style={{
                                                                    backgroundColor: '#FFD700',
                                                                    color: '#1a2035',
                                                                    fontSize: '10px',
                                                                    height: '22px',
                                                                    fontWeight: 'bold',
                                                                    boxShadow: '0 2px 4px rgba(255, 215, 0, 0.4)',
                                                                    padding: '0 6px', // 新增：增加内边距
                                                                    marginLeft: '8px' // 新增：在标签之间添加间距
                                                                }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                )) : (
                                                    <Typography variant="body2" className={classes.noTags}>{getText('noTagData')}</Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>
                            </Grid>

                            {/* 标签权重排名 - 可展开收起 */}
                            <Box mt={3}>
                                <Paper className={classes.statCard}>
                                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                                        <Typography variant="h6" style={{ color: '#64b5f6' }}>
                                            {getText('tagWeightRanking')} {(() => {
                                                const allTags = [...tagsData.genre, ...tagsData.theme];
                                                const topCount = Math.min(20, allTags.length);
                                                return topCount > 0 ? `(${getText('topN', { count: topCount })})` : '';
                                            })()}
                                        </Typography>
                                        <Button
                                            onClick={() => setRanksExpanded(!ranksExpanded)}
                                            variant="outlined"
                                            size="small"
                                            startIcon={ranksExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            style={{
                                                color: '#64b5f6',
                                                borderColor: '#64b5f6',
                                                textTransform: 'none',
                                                fontSize: '0.875rem'
                                            }}
                                        >
                                            {ranksExpanded ? getText('collapse') : getText('expand')}
                                        </Button>
                                    </Box>

                                    {ranksExpanded && (
                                        <Box style={{ maxHeight: 400, overflowY: 'auto' }}>
                                            {(() => {
                                                const topTags = selectedUser?.gamePreferenceTags?.top10tags30 || [];

                                                return topTags.length > 0 ? topTags.map((tag, index) => {
                                                    const tagColor = tag.category === 'genre' ? getGenreColor(index) : getThemeColor(index);

                                                    return (
                                                        <Box key={index} display="flex" alignItems="center" justifyContent="space-between"
                                                            py={1.5} px={2} mb={1.2} // 从py={1.5}和mb={1}增加到mb={1.2}
                                                            style={{
                                                                backgroundColor: index < 3 ? 'rgba(255, 215, 0, 0.15)' : `${tagColor}10`,
                                                                borderRadius: 8,
                                                                border: index < 3 ? '1px solid rgba(255, 215, 0, 0.4)' : `1px solid ${tagColor}40`,
                                                                transition: 'all 0.2s ease',
                                                                boxShadow: index < 3 ? '0 2px 8px rgba(255, 215, 0, 0.2)' : `0 2px 8px ${tagColor}20`
                                                            }}
                                                        >
                                                            <Box display="flex" alignItems="center">
                                                                <Typography variant="h6" style={{
                                                                    color: index < 3 ? '#FFD700' : '#b0b0b0',
                                                                    minWidth: 35,
                                                                    fontWeight: 'bold',
                                                                    textShadow: index < 3 ? '0 1px 2px rgba(255, 215, 0, 0.3)' : 'none',
                                                                    marginRight: 12 // 新增：在排名数字和颜色块之间添加间距
                                                                }}>
                                                                    #{index + 1}
                                                                </Typography>
                                                                <Box
                                                                    style={{
                                                                        width: 18,
                                                                        height: 18,
                                                                        backgroundColor: tagColor,
                                                                        borderRadius: 4,
                                                                        marginRight: 16, // 从12增加到16
                                                                        marginLeft: 0, // 从8减少到0（因为上面已经添加了marginRight）
                                                                        boxShadow: `0 2px 6px ${tagColor}50`,
                                                                        border: '1px solid rgba(255,255,255,0.2)'
                                                                    }}
                                                                />
                                                                <Box>
                                                                    <Typography variant="body1" style={{ fontWeight: 600, color: '#e0e0e0', marginBottom: 2 }}>
                                                                        {tag.name}
                                                                    </Typography>
                                                                    <Typography variant="caption" style={{
                                                                        color: tag.category === 'genre' ? '#1E88E5' : '#FF5722',
                                                                        fontWeight: 500
                                                                    }}>
                                                                        {tag.category === 'genre' ? getText('gameGenres') : getText('gameThemes')}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                <Chip
                                                                    size="small"
                                                                    label={`${getText('tagWeight')}: ${formatWeight(tag.weight)}`}
                                                                    style={{
                                                                        backgroundColor: tagColor,
                                                                        color: 'white',
                                                                        fontSize: '11px',
                                                                        fontWeight: 'bold',
                                                                        height: '24px',
                                                                        boxShadow: `0 2px 4px ${tagColor}60`,
                                                                        padding: '0 8px' // 新增：增加内边距
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Box>
                                                    );
                                                }) : (
                                                    <Box display="flex" alignItems="center" justifyContent="center" py={4}>
                                                        <Typography variant="body1" color="textSecondary">
                                                            {getText('noTagData')}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })()}
                                        </Box>
                                    )}
                                </Paper>
                            </Box>

                            {/* 游戏列表 */}
                            <Box mt={3}>
                                <Paper className={classes.statCard}>
                                    <Typography variant="h6" gutterBottom style={{ color: '#9c27b0', marginBottom: 16 }}>
                                        <GamepadIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                                        {getText('gameList')} ({selectedDays}{getText('days')}{getText('daysData', { days: '' }).replace(/\d+/, '')})
                                        {(() => {
                                            const games = selectedUser?.gamePreferenceTags?.games30 || [];
                                            return games.length > 0 && (
                                                <Chip
                                                    size="small"
                                                    label={`${games.length} ${getText('games')}`}
                                                    style={{
                                                        marginLeft: 8,
                                                        backgroundColor: '#9c27b0',
                                                        color: 'white',
                                                        fontSize: '10px'
                                                    }}
                                                />
                                            );
                                        })()}
                                    </Typography>
                                    <Divider style={{ marginBottom: 16, backgroundColor: 'rgba(255,255,255,0.1)' }} />

                                    <Box style={{ maxHeight: 500, overflowY: 'auto' }}>
                                        {(() => {
                                            const games = selectedUser?.gamePreferenceTags?.games30 || [];

                                            return games.length > 0 ? (
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell className={classes.tableHeaderCell} style={{ color: '#b0b0b0', fontWeight: 600 }}>
                                                                {getText('gameName')}
                                                            </TableCell>
                                                            <TableCell className={classes.tableHeaderCell} style={{ color: '#b0b0b0', fontWeight: 600 }}>
                                                                {getText('playTime')}
                                                            </TableCell>
                                                            <TableCell className={classes.tableHeaderCell} style={{ color: '#b0b0b0', fontWeight: 600 }}>
                                                                {getText('releaseTime')}
                                                            </TableCell>
                                                            <TableCell className={classes.tableHeaderCell} style={{ color: '#b0b0b0', fontWeight: 600 }}>
                                                                {getText('dataSyncTime')}
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {games.map((game, index) => (
                                                            <TableRow
                                                                key={index}
                                                                className={classes.tableRow}
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.2s ease',
                                                                    '&:hover': {
                                                                        backgroundColor: '#3a4b78 !important',
                                                                        transform: 'scale(1.01)'
                                                                    }
                                                                }}
                                                                onClick={() => handleGameClick(game.name)}
                                                                title="点击访问 IGDB 游戏页面"
                                                            >
                                                                <TableCell style={{
                                                                    color: '#e0e0e0',
                                                                    fontWeight: 600,
                                                                    borderBottom: '1px solid rgba(255,255,255,0.05)'
                                                                }}>
                                                                    <Box display="flex" alignItems="center">
                                                                        <GamepadIcon
                                                                            style={{
                                                                                color: '#9c27b0',
                                                                                marginRight: 8,
                                                                                fontSize: '18px'
                                                                            }}
                                                                        />
                                                                        <Typography
                                                                            variant="body2"
                                                                            style={{
                                                                                color: '#64b5f6',
                                                                                fontWeight: 500,
                                                                                textDecoration: 'none',
                                                                                '&:hover': {
                                                                                    textDecoration: 'underline'
                                                                                }
                                                                            }}
                                                                        >
                                                                            {game.name}
                                                                        </Typography>
                                                                        <i
                                                                            className="fas fa-external-link-alt"
                                                                            style={{
                                                                                fontSize: '12px',
                                                                                color: '#64b5f6',
                                                                                marginLeft: 6,
                                                                                opacity: 0.7
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell style={{
                                                                    color: '#e0e0e0',
                                                                    borderBottom: '1px solid rgba(255,255,255,0.05)'
                                                                }}>
                                                                    <Box display="flex" alignItems="center">
                                                                        <i
                                                                            className="fas fa-clock"
                                                                            style={{
                                                                                color: '#ff9800',
                                                                                marginRight: 6,
                                                                                fontSize: '14px'
                                                                            }}
                                                                        />
                                                                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                                                                            {formatPlayTime(game.timePlaying)}
                                                                        </Typography>
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell style={{
                                                                    color: '#e0e0e0',
                                                                    borderBottom: '1px solid rgba(255,255,255,0.05)'
                                                                }}>
                                                                    <Box display="flex" alignItems="center">
                                                                        <i
                                                                            className="fas fa-calendar-alt"
                                                                            style={{
                                                                                color: '#4caf50',
                                                                                marginRight: 6,
                                                                                fontSize: '14px'
                                                                            }}
                                                                        />
                                                                        <Typography variant="body2">
                                                                            {game.releaseDate ?
                                                                                getLocalTimeString(game.releaseDate, 'YYYY-MM-DD', false) :
                                                                                '未知'
                                                                            }
                                                                        </Typography>
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell style={{
                                                                    color: '#e0e0e0',
                                                                    borderBottom: '1px solid rgba(255,255,255,0.05)'
                                                                }}>
                                                                    <Box display="flex" alignItems="center">
                                                                        <i
                                                                            className="fas fa-sync-alt"
                                                                            style={{
                                                                                color: '#64b5f6',
                                                                                marginRight: 6,
                                                                                fontSize: '14px'
                                                                            }}
                                                                        />
                                                                        <Typography variant="body2" style={{ fontSize: '12px' }}>
                                                                            {game.lastSyncFromIGDB ?
                                                                                getLocalTimeString(game.lastSyncFromIGDB, 'YYYY-MM-DD HH:mm', false) :
                                                                                '未同步'
                                                                            }
                                                                        </Typography>
                                                                    </Box>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            ) : (
                                                <Box
                                                    display="flex"
                                                    flexDirection="column"
                                                    alignItems="center"
                                                    justifyContent="center"
                                                    py={6}
                                                    style={{ color: '#b0b0b0' }}
                                                >
                                                    <GamepadIcon
                                                        style={{
                                                            fontSize: 48,
                                                            marginBottom: 16,
                                                            opacity: 0.3,
                                                            color: '#9c27b0'
                                                        }}
                                                    />
                                                    <Typography variant="body1" style={{ fontStyle: 'italic' }}>
                                                        暂无游戏数据
                                                    </Typography>
                                                    <Typography variant="body2" style={{ marginTop: 8, opacity: 0.7 }}>
                                                        该用户在 {selectedDays} 天内没有游戏记录
                                                    </Typography>
                                                </Box>
                                            );
                                        })()}
                                    </Box>

                                    {/* 游戏统计信息 */}
                                    {(() => {
                                        const games = selectedUser?.gamePreferenceTags?.games30 || [];
                                        if (games.length === 0) return null;

                                        const totalPlayTime = games.reduce((sum, game) => sum + (game.timePlaying || 0), 0);
                                        const avgPlayTime = Math.round(totalPlayTime / games.length);
                                        const recentGames = games.filter(game => {
                                            if (!game.releaseDate) return false;
                                            const releaseDate = new Date(game.releaseDate);
                                            const oneYearAgo = new Date();
                                            oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
                                            return releaseDate >= oneYearAgo;
                                        }).length;

                                        return (
                                            <Box mt={3} p={2} style={{ backgroundColor: '#1a2035', borderRadius: 8 }}>
                                                <Typography variant="subtitle2" style={{ color: '#9c27b0', marginBottom: 12 }}>
                                                    {getText('gameStatsOverview')}
                                                </Typography>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={3}>
                                                        <Box textAlign="center">
                                                            <Typography variant="h5" style={{ color: '#9c27b0', fontWeight: 'bold' }}>
                                                                {games.length}
                                                            </Typography>
                                                            <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                                {getText('totalGames')}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={3}>
                                                        <Box textAlign="center">
                                                            <Typography variant="h5" style={{ color: '#ff9800', fontWeight: 'bold' }}>
                                                                {formatPlayTime(totalPlayTime)}
                                                            </Typography>
                                                            <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                                {getText('totalPlayTime')}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={3}>
                                                        <Box textAlign="center">
                                                            <Typography variant="h5" style={{ color: '#4caf50', fontWeight: 'bold' }}>
                                                                {formatPlayTime(avgPlayTime)}
                                                            </Typography>
                                                            <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                                {getText('avgPlayTime')}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={3}>
                                                        <Box textAlign="center">
                                                            <Typography variant="h5" style={{ color: '#e91e63', fontWeight: 'bold' }}>
                                                                {recentGames}
                                                            </Typography>
                                                            <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                                {getText('recentNewGames')}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        );
                                    })()}
                                </Paper>
                            </Box>
                        </>
                    )}

                    {/* 历史趋势标签面板 */}
                    {statTab === 2 && (
                        <>
                            <Typography variant="h6" gutterBottom>
                                {getText('historicalTrendAnalysis')}
                            </Typography>
                            <Typography variant="body2" paragraph style={{ color: '#b0b0b0' }}>
                                {getText('historicalTrendDesc')}
                            </Typography>

                            {/* ACCV历史对比 - 只显示有数据的30、60、90天 */}
                            <Grid container spacing={3}>
                                {/* ACCV数据概览卡片 */}
                                <Grid item xs={12} md={6}>
                                    <Paper className={classes.statCard}>
                                        <Typography variant="h6" gutterBottom style={{ color: '#64b5f6' }}>
                                            {getText('accvDataOverview')}
                                        </Typography>
                                        <Divider style={{ marginBottom: 16, backgroundColor: 'rgba(255,255,255,0.1)' }} />

                                        <Grid container spacing={2}>
                                            <Grid item xs={4}>
                                                <Box textAlign="center">
                                                    <Typography variant="h4" style={{ color: '#4caf50', fontWeight: 'bold' }}>
                                                        {Math.round(selectedUser.statistics?.AVG30 || 0).toLocaleString()}
                                                    </Typography>
                                                    <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                        {getText('dayAverage', { days: 30 })}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Box textAlign="center">
                                                    <Typography variant="h4" style={{ color: '#ff9800', fontWeight: 'bold' }}>
                                                        {Math.round(selectedUser.statistics?.AVG60 || 0).toLocaleString()}
                                                    </Typography>
                                                    <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                        {getText('dayAverage', { days: 60 })}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Box textAlign="center">
                                                    <Typography variant="h4" style={{ color: '#f44336', fontWeight: 'bold' }}>
                                                        {Math.round(selectedUser.statistics?.AVG90 || 0).toLocaleString()}
                                                    </Typography>
                                                    <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                        {getText('dayAverage', { days: 90 })}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>

                                        {/* 趋势分析 */}
                                        <Box mt={3}>
                                            <Typography variant="subtitle2" gutterBottom style={{ color: '#e0e0e0' }}>
                                                {getText('trendAnalysis')}
                                            </Typography>
                                            {(() => {
                                                const accv30 = selectedUser.statistics?.AVG30 || 0;
                                                const accv60 = selectedUser.statistics?.AVG60 || 0;
                                                const accv90 = selectedUser.statistics?.AVG90 || 0;

                                                let trendText = '';
                                                let trendColor = '#b0b0b0';

                                                if (accv30 > accv60 && accv60 > accv90) {
                                                    trendText = getText('continuousUptrend');
                                                    trendColor = '#4caf50';
                                                } else if (accv30 < accv60 && accv60 < accv90) {
                                                    trendText = getText('continuousDowntrend');
                                                    trendColor = '#f44336';
                                                } else if (accv30 > accv60) {
                                                    trendText = getText('recentImprovement');
                                                    trendColor = '#ff9800';
                                                } else if (accv30 < accv60) {
                                                    trendText = getText('recentDecline');
                                                    trendColor = '#ff9800';
                                                } else {
                                                    trendText = getText('relativelyStable');
                                                    trendColor = '#64b5f6';
                                                }

                                                return (
                                                    <Box p={1.5} style={{
                                                        backgroundColor: '#1a2035',
                                                        borderRadius: 6,
                                                        border: `1px solid ${trendColor}40`
                                                    }}>
                                                        <Typography variant="body2" style={{ color: trendColor, fontWeight: 500 }}>
                                                            {trendText}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })()}
                                        </Box>
                                    </Paper>
                                </Grid>

                                {/* 观众与粉丝数据对比 */}
                                <Grid item xs={12} md={6}>
                                    <Paper className={classes.statCard}>
                                        <Typography variant="h6" gutterBottom style={{ color: '#64b5f6' }}>
                                            {getText('audienceFollowerData')}
                                        </Typography>
                                        <Divider style={{ marginBottom: 16, backgroundColor: 'rgba(255,255,255,0.1)' }} />

                                        {(() => {
                                            const followersData = prepareFollowersData(selectedUser);
                                            return (
                                                <Grid container spacing={2}>
                                                    <Grid item xs={6}>
                                                        <Box textAlign="center">
                                                            <Typography variant="h4" style={{ color: '#e91e63', fontWeight: 'bold' }}>
                                                                {formatNumber(followersData.currentFollowers)}
                                                            </Typography>
                                                            <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                                {getText('currentFollowers')}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Box textAlign="center">
                                                            <Typography variant="h4" style={{ color: '#9c27b0', fontWeight: 'bold' }}>
                                                                {followersData.conversionRate}%
                                                            </Typography>
                                                            <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                                                {getText('conversionRate')}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>

                                                    <Grid item xs={12}>
                                                        <Box mt={2} p={2} style={{ backgroundColor: '#1a2035', borderRadius: 4 }}>
                                                            <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                                                                {getText('fanConversionRateFormula')}
                                                            </Typography>
                                                            <Typography variant="caption" style={{ color: '#888', display: 'block', marginTop: 4 }}>
                                                                {getText('conversionRateDesc')}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            );
                                        })()}
                                    </Paper>
                                </Grid>
                            </Grid>

                            {/* ACCV对比图表 - 精简版 */}
                            <Typography variant="subtitle1" style={{ marginTop: 24, marginBottom: 8 }}>
                                {getText('accvTimeComparison')}
                            </Typography>
                            <Paper className={classes.chartContainer} style={{ height: 280 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={accvData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                                        <XAxis dataKey="name" stroke="#b0b0b0" />
                                        <YAxis stroke="#b0b0b0" />
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: '#1a2035',
                                                border: 'none',
                                                borderRadius: 4,
                                                color: '#e0e0e0'
                                            }}
                                            formatter={(value) => [value.toLocaleString(), getText('avgViewers')]}
                                        />
                                        <Bar
                                            dataKey="value"
                                            name={getText('avgViewers')}
                                            fill="#4dabf5"
                                            radius={[4, 4, 0, 0]}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Paper>

                            {/* 精简的数据表格 */}
                            <Typography variant="subtitle1" style={{ marginTop: 24, marginBottom: 8 }}>
                                {getText('dataDetails')}
                            </Typography>
                            <TableContainer component={Paper} className={classes.tableContainer} style={{ marginTop: 16, height: 'auto' }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell className={classes.tableHeaderCell}>{getText('timeRange')}</TableCell>
                                            <TableCell className={classes.tableHeaderCell}>ACCV</TableCell>
                                            <TableCell className={classes.tableHeaderCell}>{getText('relativeChange')}</TableCell>
                                            <TableCell className={classes.tableHeaderCell}>{getText('dataStatus')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {accvData.map((item, index) => {
                                            // 计算相对于30天数据的变化
                                            const baseline = accvData[0].value; // 30天数据作为基准
                                            const changePercent = baseline > 0 ? (((item.value - baseline) / baseline) * 100).toFixed(1) : '0.0';
                                            const isPositive = parseFloat(changePercent) >= 0;

                                            return (
                                                <TableRow key={item.name} className={classes.tableRow}>
                                                    <TableCell>{item.name}</TableCell>
                                                    <TableCell style={{ fontWeight: 600 }}>
                                                        {item.value > 0 ? item.value.toLocaleString() : getText('noData')}
                                                    </TableCell>
                                                    <TableCell style={{
                                                        color: index === 0 ? '#64b5f6' : (isPositive ? '#f44336' : '#4caf50'),
                                                        fontWeight: 500
                                                    }}>
                                                        {index === 0 ? getText('baseline') : `${isPositive ? '+' : ''}${changePercent}%`}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            size="small"
                                                            label={item.value > 0 ? getText('hasData') : getText('noData')}
                                                            style={{
                                                                backgroundColor: item.value > 0 ? '#4caf50' : '#757575',
                                                                color: 'white',
                                                                fontSize: '10px',
                                                                height: '20px'
                                                            }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* 数据说明 */}
                            <Box mt={3} p={2} style={{ backgroundColor: '#1a2035', borderRadius: 8 }}>
                                <Typography variant="body2" style={{ color: '#64b5f6', marginBottom: 8 }}>
                                    <InfoIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 8 }} />
                                    {getText('dataNote')}
                                </Typography>
                                <Typography variant="body2" style={{ color: '#b0b0b0', lineHeight: 1.6 }}>
                                    {getText('dataNote1')}<br />
                                    {getText('dataNote2')}<br />
                                    {getText('dataNote3')}<br />
                                    {getText('dataNote4')}
                                </Typography>
                            </Box>
                        </>
                    )}
                </DialogContent>

                <DialogActions className={classes.dialogActions}>
                    <Button
                        onClick={handleCloseStatsDialog}
                        color="primary"
                        variant="contained"
                    >
                        {getText('close')}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    const fetchGameTags = async () => {
        setIsTagsLoading(true);
        try {
            const response = await axios.get('/api/v1/admin/user/game-tags');
            if (response.data && response.data.data && response.data.data.tags) {
                const tags = response.data.data.tags;
                // 提取可用的类型和主题标签名称
                const genreNames = tags.filter(tag => tag.category === 'genre').map(tag => tag.name);
                const themeNames = tags.filter(tag => tag.category === 'theme').map(tag => tag.name);

                setAvailableGenres(genreNames);
                setAvailableThemes(themeNames);

                setAllTags({
                    genre: tags.filter(tag => tag.category === 'genre'),
                    theme: tags.filter(tag => tag.category === 'theme'),
                });
            }
        } catch (error) {
            console.error('获取游戏标签失败:', error);
            setSnackbar({
                open: true,
                message: '获取游戏标签失败: ' + (error.response?.data?.message || error.message),
                severity: 'error',
            });
        } finally {
            setIsTagsLoading(false);
        }
    };

    // 添加一个新的处理函数，用于处理"全部"选项的点击
    // const handleSelectAll = (category) => {
    //     setSelectedTags(prev => ({
    //         ...prev,
    //         [category]: [] // 清空选择表示选择全部
    //     }));
    // };

    // 添加缺失的状态变量
    const [availableGenres, setAvailableGenres] = useState([]);
    const [availableThemes, setAvailableThemes] = useState([]);

    // 主播评价相关状态
    const [isUpdatingStreamerRatings, setIsUpdatingStreamerRatings] = useState(false);
    const [streamerRatingsProgress, setStreamerRatingsProgress] = useState({
        phase: '',
        percentage: 0,
        processedCount: 0,
        totalCount: 0
    });

    // 添加判断用户主播类型的函数
    const determineUserStreamerType = (user) => {
        const matchScore = user.matchScore || 0;
        const ratings = user.streamerRatings;

        if (!ratings) {
            return null;
        }

        // 检查各项评级是否满足条件
        const activityLevel = ratings.activityLevel && ratings.activityLevel.code;
        const completionAbility = ratings.completionAbility && ratings.completionAbility.code;
        const costEffectiveness = ratings.costEffectiveness && ratings.costEffectiveness.code;

        // 优质主播：匹配度>=70% 且 满足所有三个条件
        if (matchScore >= 70) {
            const isPremiumActivity = activityLevel === '110' || activityLevel === '120';
            const isPremiumCompletion = completionAbility === '210' || completionAbility === '220';
            const isPremiumCost = costEffectiveness === '310' || costEffectiveness === '320';

            if (isPremiumActivity && isPremiumCompletion && isPremiumCost) {
                return 'premium';
            }
        }

        // 潜力主播：匹配度>=50% 且 满足三个条件中的两个
        if (matchScore >= 50) {
            const isPotentialActivity = activityLevel === '110' || activityLevel === '120';
            const isPotentialCompletion = completionAbility === '210' || completionAbility === '220';
            const isPotentialCost = costEffectiveness === '310' || costEffectiveness === '320';

            const potentialCount = [isPotentialActivity, isPotentialCompletion, isPotentialCost].filter(Boolean).length;

            if (potentialCount >= 2) {
                return 'potential';
            }
        }

        // 普通主播：匹配度>=30% 且 满足三个条件中的两个（条件更宽松）
        if (matchScore >= 30) {
            const isNormalActivity = activityLevel === '110' || activityLevel === '120';
            const isNormalCompletion = completionAbility === '210' || completionAbility === '220' || completionAbility === '230';
            const isNormalCost = costEffectiveness === '310' || costEffectiveness === '320' || costEffectiveness === '330';

            const normalCount = [isNormalActivity, isNormalCompletion, isNormalCost].filter(Boolean).length;

            if (normalCount >= 2) {
                return 'normal';
            }
        }

        return null;
    };

    // 在组件状态中添加平台数据状态
    const [platformData, setPlatformData] = useState(null);

    // 修改 fetchUsers 函数，添加主播类型参数
    const fetchUsers = async () => {
        setIsLoading(true);
        try {
            const params = new URLSearchParams({
                days: selectedDays.toString(),
                page: (page + 1).toString(),
                limit: rowsPerPage.toString(),
                sortBy: sortBy, // 添加排序参数
                isNoiz: isNoizUser.toString(), // 添加平台用户参数
            });

            // 添加主播类型筛选（包括others）
            if (selectedStreamerType) {
                params.append('streamerType', selectedStreamerType);
            }

            // 添加游戏筛选
            if (selectedGame) {
                params.append('game', selectedGame);
            }

            // 添加搜索关键词
            if (searchQuery.trim()) {
                params.append('search', searchQuery.trim());
            }

            // 添加标签筛选
            if (selectedTags && selectedTags.length > 0) {
                params.append('filterTags', JSON.stringify(selectedTags));
            }

            console.log('请求参数:', {
                days: selectedDays,
                page: page + 1,
                limit: rowsPerPage,
                streamerType: selectedStreamerType,
                game: selectedGame,
                search: searchQuery,
                filterTags: selectedTags,
                sortBy: sortBy,
                isNoiz: isNoizUser // 日志中显示平台用户参数
            });

            const response = await axios.get(`/api/v1/admin/user/get-users-by-tags?${params}`);

            if (response.data && response.data.code === 'SUCCESS') {
                const data = response.data.data;
                console.log('获取用户成功:', data);

                setUsers(data.users || []);
                setTotalUsers(data.pagination.totalItems || 0);
                setTotalPages(data.pagination.totalPages || 0);
                setStatistics(data.statistics || {});
                setFilterApplied(data.filterApplied || false);

                // 新增：保存平台数据
                setPlatformData(data.platform || null);

                // 重置页码如果当前页超出范围
                if (page >= data.pagination.totalPages && data.pagination.totalPages > 0) {
                    setPage(0);
                }
            } else {
                throw new Error(response.data?.message || '获取用户失败');
            }
        } catch (error) {
            console.error('获取用户失败:', error);
            setSnackbar({
                open: true,
                message: '获取用户失败: ' + (error.response?.data?.message || error.message),
                severity: 'error',
            });
            setUsers([]);
            setTotalUsers(0);
            setTotalPages(0);
            setStatistics({});
        } finally {
            setIsLoading(false);
        }
    };

    // 修复 getCostEffectivenessLevel 函数的计算逻辑
    const getCostEffectivenessLevel = (user, platformData) => {
        if (!user.streamerRatings?.metrics || !platformData) {
            return { level: 'unknown', label: getText('dataNotAvailable'), color: '#888' };
        }

        const metrics = user.streamerRatings.metrics;
        const followers = user.connectedAccounts?.twitch?.followers || 0;
        const accv = user.statistics?.AVG30 || 0;
        const avgReward = metrics.costEffectivenessAvgReward || 0;
        const completionRate = (metrics.completionRate || 0) * 100; // 转换为百分比
        const platformAvg = platformData.costEffectivenessPlatformAvg || 1;

        // 如果没有粉丝或观众数据，无法计算
        if (followers === 0 || accv === 0) {
            return { level: 'insufficient_data', label: getText('insufficientData'), color: '#888' };
        }

        // 修正计算公式：用户性价比比值 = 任务平均报酬 / (粉丝数 × ACCV)
        const userCostRatio = avgReward / (followers * accv);

        // 平台基准不需要再除以(followers * accv)，因为platformAvg已经是平台的平均报酬
        // 应该用相同公式计算平台比值作为基准，但这里我们直接用platformAvg作为基准报酬
        // 为了比较，我们需要一个统一的基准，这里使用用户的粉丝数和ACCV来标准化平台均值
        const platformBaseline = platformAvg; // 平台平均报酬作为基准

        // 根据条件判断等级 - 修正比较逻辑
        // 超高性价比：用户报酬 ≤ 平台均值 × 0.5，且完成率 ≥ 70%
        if (userCostRatio <= platformBaseline * 0.5 && completionRate >= 70) {
            return { level: 'ultra_high', label: getText('ultraHighCostEffective'), color: '#4caf50' };
        }
        // 高性价比：用户报酬 ≤ 平台均值，且完成率 ≥ 60%
        else if (userCostRatio <= platformBaseline && completionRate >= 60) {
            return { level: 'high', label: getText('highCostEffective'), color: '#81c784' };
        }
        // 中等性价比：用户报酬 ≤ 平台均值 × 1.5，且完成率 ≥ 50%
        else if (userCostRatio <= platformBaseline * 1.5 && completionRate >= 50) {
            return { level: 'medium', label: getText('mediumCostEffective'), color: '#ff9800' };
        }
        // 普通性价比：用户报酬 ≤ 平台均值 × 1.5，且完成率 25%-50%
        else if (userCostRatio <= platformBaseline * 1.5 && completionRate >= 25 && completionRate < 50) {
            return { level: 'normal', label: getText('normalCostEffective'), color: '#ffb74d' };
        }
        // 有待提升：用户报酬 > 平台均值 × 1.5，或完成率 < 25%
        else {
            return { level: 'needs_improvement', label: getText('needsImprovement'), color: '#f44336' };
        }
    };

    // 处理排序变化
    const handleSortChange = (newSortBy) => {
        setSortBy(newSortBy);
        setPage(0); // 重置到第一页
        // 触发重新获取数据将在useEffect中处理
    };

    // 添加主播类型颜色映射
    const getStreamerTypeColor = (type) => {
        switch (type) {
            case 'premium':
                return '#6A0DAD'; // 紫色 - 优秀主播
            case 'potential':
                return '#DAA520'; // 金色 - 潜力主播
            case 'normal':
                return '#1565c0'; // 
            case '': // "不限"选项
                return '#757575'; // 灰色 - 不限
            default:
                return '#b0b0b0'; // 灰色
        }
    };

    // 添加主播类型映射
    const streamerTypeMap = {
        '': getText('unlimited'),
        'premium': getText('premiumStreamer'),
        'potential': getText('potentialStreamer'),
        'normal': getText('normalStreamer'),
        'others': getText('otherStreamers')
    };

    // 添加排序选项映射
    const sortOptionsMap = {
        'match_score': { label: '匹配度', icon: <StarIcon fontSize="small" />, desc: '按标签匹配度排序' },
        'activity_level': { label: '活跃度', icon: <TrendingUpIcon fontSize="small" />, desc: '按主播活跃度排序' },
        'completion_ability': { label: '执行力', icon: <CheckCircleIcon fontSize="small" />, desc: '按任务执行力排序' },
        'cost_effectiveness': { label: '性价比', icon: <AttachMoneyIcon fontSize="small" />, desc: '按性价比排序' },
        'followers': { label: '粉丝数', icon: <PeopleIcon fontSize="small" />, desc: '按Twitch粉丝数排序' },
        'avg_viewers': { label: '平均观众', icon: <AssessmentIcon fontSize="small" />, desc: '按平均观看人数排序' }
    };

    const [genreMenuAnchor, setGenreMenuAnchor] = useState(null);
    const [themeMenuAnchor, setThemeMenuAnchor] = useState(null);

    // 处理类型菜单
    const handleGenreMenuOpen = (event) => {
        setGenreMenuAnchor(event.currentTarget);
    };

    const handleGenreMenuClose = () => {
        setGenreMenuAnchor(null);
    };

    // 修改类型标签选择函数
    const handleGenreSelect = (genre) => {
        if (!selectedTags.some(tag => tag.category === 'genre' && tag.name === genre)) {
            setSelectedTags(prev => [...prev, { category: 'genre', name: genre }]);
            setPage(0);
            // 清除已选择的用户
            setSelectedUsers(new Set());
            setSelectedCards(new Set());
        }
        handleGenreMenuClose();
    };

    // 处理主题菜单
    const handleThemeMenuOpen = (event) => {
        setThemeMenuAnchor(event.currentTarget);
    };

    const handleThemeMenuClose = () => {
        setThemeMenuAnchor(null);
    };

    // 修改主题标签选择函数
    const handleThemeSelect = (theme) => {
        if (!selectedTags.some(tag => tag.category === 'theme' && tag.name === theme)) {
            setSelectedTags(prev => [...prev, { category: 'theme', name: theme }]);
            setPage(0);
            // 不清除用户选择
        }
        handleThemeMenuClose();
    };

    // 修改标签相关的处理函数，不清除用户选择
    const handleTagDelete = (tagToDelete) => {
        const newTags = selectedTags.filter(tag =>
            !(tag.category === tagToDelete.category && tag.name === tagToDelete.name)
        );
        setSelectedTags(newTags);
        setPage(0);
        // 不清除用户选择
    };

    // 渲染类型下拉菜单
    const renderGenreMenu = () => {
        const availableGenresToAdd = availableGenres.filter(genre =>
            !selectedTags.some(tag => tag.category === 'genre' && tag.name === genre)
        );

        return (
            <Menu
                anchorEl={genreMenuAnchor}
                open={Boolean(genreMenuAnchor)}
                onClose={handleGenreMenuClose}
                PaperProps={{
                    style: {
                        backgroundColor: '#232b42',
                        color: '#e0e0e0',
                        maxHeight: 300,
                        minWidth: 200,
                    }
                }}
            >
                {availableGenresToAdd.length > 0 ? (
                    availableGenresToAdd.map(genre => (
                        <MenuItem
                            key={genre}
                            onClick={() => handleGenreSelect(genre)}
                            style={{
                                color: '#e0e0e0',
                                '&:hover': {
                                    backgroundColor: '#3a4b78'
                                }
                            }}
                        >
                            {genre}
                        </MenuItem>
                    ))
                ) : (
                    <MenuItem disabled style={{ color: '#888' }}>
                        所有类型已添加
                    </MenuItem>
                )}
            </Menu>
        );
    };

    // 渲染主题下拉菜单
    const renderThemeMenu = () => {
        const availableThemesToAdd = availableThemes.filter(theme =>
            !selectedTags.some(tag => tag.category === 'theme' && tag.name === theme)
        );

        return (
            <Menu
                anchorEl={themeMenuAnchor}
                open={Boolean(themeMenuAnchor)}
                onClose={handleThemeMenuClose}
                PaperProps={{
                    style: {
                        backgroundColor: '#232b42',
                        color: '#e0e0e0',
                        maxHeight: 300,
                        minWidth: 200,
                    }
                }}
            >
                {availableThemesToAdd.length > 0 ? (
                    availableThemesToAdd.map(theme => (
                        <MenuItem
                            key={theme}
                            onClick={() => handleThemeSelect(theme)}
                            style={{
                                color: '#e0e0e0',
                                '&:hover': {
                                    backgroundColor: '#3a4b78'
                                }
                            }}
                        >
                            {theme}
                        </MenuItem>
                    ))
                ) : (
                    <MenuItem disabled style={{ color: '#888' }}>
                        所有主题已添加
                    </MenuItem>
                )}
            </Menu>
        );
    };

    // 修改筛选条件变化时的处理，保持选择状态
    const handleDaysChange = (days) => {
        setSelectedDays(days);
        setPage(0);
        // 不清除选择状态，保持用户选择
    };

    // 修改清除所有筛选函数，添加选项是否清除用户选择
    const handleClearAllFilters = () => {
        const hasSelectedUsers = selectedUsers.size > 0;

        if (hasSelectedUsers) {
            const confirmed = window.confirm(
                `清除筛选条件会保留已选择的 ${selectedUsers.size} 个用户。\n\n` +
                `如果要同时清除用户选择，请点击"清除选择"按钮。\n\n` +
                `是否继续清除筛选条件？`
            );

            if (!confirmed) return;
        }

        setSelectedTags([]);
        setSelectedStreamerType('');
        setSelectedGame('');
        setSortBy('match_score'); // 重置排序为默认的匹配度
        setIsNoizUser(false); // 重置平台用户筛选
        setPage(0);
        // 保持用户选择不变
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        // fetchUsers(); // 添加这一行，当页码改变时请求新数据
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
        // fetchUsers(); // 添加这一行，当每页行数改变时请求新数据
        // 清除已选择的用户
        setSelectedUsers(new Set());
        setSelectedCards(new Set());
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // 处理游戏选择的函数
    const handleGameSelection = (selectedGameName) => {
        // 游戏选择逻辑保持不变，但不清除用户选择
        if (!selectedGameName) {
            setSelectedGame('');
            setSelectedTags([]);
            setPage(0);
            return;
        }

        const selectedGameObj = availableGamesList.find(game => game.name === selectedGameName);

        if (selectedGameObj && selectedGameObj.igdbInfo) {
            const { genres = [], themes = [] } = selectedGameObj.igdbInfo;
            const newTags = [];

            genres.forEach(genre => {
                if (availableGenres.includes(genre)) {
                    newTags.push({ category: 'genre', name: genre });
                }
            });

            themes.forEach(theme => {
                if (availableThemes.includes(theme)) {
                    newTags.push({ category: 'theme', name: theme });
                }
            });

            setSelectedGame(selectedGameName);
            setSelectedTags(newTags);
            setPage(0);

            if (newTags.length > 0) {
                const genreCount = newTags.filter(tag => tag.category === 'genre').length;
                const themeCount = newTags.filter(tag => tag.category === 'theme').length;

                setSnackbar({
                    open: true,
                    message: `已选择游戏"${selectedGameName}"，自动应用了${genreCount}个类型标签和${themeCount}个主题标签`,
                    severity: 'info',
                });
            }
        } else {
            setSelectedGame(selectedGameName);
            setPage(0);
        }
    };

    // 修改主播类型选择函数
    const handleStreamerTypeChange = (type) => {
        setSelectedStreamerType(type);
        setPage(0);
        // 不清除选择状态，保持用户选择
    };

    // 在现有状态中添加展开/收缩状态
    const [tagsExpanded, setTagsExpanded] = useState(false); // 控制整个标签区域的展开

    if (process.env.NODE_ENV === 'development') {
        console.debug('Unused variables check:', {
            isTagsLoading,
            selectedGroupForPackage,
            allTags,
            totalPages,
            statistics,
            isGameSyncing,
            setIsGameSyncing,
            configDialogOpen
        });
    }

    return (
        <>
            {/* 优化后的筛选器容器 */}
            <Paper className={classes.filterContainer}>
                {/* 第一行：时间范围、游戏、排序 */}
                <Box className={classes.filterRowResponsive}>
                    <Box className={classes.filterGroup}>
                        <Typography variant="subtitle1" className={classes.filterTitle}>
                            <FilterListIcon />
                            {getText('timeRange')}
                        </Typography>
                        <Select
                            value={selectedDays}
                            onChange={(e) => handleDaysChange(e.target.value)}
                            variant="outlined"
                            size="small"
                            className={classes.select}
                        >
                            {[30, 60, 90, 120, 365].map(days => (
                                <MenuItem key={days} value={days}>
                                    {getText(`${days}days`)}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>

                    <Box className={classes.filterGroup}>
                        <Typography variant="subtitle1" className={classes.filterTitle}>
                            <GamepadIcon />
                            {getText('game')}
                        </Typography>
                        <Select
                            value={selectedGame}
                            onChange={(e) => handleGameSelection(e.target.value)}
                            variant="outlined"
                            size="small"
                            className={classes.gameSelect}
                            displayEmpty
                            disabled={isLoadingAvailableGames}
                        >
                            <MenuItem value="">
                                <em>{getText('allGames')}</em>
                            </MenuItem>
                            {availableGamesList.map((game) => (
                                <MenuItem key={game.id || game.name} value={game.name}>
                                    <Box display="flex" alignItems="center" width="100%">
                                        <Typography variant="body2" style={{ flex: 1 }}>
                                            {game.name}
                                        </Typography>
                                        {game.igdbInfo ? (
                                            <Chip
                                                size="small"
                                                label={`${(game.igdbInfo.genres || []).length + (game.igdbInfo.themes || []).length}标签`}
                                                style={{
                                                    backgroundColor: '#4caf50',
                                                    color: 'white',
                                                    fontSize: '10px',
                                                    height: '20px',
                                                    marginLeft: 8
                                                }}
                                            />
                                        ) : (
                                            <Chip
                                                size="small"
                                                label={getText('noTags')}
                                                style={{
                                                    backgroundColor: '#757575',
                                                    color: 'white',
                                                    fontSize: '10px',
                                                    height: '20px',
                                                    marginLeft: 8
                                                }}
                                            />
                                        )}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                        {isLoadingAvailableGames && (
                            <CircularProgress size={20} style={{ marginLeft: 8 }} />
                        )}
                    </Box>

                    <Box className={classes.filterGroup}>
                        <Typography variant="subtitle1" className={classes.filterTitle}>
                            <SortIcon />
                            {getText('sort')}
                        </Typography>
                        <Select
                            value={sortBy}
                            onChange={(e) => handleSortChange(e.target.value)}
                            variant="outlined"
                            size="small"
                            className={classes.select}
                        >
                            {Object.entries(sortOptionsMap).map(([value, config]) => (
                                <MenuItem key={value} value={value}>
                                    <Box display="flex" alignItems="center">
                                        {config.icon}
                                        <Typography style={{ marginLeft: 8 }}>
                                            {getText(value === 'match_score' ? 'matchScore' :
                                                value === 'activity_level' ? 'activityLevel' :
                                                    value === 'completion_ability' ? 'executionAbility' :
                                                        value === 'cost_effectiveness' ? 'costEffectiveness' :
                                                            value === 'followers' ? 'followers' : 'avgViewers')}
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                </Box>

                {/* 第二行：搜索框和工具按钮 */}
                <Box className={classes.filterRowResponsive}>
                    <Box className={classes.filterGroup} style={{ flex: 1 }}>
                        <Typography variant="subtitle1" className={classes.filterTitle}>
                            <SearchIcon />
                            {getText('search')}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1} style={{ flex: 1 }}>
                            <TextField
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder={getText('searchPlaceholder')}
                                variant="outlined"
                                size="small"
                                className={classes.searchField}
                                onKeyDown={handleSearchKeyDown} // 使用新的处理函数
                            />

                            {/* 修复平台用户筛选复选框 - 移出TextField，独立布局 */}
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isNoizUser}
                                        onChange={(e) => handleNoizUserChange(e.target.checked)}
                                        color="primary"
                                        size="small"
                                        style={{
                                            color: isNoizUser ? '#64b5f6' : '#b0b0b0',
                                            padding: '6px',
                                        }}
                                    />
                                }
                                label={
                                    <Typography
                                        variant="body2"
                                        style={{
                                            color: isNoizUser ? '#64b5f6' : '#b0b0b0',
                                            fontSize: '0.875rem',
                                            fontWeight: isNoizUser ? 600 : 400,
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {getText('platformUsers', {}, '平台用户')}
                                    </Typography>
                                }
                                style={{
                                    margin: 0,
                                    marginLeft: '10px', // 添加10px左边距
                                    flexShrink: 0,
                                    backgroundColor: isNoizUser ? 'rgba(100, 181, 246, 0.1)' : 'transparent',
                                    borderRadius: 6,
                                    padding: '4px 8px',
                                    border: isNoizUser ? '1px solid rgba(100, 181, 246, 0.3)' : '1px solid rgba(255, 255, 255, 0.1)',
                                    transition: 'all 0.2s ease',
                                    minWidth: 'auto',
                                    height: '40px', // 与搜索框保持一致的高度
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                                title={getText('platformUsersDesc', {}, '经过平台验证和管理的用户')}
                            />
                        </Box>
                    </Box>

                    {/* 工具按钮组 */}
                    <Box className={classes.filterToolbar}>
                        <Box className={classes.buttonGroup}>
                            <Button
                                onClick={handleSearchClick}
                                variant="contained"
                                size="small"
                                className={classes.searchButton}
                                disabled={isLoading}
                            >
                                {getText('searchBtn')}
                            </Button>

                            <Button
                                onClick={handleMoreMenuOpen}
                                variant="outlined"
                                size="small"
                                className={classes.moreButton}
                                startIcon={<SettingsIcon />}
                                disabled={isLoading}
                            >
                                {getText('more')}
                            </Button>

                            <Button
                                onClick={() => setTagsExpanded(!tagsExpanded)}
                                variant="outlined"
                                size="small"
                                startIcon={tagsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                className={classes.expandButton}
                                style={{
                                    color: selectedTags.length > 0 ? '#ff7043' : '#b0b0b0',
                                    borderColor: selectedTags.length > 0 ? '#ff7043' : '#b0b0b0',
                                }}
                            >
                                {getText('gameTags')} {selectedTags.length > 0 && `(${selectedTags.length})`}
                            </Button>
                        </Box>
                    </Box>
                </Box>

                {/* 游戏标签展开区域保持不变 */}
                {tagsExpanded && (
                    <>
                        <Box className={classes.filterRowResponsive}>
                            <Typography variant="subtitle1" className={classes.filterTitle}>
                                <GamepadIcon />
                                {getText('gameType')}
                            </Typography>
                            <Box className={classes.chipContainer}>
                                {selectedTags
                                    .filter(tag => tag.category === 'genre')
                                    .map((tag, index) => (
                                        <Chip
                                            key={`selected-genre-${index}`}
                                            label={tag.name}
                                            className={`${classes.chip} selected`}
                                            onDelete={() => handleTagDelete(tag)}
                                            style={{
                                                backgroundColor: '#1565c0',
                                                color: 'white',
                                                border: 'none',
                                                borderColor: 'transparent',
                                            }}
                                        />
                                    ))}

                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={(e) => handleGenreMenuOpen(e)}
                                    className={classes.addButton}
                                    style={{
                                        borderRadius: '16px',
                                        borderColor: '#4dabf5',
                                        color: '#4dabf5',
                                        textTransform: 'none',
                                        fontSize: '0.875rem',
                                        height: '32px', // 与芯片保持一致
                                        minWidth: 'auto',
                                        padding: '6px 12px',
                                    }}
                                >
                                    {getText('addType')}
                                </Button>
                            </Box>
                        </Box>

                        <Box className={classes.filterRowResponsive}>
                            <Typography variant="subtitle1" className={classes.filterTitle}>
                                <CategoryIcon />
                                {getText('gameTheme')}
                            </Typography>
                            <Box className={classes.chipContainer}>
                                {selectedTags
                                    .filter(tag => tag.category === 'theme')
                                    .map((tag, index) => (
                                        <Chip
                                            key={`selected-theme-${index}`}
                                            label={tag.name}
                                            className={`${classes.chip} selected`}
                                            onDelete={() => handleTagDelete(tag)}
                                            style={{
                                                backgroundColor: '#ff7043',
                                                color: 'white',
                                                border: 'none',
                                                borderColor: 'transparent',
                                            }}
                                        />
                                    ))}

                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={(e) => handleThemeMenuOpen(e)}
                                    className={classes.addButton}
                                    style={{
                                        borderRadius: '16px',
                                        borderColor: '#ff7043',
                                        color: '#ff7043',
                                        textTransform: 'none',
                                        fontSize: '0.875rem',
                                        height: '32px', // 与芯片保持一致
                                        minWidth: 'auto',
                                        padding: '6px 12px',
                                    }}
                                >
                                    {getText('addTheme')}
                                </Button>
                            </Box>
                        </Box>
                    </>
                )}

                {/* 筛选状态显示 */}
                {(filterApplied || selectedStreamerType || selectedTags.length > 0 || selectedGame || isNoizUser) && (
                    <Box className={classes.filterStatus}>
                        <Typography variant="body2" className="filter-title">
                            <FilterListIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 8 }} />
                            {getText('currentFilters')}
                        </Typography>

                        <Box className="filter-chips">
                            {/* 添加平台用户筛选状态显示 */}
                            {isNoizUser && (
                                <Chip
                                    label={getText('platformUsers', {}, '平台用户')}
                                    size="small"
                                    style={{
                                        backgroundColor: '#64b5f6',
                                        color: 'white',
                                        fontWeight: 500,
                                    }}
                                    onDelete={() => handleNoizUserChange(false)}
                                />
                            )}
                            {selectedGame && (
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Chip
                                        label={`${getText('game')} ${selectedGame}`}
                                        size="small"
                                        style={{
                                            backgroundColor: '#6A0DAD',
                                            color: 'white',
                                            fontWeight: 500,
                                        }}
                                        onDelete={() => handleGameSelection('')}
                                    />
                                    {(() => {
                                        const gameObj = availableGamesList.find(game => game.name === selectedGame);
                                        if (gameObj && gameObj.igdbInfo) {
                                            const { genres = [], themes = [] } = gameObj.igdbInfo;
                                            return (
                                                <Typography variant="caption" style={{ color: '#b0b0b0', marginLeft: 8 }}>
                                                    自动应用了 {genres.length} 个类型标签和 {themes.length} 个主题标签
                                                </Typography>
                                            );
                                        }
                                        return null;
                                    })()}
                                </Box>
                            )}

                            {selectedStreamerType && (
                                <Chip
                                    label={`${getText('streamerType')} ${streamerTypeMap[selectedStreamerType]}`}
                                    size="small"
                                    style={{
                                        backgroundColor: getStreamerTypeColor(selectedStreamerType),
                                        color: 'white',
                                        fontWeight: 400,
                                    }}
                                    onDelete={() => {
                                        setSelectedStreamerType('');
                                        setPage(0);
                                    }}
                                />
                            )}

                            {/* 新增：排序显示 */}
                            {sortBy !== 'match_score' && (
                                <Chip
                                    label={`排序: ${sortOptionsMap[sortBy]?.label}`}
                                    size="small"
                                    icon={sortOptionsMap[sortBy]?.icon}
                                    style={{
                                        backgroundColor: '#ff9800',
                                        color: 'white',
                                        fontWeight: 500,
                                    }}
                                    onDelete={() => {
                                        setSortBy('match_score');
                                        setPage(0);
                                    }}
                                />
                            )}

                            {selectedTags.map((tag, index) => (
                                <Chip
                                    key={index}
                                    label={`${tag.category === 'genre' ? '类型' : '主题'}: ${tag.name}`}
                                    size="small"
                                    style={{
                                        backgroundColor: tag.category === 'genre' ? '#1565c0' : '#ff7043',
                                        color: 'white',
                                        fontWeight: 500,
                                    }}
                                    onDelete={() => {
                                        setSelectedTags(prev => prev.filter((_, i) => i !== index));
                                        setPage(0);
                                    }}
                                />
                            ))}

                            <Button
                                size="small"
                                className="clear-button"
                                onClick={handleClearAllFilters} // 使用新的清除函数
                                startIcon={<ClearIcon />}
                            >
                                {getText('clearAllFilters')}
                            </Button>
                        </Box>

                        {/* 显示游戏的详细标签信息 */}
                        {selectedGame && (() => {
                            const gameObj = availableGamesList.find(game => game.name === selectedGame);
                            if (gameObj && gameObj.igdbInfo) {
                                const { genres = [], themes = [] } = gameObj.igdbInfo;
                                return (
                                    <Box mt={1} p={1} style={{ backgroundColor: '#1a2035', borderRadius: 4 }}>
                                        <Typography variant="caption" style={{ color: '#64b5f6', display: 'block', marginBottom: 4 }}>
                                            {getText('gameTagDetails')}
                                        </Typography>
                                        <Box display="flex" flexWrap="wrap" gap={1}>
                                            {genres.map(genre => (
                                                <Chip
                                                    key={`detail-genre-${genre}`}
                                                    label={genre}
                                                    size="small"
                                                    style={{
                                                        backgroundColor: availableGenres.includes(genre) ? '#1565c0' : '#757575',
                                                        color: 'white',
                                                        fontSize: '10px',
                                                        height: '20px',
                                                        margin: '2px 4px' // 添加左右边距，2px上下，4px左右
                                                    }}
                                                />
                                            ))}
                                            {themes.map(theme => (
                                                <Chip
                                                    key={`detail-theme-${theme}`}
                                                    label={theme}
                                                    size="small"
                                                    style={{
                                                        backgroundColor: availableThemes.includes(theme) ? '#ff7043' : '#757575',
                                                        color: 'white',
                                                        fontSize: '10px',
                                                        height: '20px',
                                                        margin: '2px 4px' // 添加左右边距，2px上下，4px左右
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                        <Typography variant="caption" style={{ color: '#b0b0b0', display: 'block', marginTop: 4 }}>
                                            {getText('grayTagsNotAvailable')}
                                        </Typography>
                                    </Box>
                                );
                            }
                        })()}
                    </Box>
                )}
            </Paper>

            {/* 更多菜单 */}
            <Menu
                anchorEl={moreMenuAnchor}
                open={Boolean(moreMenuAnchor)}
                onClose={handleMoreMenuClose}
                PaperProps={{
                    style: {
                        backgroundColor: '#232b42',
                        color: '#e0e0e0',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: 8,
                        minWidth: 200,
                    }
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <MenuItem
                    onClick={() => handleMenuItemClick('syncStreamerRatings')}
                    style={{
                        color: '#e0e0e0',
                        fontSize: '14px',
                        padding: '12px 16px',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        },
                    }}
                >
                    <UpdateIcon style={{ marginRight: 12, color: '#ff7043', fontSize: 20 }} />
                    {getText('syncStreamerRatings')}
                </MenuItem>
                <MenuItem
                    onClick={() => handleMenuItemClick('syncGameTags')}
                    style={{
                        color: '#e0e0e0',
                        fontSize: '14px',
                        padding: '12px 16px',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        },
                    }}
                >
                    <SyncIcon style={{ marginRight: 12, color: '#1565c0', fontSize: 20 }} />
                    {getText('syncGameTags')}
                </MenuItem>
                <MenuItem
                    onClick={() => handleMenuItemClick('syncGameInfo')}
                    style={{
                        color: '#e0e0e0',
                        fontSize: '14px',
                        padding: '12px 16px',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        },
                    }}
                >
                    <SettingsIcon style={{ marginRight: 12, color: '#64b5f6', fontSize: 20 }} />
                    {getText('syncGameInfo')}
                </MenuItem>
            </Menu>

            {/* 修改操作区域 */}
            <Box className={classes.actionBar}>
                <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="h6" style={{ color: '#e0e0e0' }}>
                        {getText('userList', { count: totalUsers.toLocaleString() })}
                        {selectedUsers.size > 0 && (
                            <span style={{ color: '#64b5f6', marginLeft: 8 }}>
                                {getText('selectedUsers', { count: selectedUsers.size })}
                            </span>
                        )}
                    </Typography>

                    {/* 将批量操作按钮移到用户列表标题旁边 */}
                    <Box className={classes.bulkActions}>
                        <Button
                            className={`${classes.bulkActionBtn} ${classes.selectAllBtn}`}
                            onClick={handleSelectAll}
                            startIcon={<CheckBoxIcon />}
                            disabled={isLoading || users.length === 0}
                        >
                            {getText('oneClickSelect')}
                        </Button>
                    </Box>
                </Box>

                {/* 主播类型选择 - 使用与筛选器一致的 filterGroup 结构和间距 */}
                <Box className={classes.filterGroup}>
                    <Typography variant="subtitle1" className={classes.filterTitle}>
                        <PersonIcon />
                        {getText('streamerType')}
                    </Typography>
                    <Box className={classes.actionBarTypeChips}>
                        {Object.entries(streamerTypeMap).map(([value, label]) => (
                            <Chip
                                key={value}
                                label={label}
                                clickable
                                className={`${classes.chip} ${selectedStreamerType === value ? 'selected' : ''}`}
                                onClick={() => handleStreamerTypeChange(value)}
                                style={{
                                    backgroundColor: selectedStreamerType === value ? getStreamerTypeColor(value) : undefined,
                                    borderColor: getStreamerTypeColor(value),
                                }}
                            />
                        ))}
                    </Box>
                </Box>
            </Box>

            {/* 只在有选择时显示操作区域，合并两种选择状态 */}
            {selectedUsers.size > 0 && (
                <Box
                    mt={2}
                    mb={2}
                    p={2}
                    style={{
                        backgroundColor: '#29314a',
                        borderRadius: 8,
                        border: '1px solid rgba(138, 43, 226, 0.4)'
                    }}
                >
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="body1" style={{ color: '#8A2BE2' }}>
                            <CheckBoxIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 8, weight: 600 }} />
                            {getText('selectedUsersCount', { count: selectedUsers.size }, `已选择 ${selectedUsers.size} 个用户`)}
                        </Typography>

                        {/* 操作按钮组 */}
                        <Box className={classes.selectedUserActions}>
                            <Button
                                className={`${classes.bulkActionBtn} ${classes.groupActionBtn}`}
                                onClick={handleAddToStreamerPackage}
                                startIcon={<SendIcon />}
                                disabled={selectedUsers.size === 0}
                                size="small"
                            >
                                {getText('addToPackage')}
                            </Button>
                            <Button
                                className={`${classes.bulkActionBtn} ${classes.deselectAllBtn}`}
                                onClick={handleDeselectAll}
                                startIcon={<ClearIcon />}
                                disabled={selectedUsers.size === 0}
                                size="small"
                            >
                                {getText('clearSelection')}
                            </Button>
                        </Box>
                    </Box>

                    {/* 显示选中用户的详细列表 */}
                    <Box mt={2}>
                        <Typography variant="caption" style={{ color: '#b0b0b0', display: 'block', marginBottom: 8 }}>
                            {getText('selectedUsersListsersDetails')}
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                            {selectedUsersDetails.map((userDetail) => {
                                const typeColor = getStreamerTypeColor(userDetail.type);
                                const typeName = streamerTypeMap[userDetail.type] || getText('unclassifiedStreamer');

                                // 为未分类用户使用更深的背景色以提供更好的对比度
                                const finalTypeColor = userDetail.type ? typeColor : '#424242'; // 深灰色而不是浅灰色

                                return (
                                    <Chip
                                        key={userDetail.id}
                                        label={
                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                <span>{userDetail.username}</span>
                                                <span style={{
                                                    fontSize: '10px',
                                                    opacity: 0.9,
                                                    backgroundColor: 'rgba(255,255,255,0.3)',
                                                    padding: '1px 4px',
                                                    borderRadius: '2px'
                                                }}>
                                                    {typeName}
                                                </span>
                                            </Box>
                                        }
                                        onDelete={() => handleRemoveSelectedUser(userDetail.id)}
                                        style={{
                                            backgroundColor: finalTypeColor,
                                            color: 'white',
                                            fontWeight: 600, // 增加字重
                                            fontSize: '12px',
                                            height: '28px',
                                            margin: '2px',
                                            textShadow: '0 1px 2px rgba(0, 0, 0, 0.7)', // 添加文字阴影增强对比度
                                            border: userDetail.type ? 'none' : '1px solid #757575', // 为未分类用户添加边框
                                            '&:hover': {
                                                backgroundColor: finalTypeColor,
                                                filter: 'brightness(1.2)', // 悬停时稍微变亮
                                            }
                                        }}
                                        deleteIcon={
                                            <ClearIcon
                                                style={{
                                                    color: 'white',
                                                    fontSize: '16px',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255,255,255,0.2)',
                                                        borderRadius: '50%'
                                                    }
                                                }}
                                            />
                                        }
                                    />
                                );
                            })}
                        </Box>
                    </Box>

                    {/* 显示选中用户的类型分布统计 */}
                    <Box mt={2}>
                        <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                            {getText('typeDistribution')}
                            {(() => {
                                const typeCount = {
                                    premium: selectedUsersDetails.filter(user => user.type === 'premium').length,
                                    potential: selectedUsersDetails.filter(user => user.type === 'potential').length,
                                    normal: selectedUsersDetails.filter(user => user.type === 'normal').length,
                                    other: selectedUsersDetails.filter(user => !user.type || user.type === null).length
                                };

                                const parts = [];
                                if (typeCount.premium > 0) parts.push(`${getText('premiumStreamer')} ${typeCount.premium} 个`);
                                if (typeCount.potential > 0) parts.push(`${getText('potentialStreamer')} ${typeCount.potential} 个`);
                                if (typeCount.normal > 0) parts.push(`${getText('normalStreamer')} ${typeCount.normal} 个`);
                                if (typeCount.other > 0) parts.push(`${getText('otherStreamers')} ${typeCount.other} 个`);

                                return parts.join('，') || '无分类信息';
                            })()}
                        </Typography>
                    </Box>

                    {/* 添加选择持久化提示 */}
                    <Box mt={1} p={1} style={{ backgroundColor: '#1a2035', borderRadius: 4 }}>
                        <Typography variant="caption" style={{ color: '#64b5f6' }}>
                            <InfoIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
                            {getText('selectionPersistence')}
                        </Typography>
                    </Box>

                    {/* 主播包操作提示 */}
                    <Box mt={1} p={1} style={{ backgroundColor: '#1a2035', borderRadius: 4 }}>
                        <Typography variant="caption" style={{ color: '#e0e0e0' }}>
                            <SendIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
                            {getText('clickToAddToPackage')}
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* 用户卡片容器 */}
            <Paper style={{ backgroundColor: 'transparent', boxShadow: 'none' }}>
                {renderUserCards()}
            </Paper>

            {/* 分页控件 */}
            <Box display="flex" justifyContent="center" mt={3}>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 30, 50, 100]}
                    component="div"
                    count={totalUsers}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage={getText('rowsPerPage', {}, '每页行数:')}
                    labelDisplayedRows={({ from, to, count }) => getText('displayedRows', { from, to, count }, `${from}-${to} / 共 ${count}`)}
                    classes={{
                        root: classes.paginationToolbar,
                    }}
                />
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Paper className={`${classes.notificationPaper} ${snackbar.severity}`}>
                    {snackbar.severity === 'success' && <CheckCircleIcon />}
                    {snackbar.severity === 'error' && <ErrorIcon />}
                    {snackbar.severity === 'warning' && <WarningIcon />}
                    {snackbar.severity === 'info' && <InfoIcon />}
                    <Typography>{snackbar.message}</Typography>
                </Paper>
            </Snackbar>

            {/* 添加主播包选择弹窗 */}
            {renderPackageSelectionDialog()}

            {/* 确保下拉菜单在这里渲染 */}
            {renderGenreMenu()}
            {renderThemeMenu()}
            {renderSyncDialog()}
            {renderStatsDialog()}
            {/* 渲染所有弹窗 */}
            {renderStreamerRatingsDialog()}
            {renderGameTagsDialog()}
            {renderGameInfoDialog()}
        </>
    );
}